/** Conversa com a API do Hub. Toda chamada passa por aqui. */

export type Papel = 'master' | 'colaborador';
export type TipoCompromisso = 'entrega' | 'reuniao' | 'interno';

export type Usuario = {
  id: string;
  nome: string;
  email: string;
  papel: Papel;
};

export type Autor = { id: string; nome: string };

export type Compromisso = {
  id: string;
  titulo: string;
  /** Sempre `AAAA-MM-DD`. A tela nunca recebe fuso para adivinhar. */
  data: string;
  tipo: TipoCompromisso;
  observacao: string | null;
  concluido: boolean;
  concluidoEm: string | null;
  concluidoPor: Autor | null;
  criadoPor: Autor;
  criadoEm: string;
};

export type TipoFeriado = 'nacional' | 'estadual' | 'municipal' | 'facultativo';

export type Feriado = {
  data: string;
  nome: string;
  tipo: TipoFeriado;
};

export type Painel = {
  hoje: string;
  atrasados: Compromisso[];
  proximos: Compromisso[];
};

/** Erro que já vem com a mensagem que dá para mostrar na tela. */
export class ErroDaApi extends Error {
  constructor(
    mensagem: string,
    readonly status: number,
  ) {
    super(mensagem);
  }
}

/** ⭐ §70: o servidor está com as duas vagas da leitura de matrícula tomadas (ou no teto da hora). */
export class LeituraOcupada extends Error {
  constructor(
    mensagem: string,
    readonly esperar: number,
  ) {
    super(mensagem);
  }
}

async function chamar<T>(caminho: string, opcoes: RequestInit = {}): Promise<T> {
  const resposta = await fetch(caminho, {
    ...opcoes,
    credentials: 'same-origin',
    headers: {
      ...(opcoes.body ? { 'Content-Type': 'application/json' } : {}),
      ...opcoes.headers,
    },
  });

  if (resposta.status === 204) return undefined as T;

  const texto = await resposta.text();
  /*
   * ⛔ **A página de erro não é JSON — 14/09/2026.** Quando a conexão esgota
   * (504) ou o servidor cai no meio (502), quem responde é o nginx, com HTML.
   * O `JSON.parse` direto quebrava ali e a tela mostrava *"Unexpected token
   * '<', "<html>…" is not valid JSON"* — foi o que o Richard viu na busca de
   * imóveis. Agora a mensagem diz o que aconteceu.
   */
  let corpo: unknown = null;
  try {
    corpo = texto ? JSON.parse(texto) : null;
  } catch {
    throw new ErroDaApi(
      resposta.status === 504 || resposta.status === 502
        ? 'O servidor demorou mais que o limite da conexão e a tela parou de esperar. ' +
          'O trabalho pode ter continuado lá: o que foi consultado fica guardado — ' +
          'atualize a página antes de tentar de novo.'
        : `O servidor respondeu com um erro (${resposta.status}) em vez dos dados.`,
      resposta.status,
    );
  }

  if (!resposta.ok) {
    const mensagem =
      corpo && typeof corpo === 'object' && 'erro' in corpo
        ? String((corpo as { erro: unknown }).erro)
        : 'Não consegui falar com o servidor.';
    throw new ErroDaApi(mensagem, resposta.status);
  }

  return corpo as T;
}

export const api = {
  entrar: (email: string, senha: string) =>
    chamar<Usuario>('/api/sessao', {
      method: 'POST',
      body: JSON.stringify({ email, senha }),
    }),

  quemSouEu: () => chamar<Usuario>('/api/sessao'),

  sair: () => chamar<{ ok: boolean }>('/api/sessao', { method: 'DELETE' }),

  trocarSenha: (senhaAtual: string, senhaNova: string) =>
    chamar<{ ok: boolean }>('/api/senha/trocar', {
      method: 'POST',
      body: JSON.stringify({ senhaAtual, novaSenha: senhaNova }),
    }),

  esqueciSenha: (email: string) =>
    chamar<{ ok: boolean; mensagem: string }>('/api/senha/esqueci', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  conferirLink: (token: string) =>
    chamar<{ valido: boolean; nome: string | null }>(
      `/api/senha/conferir?token=${encodeURIComponent(token)}`,
    ),

  redefinirSenha: (token: string, senhaNova: string) =>
    chamar<{ ok: boolean }>('/api/senha/redefinir', {
      method: 'POST',
      body: JSON.stringify({ token, novaSenha: senhaNova }),
    }),

  painel: () => chamar<Painel>('/api/compromissos/painel'),

  feriados: (de: string, ate: string) =>
    chamar<{ feriados: Feriado[] }>(`/api/feriados?de=${de}&ate=${ate}`),

  doMes: (de: string, ate: string) =>
    chamar<{ de: string; ate: string; compromissos: Compromisso[] }>(
      `/api/compromissos?de=${de}&ate=${ate}`,
    ),

  criar: (dados: {
    titulo: string;
    data: string;
    tipo: TipoCompromisso;
    observacao?: string | null;
  }) =>
    chamar<Compromisso>('/api/compromissos', {
      method: 'POST',
      body: JSON.stringify(dados),
    }),

  editar: (
    id: string,
    dados: Partial<{
      titulo: string;
      data: string;
      tipo: TipoCompromisso;
      observacao: string | null;
    }>,
  ) =>
    chamar<Compromisso>(`/api/compromissos/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(dados),
    }),

  concluir: (id: string) =>
    chamar<Compromisso>(`/api/compromissos/${id}/conclusao`, { method: 'POST' }),

  desmarcar: (id: string) =>
    chamar<Compromisso>(`/api/compromissos/${id}/conclusao`, { method: 'DELETE' }),

  apagar: (id: string) =>
    chamar<void>(`/api/compromissos/${id}`, { method: 'DELETE' }),
};

// ── Clientes e Casos — Incremento 2 ──────────────────────────────────────────

export type TipoPessoa = 'pf' | 'pj';

export type NivelServico = 'n1_insight' | 'n2_forensic' | 'n3_kairos' | 'customizado';

export type SituacaoCaso =
  | 'contratado'
  | 'briefing'
  | 'levantamento'
  | 'analise'
  | 'relatorio'
  | 'entregue'
  | 'encerrado'
  | 'suspenso';

export type Cliente = {
  id: string;
  tipo: TipoPessoa;
  nome: string;
  nomeFantasia: string | null;
  documento: string;
  documentoFormatado: string;
  email: string | null;
  telefone: string | null;
  contatoNome: string | null;
  contatoCargo: string | null;
  origem: string | null;
  observacao: string | null;
  cep: string | null;
  logradouro: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  cidade: string | null;
  uf: string | null;
  site: string | null;
  representanteNome: string | null;
  representanteCpf: string | null;
  representanteCargo: string | null;
  ativo: boolean;
  criadoEm: string;
  casos?: number;
};

export type FormaPagamento = 'a_vista' | 'entrada_parcelas' | 'parcelado';
/**
 * A vida comercial inteira, da oferta ao desfecho.
 *
 * ⚠️ `recusada` e `cancelado` são coisas diferentes: recusada nunca foi
 * receita; cancelado foi e se desfez. O Financeiro só soma `ativo` e
 * `concluido`.
 */
export type SituacaoContrato =
  | 'proposta'
  | 'ativo'
  | 'concluido'
  | 'recusada'
  | 'cancelado';

/**
 * ⚠️ Todo dinheiro vem como `string | null`, e o `null` não quer dizer zero.
 *
 * Quer dizer **"não é para você ver"**: o colaborador não enxerga valor, e o
 * servidor simplesmente não manda o campo. Ver `valoresOcultos` no contrato
 * para distinguir isso de um campo que ninguém preencheu.
 */
export type Parcela = {
  id: string;
  numero: number;
  valor: string | null;
  vencimentoEm: string;
  pagoEm: string | null;
  observacao: string | null;
  /** Só acontece com data escrita à mão: o Hub adia o que ele gera. */
  emDiaNaoUtil: boolean;
  /** O nome do feriado, quando é feriado. Fim de semana vem nulo. */
  feriado: string | null;
};

/** Como as cobranças se repetem quando o Hub as gera. */
export type Intervalo = 'mensal' | 'quinzenal' | 'dias_30';

/** Uma cobrança como a tela envia — sem `pagoEm`, que é outra rota. */
export type CobrancaDigitada = {
  id?: string;
  numero: number;
  valor: string;
  vencimentoEm: string;
  observacao?: string | null;
};

export type Contrato = {
  id: string;
  codigo: string;
  objeto: string;
  situacao: SituacaoContrato;
  assinadoEm: string | null;
  valorTotal: string | null;
  formaPagamento: FormaPagamento;
  entrada: string | null;
  observacao: string | null;
  parcelas: Parcela[];
  recebido: string | null;
  aReceber: string | null;
  casos: number;
  /** `true` quando o servidor escondeu o dinheiro por causa do papel de quem
   *  pediu — diferente de "não preenchido". */
  valoresOcultos: boolean;
  /** ── O que era da proposta. `nivel` nulo = contrato lançado direto. ── */
  nivel: NivelServico | null;
  nivelNome: string | null;
  /**
   * Os níveis que o contrato REALMENTE cobre — os dos casos vinculados.
   *
   * ⚠️ **O `nivel` acima é o da proposta: um só.** Um contrato pode ter vários
   * casos de níveis diferentes, e aí aquela tag mente para pelo menos um deles.
   * Distintos, na ordem em que os casos entraram.
   *
   * ⚠️ **Vazio em toda PROPOSTA**, porque proposta não pendura caso. Ali o
   * `nivel` é a única verdade que existe, e a tela cai nele.
   */
  /**
   * Os níveis que o contrato cobre — **das PESQUISAS vendidas**.
   *
   * ⛔ Chamava-se `niveisDosCasos` e lia os casos, até 29/08/2026: um contrato
   * com duas pesquisas e um caso só mostrava uma tarja. **Um contrato é a
   * venda**, e quem responde pelo que foi vendido são as pesquisas
   * (`CLAUDE.md`). Cai nos casos só em contrato antigo, sem pesquisa lançada.
   */
  niveisDoContrato: Array<{ nivel: NivelServico; nome: string }>;
  /**
   * O que o cliente está contratando — uma linha por pesquisa vendida.
   *
   * ⚠️ **A soma dos valores fecha com `valorTotal`**, e o servidor recusa o que
   * não bate — a mesma trava das cobranças.
   */
  itens: Array<{
    id: string;
    ordem: number;
    nivel: NivelServico;
    nivelNome: string;
    /** ⚠️ Nulo para o colaborador: ele não vê dinheiro. */
    valor: string | null;
    /** Quantos esta pesquisa cobre — o combinado, ou o teto do nível. */
    investigados: number | null;
    /** A tela precisa distinguir "combinado diferente" de "é o teto do nível". */
    investigadosCombinado: boolean;
    /** ⚠️ QUEM esta pesquisa investiga — texto. Não confundir com `investigados`, que é quantos. */
    entendimento: string | null;
    /** ⭐ 29/09/2026: o escopo do Customizado, definido no briefing. Nulos nas outras pesquisas. */
    escopo: string | null;
    prazoDias: number | null;
    reunioes: number | null;
  }>;
  entendimento: string | null;
  investigados: number | null;
  fundoDeCustas: string | null;
  /**
   * ⭐ **Contrato de cortesia — 10/09/2026; só dos HONORÁRIOS desde
   * 29/09/2026.** O Fundo de Custas, a assinatura e o aceite seguem o rito da
   * pesquisa paga. ⚠️ **Chega mesmo ao colaborador**, que não vê valor.
   */
  cortesia: boolean;
  /** ⭐ 29/09/2026: a cortesia antiga, sem fundo, em que a KAIRÓS custeia tudo (os casos de teste). */
  cortesiaTotal: boolean;
  validadeDias: number | null;
  condicoesPagamento: string | null;
  /** Ainda é oferta: a tela oferece aceitar e recusar. */
  ehProposta: boolean;
  criadoEm: string;
};

export type Contato = {
  id: string;
  nome: string;
  cargo: string | null;
  email: string | null;
  telefone: string | null;
  principal: boolean;
};

export type Anotacao = {
  id: string;
  texto: string;
  criadoEm: string;
  autor: string;
};

export type Ficha = Cliente & {
  casos: Caso[];
  contratos: Contrato[];
  contatos: Contato[];
  anotacoes: Anotacao[];
};

export type Limites = {
  investigados: number | null;
  dias: number | null;
  reunioes: number | null;
};

export type ReuniaoDoCaso = {
  id: string;
  data: string;
  assunto: string;
  observacao: string | null;
  realizada: boolean;
};

/** O Fundo de Custas resumido, como o cartão do caso o mostra. */
export type ResumoDoFundo = {
  teto: string | null;
  depositado: string;
  gasto: string;
  saldo: string;
  consumidoDoTeto: number | null;
  perto: boolean;
  estourou: boolean;
  devolvidoEm: string | null;
  devolvido: string | null;
  avisos: string[];
};

export type LancamentoDoFundo = {
  id: string;
  valor: string;
  data: string;
  observacao: string | null;
};

export type GastoDoFundo = LancamentoDoFundo & { descricao: string };

export type FundoDoCaso = {
  casoId: string;
  codigo: string;
  titulo: string | null;
  cliente: { id: string; nome: string };
  resumo: Omit<ResumoDoFundo, 'avisos'>;
  avisos: string[];
  depositos: LancamentoDoFundo[];
  gastos: GastoDoFundo[];
};

/**
 * ⚠️ Tudo aqui é do master. O Fundo de Custas é dinheiro, e o colaborador não
 * vê dinheiro — o servidor recusa com 403.
 */
export const apiFundo = {
  ler: (casoId: string) => chamar<FundoDoCaso>(`/api/casos/${casoId}/fundo`),

  definir: (casoId: string, dados: { teto?: string | null; devolvidoEm?: string | null; devolvido?: string | null }) =>
    chamar<FundoDoCaso>(`/api/casos/${casoId}/fundo`, {
      method: 'PATCH',
      body: JSON.stringify(dados),
    }),

  depositar: (casoId: string, dados: { valor: string; data: string; observacao?: string | null }) =>
    chamar<FundoDoCaso>(`/api/casos/${casoId}/fundo/depositos`, {
      method: 'POST',
      body: JSON.stringify(dados),
    }),

  gastar: (
    casoId: string,
    dados: { descricao: string; valor: string; data: string; observacao?: string | null },
  ) =>
    chamar<FundoDoCaso>(`/api/casos/${casoId}/fundo/gastos`, {
      method: 'POST',
      body: JSON.stringify(dados),
    }),

  apagarDeposito: (casoId: string, id: string) =>
    chamar<FundoDoCaso>(`/api/casos/${casoId}/fundo/depositos/${id}`, { method: 'DELETE' }),

  apagarGasto: (casoId: string, id: string) =>
    chamar<FundoDoCaso>(`/api/casos/${casoId}/fundo/gastos/${id}`, { method: 'DELETE' }),

  /** O endereço do PDF. Abre em aba nova — o navegador imprime ou salva de lá. */
  prestacaoDeContas: (casoId: string) =>
    `/api/casos/${casoId}/fundo/prestacao-de-contas.pdf`,
};

export type Caso = {
  id: string;
  codigo: string;
  /** ⭐ §77: "este caso não vai a IA de fora" (as matrículas dele são lidas só pelo Hub). Só o master a muda. */
  leituraSoNoHub?: boolean;
  /** Como o caso é reconhecido por gente. O código identifica; o título nomeia. */
  titulo: string | null;
  cliente: { id: string; nome: string };
  /** O relatório deste caso, quando já existe. No máximo um. */
  relatorio: { id: string; codigo: string; situacao: SituacaoRelatorio } | null;
  nivel: NivelServico;
  nivelNome: string;
  situacao: SituacaoCaso;
  /**
   * Quantos o CONTRATO previu. ⚠️ **Não é quantos estão na ficha** — é dele que
   * sai o aviso de aditivo, e por isso ele não acompanha o cadastro.
   */
  investigados: number;
  /**
   * Quantos estão na FICHA, agora — 20/08/2026.
   *
   * ⚠️ **É este que o cartão mostra**, porque é o que muda quando alguém
   * cadastra. O Richard cadastrou três e o cartão continuava dizendo "1 de 22":
   * o "1" era o contratado, e ele não sobe sozinho — nem deve.
   */
  investigadosNaFicha: number;
  /*
   * ── Os cinco marcos da dívida — ganharam tela em 20/08/2026 ──────────────
   *
   * ⚠️ **A data da dívida é o EIXO da investigação.** É contra ela que se
   * comparam a transmissão do imóvel, a abertura da empresa e a entrada do
   * sócio novo. Sem elas o Hub tem os fatos e não diz o que indicam.
   */
  /** ⚠️ **NÃO é dinheiro da KAIRÓS** — é o que o cliente tem a receber do
   *  devedor. O honorário mora só no contrato, e é ele que o colaborador
   *  não vê. */
  dividaValor: string | null;
  /** Quando o cliente firmou o contrato com o devedor. */
  dividaEm: string | null;
  /** Quando o devedor deixou de pagar. */
  inadimplenciaEm: string | null;
  /** Quando a ação foi proposta, se foi. */
  ajuizadoEm: string | null;
  /** Só dígitos. A tela formata; o banco guarda cru. */
  processoNumero: string | null;
  assinadoEm: string | null;
  prazoEm: string | null;
  prazoManual: boolean;
  limites: Limites;
  contrato: {
    id: string;
    codigo: string;
    valorTotal: string | null;
    /** ⭐ Contrato de cortesia — 11/09/2026. Chega ao colaborador também. */
    cortesia: boolean;
    casos: number;
    valoresOcultos: boolean;
  } | null;
  reunioes: ReuniaoDoCaso[];
  /** `null` sem teto acordado, e sempre `null` para colaborador. */
  fundo: ResumoDoFundo | null;
  reunioesAgendadas: number;
  reunioesUsadas: number;
  reunioesRestantes: number | null;
  observacao: string | null;
  pendenteDeAssinatura: boolean;
  /** Calculados no servidor: a regra do contrato tem uma fonte só. */
  avisos: string[];
  /**
   * ⭐ **As datas da dívida fora de ordem — 11/09/2026.**
   *
   * ⚠️ **Separado de `avisos` de propósito.** Só o card "Sobre a dívida" os
   * mostra, e separá-los procurando um pedaço do texto seria filtro que para de
   * casar no dia em que alguém melhora a frase — e para em silêncio. O tipo é a
   * única separação que o compilador cobra. Ver `datasForaDeOrdem`.
   */
  avisosDasDatas: string[];
  encerradoEm: string | null;
  criadoEm: string;
};

export type FichaDeCliente = Cliente & { casos_lista?: Caso[] };

export type DadosCliente = {
  tipo: TipoPessoa;
  nome: string;
  nomeFantasia?: string | null;
  documento: string;
  email?: string | null;
  telefone?: string | null;
  contatoNome?: string | null;
  contatoCargo?: string | null;
  origem?: string | null;
  observacao?: string | null;
  cep?: string | null;
  logradouro?: string | null;
  numero?: string | null;
  complemento?: string | null;
  bairro?: string | null;
  cidade?: string | null;
  uf?: string | null;
  site?: string | null;
  representanteNome?: string | null;
  representanteCpf?: string | null;
  representanteCargo?: string | null;
};

export type DadosCaso = {
  clienteId: string;
  contratoId?: string | null;
  titulo?: string | null;
  nivel: NivelServico;
  investigados: number;
  assinadoEm?: string | null;
  prazoEm?: string | null;
  observacao?: string | null;
  limiteInvestigados?: number | null;
  limitePrazoDias?: number | null;
  limiteReunioes?: number | null;
  /* Os cinco marcos da dívida — editáveis pelo card do relatório, 20/08/2026.
     ⚠️ Cada um só é gravado quando VEM no pedido: o card manda um campo por
     vez, ao sair dele, e mandar o conjunto apagaria o que outra aba tivesse
     acabado de preencher. */
  dividaValor?: string | null;
  dividaEm?: string | null;
  inadimplenciaEm?: string | null;
  ajuizadoEm?: string | null;
  processoNumero?: string | null;
  /** ⭐ De qual pesquisa do pedido o caso nasce (29/10/2026, PRD §74). Sem ela, o servidor decide ou pergunta. */
  doPedido?: { pedidoId: string; pesquisa: number } | 'nenhum';
};

/** ⭐ O que o Novo caso traz do pedido — 29/10/2026 (PRD §74). */
export type OrigemDoCasoNovo = {
  viaContrato: boolean;
  automatica: { pedidoId: string; pesquisa: number } | null;
  precisaEscolher: boolean;
  opcoes: Array<{
    pedidoId: string;
    recebidoEm: string;
    pesquisa: number;
    nivel: NivelServico;
    doContrato: boolean;
    quantos: number;
    /** Os nomes, só para quem vê o pedido (o master); o colaborador recebe a lista vazia e vê `quantos`. */
    investigados: string[];
    marcos: string[];
    notas: boolean;
  }>;
};

/** ⭐ O que veio do pedido no caso que acabou de nascer. */
export type TrazidoNoCasoNovo = {
  recebidoEm: string;
  pesquisa: number;
  investigados: number;
  marcos: string[];
  notas: boolean;
  notasNaoCouberam: boolean;
};

/** ⭐ O aviso do cartão: o que o pedido do contrato tem e o caso ainda não. */
export type FaltaDoPedido = { recebidoEm: string; investigados: number; marcos: string[]; notas: boolean; pesquisasSemCaso: number };

export const apiClientes = {
  listar: (busca?: string) =>
    chamar<{ clientes: Cliente[] }>(
      '/api/clientes' + (busca ? `?busca=${encodeURIComponent(busca)}` : ''),
    ),

  /**
   * A ficha da empresa pelo CNPJ, para o cadastro se preencher sozinho.
   *
   * ⚠️ **Gratuita, e por isso automática.** A regra de *"consulta paga nunca é
   * automática"* existe para gasto; esta lê as bases públicas do CNPJ e não
   * custa nada. Não há equivalente para CPF: nome de pessoa física custa
   * dinheiro, e gasto se autoriza por clique.
   *
   * ⚠️ **`achou: false` NÃO é erro** — é base pública fora do ar ou CNPJ que
   * não existe. A tela segue com os campos em branco.
   */
  pelocnpj: (cnpj: string) =>
    chamar<{
      achou: boolean;
      empresa: {
        nome: string;
        nomeFantasia: string | null;
        situacao: string | null;
        cep: string | null;
        logradouro: string | null;
        numero: string | null;
        complemento: string | null;
        bairro: string | null;
        cidade: string | null;
        uf: string | null;
      } | null;
    }>(`/api/clientes/pelo-cnpj/${encodeURIComponent(cnpj)}`),

  conferirDocumento: (documento: string) =>
    chamar<{ existe: boolean; cliente: { id: string; nome: string } | null }>(
      `/api/clientes/conferir-documento?documento=${encodeURIComponent(documento)}`,
    ),

  ficha: (id: string) => chamar<Ficha>(`/api/clientes/${id}`),

  criarContato: (
    clienteId: string,
    dados: { nome: string; cargo?: string | null; email?: string | null; telefone?: string | null; principal?: boolean },
  ) =>
    chamar<Contato>(`/api/clientes/${clienteId}/contatos`, {
      method: 'POST',
      body: JSON.stringify(dados),
    }),

  editarContato: (
    clienteId: string,
    contatoId: string,
    dados: Partial<{
      nome: string;
      cargo: string | null;
      email: string | null;
      telefone: string | null;
      principal: boolean;
    }>,
  ) =>
    chamar<Contato>(`/api/clientes/${clienteId}/contatos/${contatoId}`, {
      method: 'PATCH',
      body: JSON.stringify(dados),
    }),

  apagarContato: (clienteId: string, contatoId: string) =>
    chamar<void>(`/api/clientes/${clienteId}/contatos/${contatoId}`, { method: 'DELETE' }),

  criarAnotacao: (clienteId: string, texto: string) =>
    chamar<Anotacao>(`/api/clientes/${clienteId}/anotacoes`, {
      method: 'POST',
      body: JSON.stringify({ texto }),
    }),

  editarAnotacao: (clienteId: string, anotacaoId: string, texto: string) =>
    chamar<Anotacao>(`/api/clientes/${clienteId}/anotacoes/${anotacaoId}`, {
      method: 'PATCH',
      body: JSON.stringify({ texto }),
    }),

  apagarAnotacao: (clienteId: string, anotacaoId: string) =>
    chamar<void>(`/api/clientes/${clienteId}/anotacoes/${anotacaoId}`, { method: 'DELETE' }),

  criar: (dados: DadosCliente) =>
    chamar<Cliente>('/api/clientes', { method: 'POST', body: JSON.stringify(dados) }),

  editar: (id: string, dados: Partial<DadosCliente>) =>
    chamar<Cliente>(`/api/clientes/${id}`, { method: 'PATCH', body: JSON.stringify(dados) }),

  apagar: (id: string) => chamar<void>(`/api/clientes/${id}`, { method: 'DELETE' }),
};

export const apiCasos = {
  listar: (filtro?: { situacao?: SituacaoCaso; clienteId?: string }) => {
    const p = new URLSearchParams();
    if (filtro?.situacao) p.set('situacao', filtro.situacao);
    if (filtro?.clienteId) p.set('clienteId', filtro.clienteId);
    const q = p.toString();
    return chamar<{ casos: Caso[] }>('/api/casos' + (q ? `?${q}` : ''));
  },

  /**
   * Um caso, inteiro.
   *
   * ⚠️ **A rota já existia** (`GET /api/casos/:id`, com os movimentos); só não
   * havia chamada para ela aqui. O card dos marcos da dívida, no topo do
   * relatório, precisa do caso completo — o relatório traz só um resumo dele.
   */
  ler: (id: string) => chamar<Caso>(`/api/casos/${id}`),

  criar: (dados: DadosCaso) =>
    chamar<Caso & { doPedido: TrazidoNoCasoNovo | null }>('/api/casos', { method: 'POST', body: JSON.stringify(dados) }),

  /** ⭐ O que o caso vai trazer do pedido, antes de gravar (29/10/2026). */
  origemDoPedido: (q: { clienteId: string; contratoId: string | null; nivel: NivelServico }) => {
    const p = new URLSearchParams({ clienteId: q.clienteId, nivel: q.nivel });
    if (q.contratoId) p.set('contratoId', q.contratoId);
    return chamar<OrigemDoCasoNovo>(`/api/casos/origem-do-pedido?${p.toString()}`);
  },

  editar: (id: string, dados: Partial<Omit<DadosCaso, 'clienteId'>>) =>
    chamar<Caso>(`/api/casos/${id}`, { method: 'PATCH', body: JSON.stringify(dados) }),

  mover: (id: string, situacao: SituacaoCaso, nota?: string) =>
    chamar<Caso>(`/api/casos/${id}/situacao`, {
      method: 'POST',
      body: JSON.stringify({ situacao, nota }),
    }),

  registrarReuniao: (id: string, dados: { data: string; assunto: string; observacao?: string | null }) =>
    chamar<Caso>(`/api/casos/${id}/reunioes`, { method: 'POST', body: JSON.stringify(dados) }),

  apagarReuniao: (id: string, reuniaoId: string) =>
    chamar<Caso>(`/api/casos/${id}/reunioes/${reuniaoId}`, { method: 'DELETE' }),

  editarReuniao: (
    id: string,
    reuniaoId: string,
    dados: { data?: string; assunto?: string; observacao?: string | null },
  ) =>
    chamar<Caso>(`/api/casos/${id}/reunioes/${reuniaoId}`, {
      method: 'PATCH',
      body: JSON.stringify(dados),
    }),

  marcarReuniao: (id: string, reuniaoId: string, realizada: boolean) =>
    chamar<Caso>(`/api/casos/${id}/reunioes/${reuniaoId}/realizada`, {
      method: 'POST',
      body: JSON.stringify({ realizada }),
    }),

  apagar: (id: string) => chamar<void>(`/api/casos/${id}`, { method: 'DELETE' }),
};

export type DadosContrato = {
  clienteId: string;
  /**
   * ⭐ A proposta montada a partir de um PEDIDO DE PESQUISA (17/09/2026). O
   * servidor tira o pedido de "a conferir" na mesma gravação.
   */
  pedidoId?: string;
  objeto: string;
  /** Nasce como oferta ou como contrato já fechado. */
  situacao?: 'proposta' | 'ativo';
  /**
   * ⚠️ **`nivel` ficou para trás em 28/08/2026** — quem diz o que foi vendido é
   * `itens`. Continua no tipo porque a coluna existe no banco; nada o lê mais.
   */
  nivel?: NivelServico | null;
  /**
   * O que o cliente está contratando — uma linha por pesquisa.
   *
   * ⚠️ **Quando vem, substitui a lista inteira.** Corrigir uma e deixar as
   * outras seria mesclar dois estados.
   */
  itens?: Array<{
    nivel: NivelServico;
    valor: string;
    /** Nulo = o teto do nível, que o servidor resolve. */
    investigados?: number | null;
    /** QUEM esta pesquisa investiga. */
    entendimento?: string | null;
    /** Só o Customizado: o escopo, o prazo em dias e as reuniões do briefing. */
    escopo?: string | null;
    prazoDias?: number | null;
    reunioes?: number | null;
  }>;
  entendimento?: string | null;
  investigados?: number | null;
  fundoDeCustas?: string | null;
  cortesia?: boolean;
  validadeDias?: number | null;
  condicoesPagamento?: string | null;
  casoIds?: string[];
  valorTotal: string;
  formaPagamento: FormaPagamento;
  entrada?: string | null;
  parcelas?: number;
  /** Não se exige em proposta: cobrança só nasce no aceite. */
  primeiroVencimento?: string;
  intervalo?: Intervalo;
  /** Combinadas à mão: substituem a geração automática por inteiro. */
  cobrancas?: CobrancaDigitada[];
  assinadoEm?: string | null;
  observacao?: string | null;
};

export type ReferenciaDoNivel = {
  id: NivelServico;
  nome: string;
  investigados: number | null;
  dias: number | null;
  reunioes: number | null;
  honorarios: string | null;
  fundoDeCustas: string | null;
  /**
   * ⭐ **Contrato de cortesia — 10/09/2026.** Sem valor, sem aceite, sem fundo
   * de custas. ⚠️ **Chega mesmo ao colaborador**, que não vê valor: sem isto a
   * ficha pareceria incompleta e ele perguntaria por um dinheiro que não existe.
   */
  cortesia: boolean;
  investigadoAdicional: string | null;
};

export const apiContratos = {
  /** Os valores de referência. Vêm do servidor: preço tem uma fonte só. */
  referencia: () => chamar<{ niveis: ReferenciaDoNivel[] }>('/api/contratos/referencia'),

  criar: (dados: DadosContrato) =>
    chamar<Contrato>('/api/contratos', { method: 'POST', body: JSON.stringify(dados) }),

  editar: (
    id: string,
    dados: Partial<{
      objeto: string;
      situacao: SituacaoContrato;
      assinadoEm: string | null;
      observacao: string | null;
      casoIds: string[];
      valorTotal: string;
      /**
       * O que o cliente está contratando — uma linha por pesquisa.
       *
       * ⚠️ **Quando vem, substitui a lista inteira**, e a soma tem de fechar
       * com o total. Substituiu `nivel`, `entendimento` e `investigados`
       * soltos, que só sabiam falar de uma pesquisa.
       */
      itens: DadosContrato['itens'];
      /** ⚠️ Ficaram para trás em 28/08/2026 — nada os lê mais. */
      nivel: NivelServico | null;
      entendimento: string | null;
      investigados: number | null;
      fundoDeCustas: string | null;
  /**
   * ⭐ **Contrato de cortesia — 10/09/2026.** Sem valor, sem aceite, sem fundo
   * de custas. ⚠️ **Chega mesmo ao colaborador**, que não vê valor: sem isto a
   * ficha pareceria incompleta e ele perguntaria por um dinheiro que não existe.
   */
  cortesia: boolean;
      validadeDias: number | null;
      condicoesPagamento: string | null;
    }>,
  ) => chamar<Contrato>(`/api/contratos/${id}`, { method: 'PATCH', body: JSON.stringify(dados) }),

  /** Refaz o conjunto inteiro de cobranças. A soma tem de bater com o total. */
  /**
   * Substitui as cobranças — e o total do contrato, quando ele muda junto.
   *
   * ⚠️ Os dois viajam no mesmo pedido porque a soma tem de fechar com o total:
   * em chamadas separadas, a primeira deixaria o contrato inválido.
   */
  refazerParcelas: (id: string, parcelas: CobrancaDigitada[], valorTotal?: string) =>
    chamar<Contrato>(`/api/contratos/${id}/parcelas`, {
      method: 'PUT',
      body: JSON.stringify(valorTotal ? { parcelas, valorTotal } : { parcelas }),
    }),

  /**
   * Aceitar a proposta: ela vira contrato ativo e as cobranças nascem agora.
   *
   * Parcela só existe no aceite — antes disso, data de vencimento de um
   * negócio que talvez não feche seria data inventada.
   */
  aceitar: (
    id: string,
    dados: {
      assinadoEm?: string | null;
      /** ⚠️ Opcional só na cortesia, que não tem cobrança de honorário. */
      primeiroVencimento?: string;
      formaPagamento?: FormaPagamento;
      entrada?: string | null;
      parcelas?: number;
      intervalo?: Intervalo;
    },
  ) => chamar<Contrato>(`/api/contratos/${id}/aceitar`, {
    method: 'POST',
    body: JSON.stringify(dados),
  }),

  /** O PDF da proposta. Sai de qualquer contrato que tenha nível. */
  proposta: (id: string) => `/api/contratos/${id}/proposta.pdf`,

  baixarParcela: (id: string, parcelaId: string, pagoEm: string | null) =>
    chamar<Contrato>(`/api/contratos/${id}/parcelas/${parcelaId}/pagamento`, {
      method: 'POST',
      body: JSON.stringify({ pagoEm }),
    }),

  apagar: (id: string) => chamar<void>(`/api/contratos/${id}`, { method: 'DELETE' }),
};

// ── Financeiro ───────────────────────────────────────────────────────────────

/** Uma cobrança como o painel do Financeiro a mostra. */
export type CobrancaNoPainel = {
  parcelaId: string;
  numero: number;
  valor: string;
  vencimentoEm: string;
  contratoId: string;
  contratoCodigo: string;
  /** O que foi vendido — nunca o nome do investigado. Ver `rotas/financeiro.ts`. */
  contratoNivel: string;
  clienteId: string;
  clienteNome: string;
  /** Dias já passados, em `vencidas`; dias que faltam, em `aVencer`. */
  dias: number;
};

export type PainelFinanceiro = {
  hoje: string;
  mes: string;
  janela: number;
  resumo: {
    contratado: string;
    recebido: string;
    aReceber: string;
    vencido: string;
    recebidoNoMes: string;
    aVencerNaJanela: string;
  };
  vencidas: CobrancaNoPainel[];
  aVencer: CobrancaNoPainel[];
};

/**
 * ⚠️ Só o master alcança. A aba é feita de dinheiro, e o colaborador não vê
 * dinheiro — para ele o servidor recusa com 403, e a aba nem aparece.
 */
/**
 * O gasto de uma fonte paga no período apurado.
 *
 * ⚠️ **Este é o QUARTO dinheiro do Hub, e não se soma a nenhum dos outros:** o
 * contrato é o que a KAIRÓS **recebe**, o passivo é o que o investigado
 * **deve**, a avaliação é o que ele **tem**, e isto é o que a KAIRÓS **gasta
 * para apurar**. Somar dois quaisquer é o erro mais grosseiro possível.
 */
export type GastoDaFonte = {
  chave: string;
  nome: string;
  /** O piso mensal pago, use-se ou não. */
  piso: string;
  /** Teto de gasto além do piso, quando há. */
  teto: string | null;
  gasto: string;
  /** ⚠️ Nulo = "não dá para calcular", nunca zero. */
  sobra: string | null;
  consultas: number;
  /** Reconsulta não custa — separada para o acumulado não parecer maior que a fatura. */
  reconsultas: number;
  /** ⚠️ Podem ter cobrado sem o Hub saber. Reconsulta NÃO entra aqui. */
  semCustoConhecido: number;
  falhas: number;
  /** ⚠️ Das que falharam, quantas cobraram assim mesmo — paga-se pela resposta. */
  falhasCobradas: number;
  lacuna: string | null;
};

export type CustoDasFontes = {
  de: string;
  ate: string;
  /** ⚠️ A tela precisa dizer qual dos dois: ciclo do plano ou mês do calendário. */
  periodo: 'ciclo' | 'mes_calendario';
  fontes: GastoDaFonte[];
  gastoNoPeriodo: string;
  gastoDesdeSempre: string;
};

export const apiFinanceiro = {
  painel: (mes?: string) =>
    chamar<PainelFinanceiro>('/api/financeiro' + (mes ? `?mes=${mes}` : '')),
  /**
   * ⚠️ **Chamada SEPARADA da do painel, de propósito.** O painel é feito de
   * contrato e parcela; este é o dinheiro que sai. Vindo juntos, a primeira tela
   * a precisar de um total somaria os dois.
   */
  fontes: () => chamar<CustoDasFontes>('/api/financeiro/fontes'),
};

// ── Comercial: o funil ───────────────────────────────────────────────────────

/** As sete etapas, mais as duas saídas — PRD §49. */
export type SituacaoDaProspeccao =
  | 'a_contatar' | 'contatado' | 'retomada' | 'respondeu_aguardando' | 'conversando'
  | 'reuniao_marcada' | 'proposta_enviada' | 'fechado' | 'sem_resposta' | 'recusou';

/** ⭐ PRD §63.2: o tipo do e-mail que voltou, lido do código do aviso. */
export type TipoDeRetorno = 'endereco_nao_existe' | 'dominio_nao_recebe' | 'bloqueio' | 'passageiro' | 'nao_sei';

/** ⭐ PRD §63.2: por que a empresa ficou sem por onde receber. */
export type MotivoDoInsucesso = 'sem_email_valido' | 'dominio_nao_recebe' | 'dominio_bloqueado';

export type CanalDoToque = 'email' | 'whatsapp' | 'telefone' | 'reuniao' | 'outro';

export type ToqueDaProspeccao = {
  id: string;
  canal: CanalDoToque;
  /** ⭐ PRD §63.3: a casa falou, a empresa respondeu, ou o e-mail voltou. */
  tipo: 'contato' | 'resposta' | 'devolucao';
  modelo: string;
  observacao: string;
  /** ⭐ Quando ACONTECEU (AAAA-MM-DD), que pode não ser quando se anotou. */
  aconteceuEm: string;
  criadoEm: string;
  criadoPor: { nome: string };
};

/** Com quem se falar na empresa. Sem nome, é um canal da empresa (o `contato@`). */
export type ContatoDaProspeccao = {
  id: string;
  nome: string;
  cargo: string;
  tratamento: string;
  email: string;
  telefone: string;
  whatsapp: string;
  linkedin: string;
  principal: boolean;
  observacao: string;
  /**
   * ⭐ **Quando o e-mail voltou** — `docs/PROSPECCAO.md` §4.7.
   *
   * ⚠️ **Nulo é o normal**, e não quer dizer que chegou: quer dizer que não
   * voltou. Com dez por dia, o que volta é o único sinal que existe.
   */
  voltouEm: string | null;
  voltouMotivo: string;
  /** ⭐ PRD §63.2: o último aviso de devolução, por tipo, e a nova tentativa do passageiro. */
  retornoTipo: TipoDeRetorno | null;
  retornoCodigo: string;
  retornoEm: string | null;
  tentarDeNovoEm: string | null;
  conferir: boolean;
  criadoEm: string;
};

export type DadosDoContato = {
  nome?: string;
  cargo?: string;
  tratamento?: string;
  email?: string;
  telefone?: string;
  whatsapp?: string;
  linkedin?: string;
  observacao?: string;
  principal?: boolean;
};

/** As ordens que a lista oferece. `cadastro` é a ordem do levantamento. */
export type OrdemDoFunil = 'nome' | 'cadastro' | 'contato' | 'movimentacao' | 'etapa' | 'cidade' | 'passo';

/**
 * ⭐⭐ **O nível dentro do texto — 21/09/2026.** Não é cor: é o NOME do nível, e
 * a cor sai dele, das variáveis de `tokens.css`. *Cor não se inventa.*
 */
export type NivelNoTexto = 'insight' | 'forensic' | 'kairos';

/** Um pedaço do parágrafo. Mesmo vocabulário das anotações do relatório. */
export type TrechoDoModelo = { t: string; n?: true; i?: true; nivel?: NivelNoTexto };
/** Um parágrafo do modelo. `lista` é o tópico com marcador — 22/09/2026. */
export type ParagrafoDoModelo = { trechos: TrechoDoModelo[]; lista?: true };

export type ModeloDeAbordagem = {
  id: string;
  canal: 'email' | 'whatsapp';
  paraQuem: string;
  assunto: string;
  /** O texto simples, para onde formatação não entra. */
  corpo: string;
  /** O MESMO texto, com o negrito e a cor. É o que o Gmail recebe ao colar. */
  paragrafos: ParagrafoDoModelo[];
  /** O que o Hub NÃO sabe e a pessoa precisa completar antes de enviar. */
  aPreencher: string[];
};

export type Prospeccao = {
  id: string;
  nome: string;
  documento: string;
  cidade: string;
  uf: string;
  grupo: string;
  perfil: string;
  /** ⚠️ Ficaram para trás: quem responde por pessoa agora é `contatos`. */
  decisor: string;
  cargo: string;
  tratamento: string;
  email: string;
  telefone: string;
  whatsapp: string;
  site: string;
  linkedin: string;
  modelo: string;
  sinal: string;
  ressalva: string;
  ordem: number | null;
  situacao: SituacaoDaProspeccao;
  /** Nulo quer dizer NUNCA contatada. */
  ultimoContatoEm: string | null;
  ultimaMovimentacaoEm: string | null;
  clienteId: string | null;
  cliente: { id: string; nome: string } | null;
  /**
   * ⭐⭐ **O próximo passo, e quando — 21/09/2026.**
   *
   * ⚠️ `proximoPassoDoHub` diz quem escreveu: `true` é a cadência automática,
   * `false` é gente. O Hub **só reescreve o que ele mesmo pôs**.
   */
  proximoPasso: string;
  proximoPassoEm: string | null;
  proximoPassoDoHub: boolean;
  /**
   * ⛔⛔ **O pedido de não contatar — 21/09/2026.** Nulo é o normal.
   *
   * Não é etapa: é um fato. Uma empresa pode estar conversando e pedir para
   * sair, e o pedido não apaga onde ela estava.
   */
  naoContatarEm: string | null;
  naoContatarCanal: string;
  naoContatarObs: string;
  naoContatarPor: { nome: string } | null;
  /** ⭐ PRD §63.2: o "insucesso" (ficou sem e-mail que receba), com o motivo. */
  insucessoEm: string | null;
  insucessoMotivo: MotivoDoInsucesso | null;
  /** Vai UMA vez para a lista de pesquisa do Comercial. */
  paraPesquisar: boolean;
  /** ⭐ Os domínios dela que estão bloqueados (só na ficha: `GET /api/comercial/:id`). */
  dominiosBloqueados?: { dominio: string; motivo: 'sair' | 'bloqueio'; codigo: string; criadoEm: string }[];
  contatos: ContatoDaProspeccao[];
  toques: ToqueDaProspeccao[];
  criadoEm: string;
  atualizadoEm: string;
  /** Só volta do POST e do PATCH, quando o documento não fecha. */
  avisoDoDocumento?: string | null;
};

export type PainelComercial = {
  prospeccoes: Prospeccao[];
  placar: Record<SituacaoDaProspeccao, number>;
  /** ⛔ Quantas pediram para não ser procuradas. Fora do placar, porque não é etapa. */
  naoContatar: number;
  /** ⭐ Quantos ENDEREÇOS voltaram — a medida da qualidade da lista. */
  emailsQueVoltaram: number;
  /** ⭐ PRD §63.2: quantas ficaram sem e-mail que receba, e quantas esperam a pesquisa. */
  insucessos: number;
  paraPesquisar: number;
  /** ⭐ A fila do dia. `paraHoje` INCLUI o atrasado: é a lista de trabalho. */
  fila: { paraHoje: number; atrasado: number };
  /** A lista em que a tela está: a fila, ou quem saiu dela. */
  vendoQuemPediu: boolean;
  total: number;
  ordem: OrdemDoFunil;
  sentido: 'asc' | 'desc';
};

export const apiComercial = {
  listar: (
    filtro: {
      situacao?: string;
      grupo?: string;
      termo?: string;
      ordem?: string;
      sentido?: string;
      naoContatar?: string;
      /** '1': só as empresas com algum e-mail que voltou. */
      voltaram?: string;
      /** '1': só as do "insucesso" (PRD §63.2). */
      insucesso?: string;
      fila?: string;
    } = {},
  ) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(filtro)) if (v) q.set(k, v);
    const cauda = q.toString();
    return chamar<PainelComercial>('/api/comercial' + (cauda ? `?${cauda}` : ''));
  },
  ver: (id: string) => chamar<Prospeccao>(`/api/comercial/${id}`),
  /**
   * ⭐ **Marcar o que vem, e quando.** Do colaborador: dizer o próximo passo é
   * o trabalho do dia. ⛔ Escrever à mão tira o passo das mãos do Hub.
   */
  proximoPasso: (id: string, dados: { passo: string; em?: string }) =>
    chamar<Prospeccao>(`/api/comercial/${id}/proximo-passo`, { method: 'PATCH', body: JSON.stringify(dados) }),
  /**
   * ⛔⛔ **Registrar que a empresa pediu para não ser procurada.**
   *
   * ⭐ É do colaborador: registrar isso é o trabalho do dia e não pode esperar
   * autorização. ⛔ Desfazer é do master.
   */
  naoContatar: (id: string, dados: { canal: CanalDoToque; observacao?: string }) =>
    chamar<Prospeccao>(`/api/comercial/${id}/nao-contatar`, { method: 'POST', body: JSON.stringify(dados) }),
  /** ⛔ Do master, e só para o caso do clique errado. */
  desfazerNaoContatar: (id: string) =>
    chamar<Prospeccao>(`/api/comercial/${id}/nao-contatar`, { method: 'DELETE' }),
  /**
   * ⭐ **O e-mail voltou.** Do colaborador: quem mandou vê a devolução na
   * caixa minutos depois, e anotar é o trabalho do dia.
   */
  emailVoltou: (id: string, contatoId: string, dados: { motivo?: string }) =>
    chamar<Prospeccao>(`/api/comercial/${id}/contatos/${contatoId}/voltou`, {
      method: 'POST',
      body: JSON.stringify(dados),
    }),
  /** ⛔ Do master — para o clique na linha errada. */
  desfazerEmailVoltou: (id: string, contatoId: string) =>
    chamar<Prospeccao>(`/api/comercial/${id}/contatos/${contatoId}/voltou`, { method: 'DELETE' }),
  /** ⛔ Do master: desfaz o bloqueio de um domínio (o engano), com o registro de quem desfez e quando. */
  desfazerBloqueio: (dominio: string) =>
    chamar<{ dominio: string; empresasDeVolta: number }>(`/api/comercial/dominios-bloqueados/${encodeURIComponent(dominio)}`, { method: 'DELETE' }),
  criar: (dados: Partial<Prospeccao>) =>
    chamar<Prospeccao>('/api/comercial', { method: 'POST', body: JSON.stringify(dados) }),
  editar: (id: string, dados: Partial<Prospeccao>) =>
    chamar<Prospeccao>(`/api/comercial/${id}`, { method: 'PATCH', body: JSON.stringify(dados) }),
  /** ⭐ Mover de situação é do colaborador também — é o trabalho do dia. */
  mover: (id: string, situacao: SituacaoDaProspeccao) =>
    chamar<Prospeccao>(`/api/comercial/${id}/situacao`, { method: 'PATCH', body: JSON.stringify({ situacao }) }),
  /** ⭐ Os oito textos, já com o nome e o tratamento desta empresa dentro. */
  modelos: (id: string) =>
    chamar<{ indicado: string; saudacao: string; para: string; modelos: ModeloDeAbordagem[] }>(`/api/comercial/${id}/modelos`),
  criarContato: (id: string, d: DadosDoContato) =>
    chamar<Prospeccao>(`/api/comercial/${id}/contatos`, { method: 'POST', body: JSON.stringify(d) }),
  editarContato: (id: string, contatoId: string, d: DadosDoContato) =>
    chamar<Prospeccao>(`/api/comercial/${id}/contatos/${contatoId}`, { method: 'PATCH', body: JSON.stringify(d) }),
  /** ⛔ Do master. */
  apagarContato: (id: string, contatoId: string) =>
    chamar<Prospeccao>(`/api/comercial/${id}/contatos/${contatoId}`, { method: 'DELETE' }),
  registrarToque: (id: string, toque: { canal: CanalDoToque; modelo?: string; observacao?: string; aconteceuEm?: string }) =>
    chamar<Prospeccao>(`/api/comercial/${id}/toques`, { method: 'POST', body: JSON.stringify(toque) }),
  /**
   * ⭐ **O prospecto virou cliente.** Grava o vínculo e move para `fechado` na
   * mesma chamada — é um ato só.
   *
   * ⛔ **Só no vazio:** prospecto que já aponta para um cliente devolve 409.
   */
  vincularCliente: (id: string, clienteId: string) =>
    chamar<Prospeccao>(`/api/comercial/${id}/cliente`, { method: 'PATCH', body: JSON.stringify({ clienteId }) }),
  /** ⛔ Do master: desfaz o vínculo. Não mexe no cliente nem volta a etapa. */
  desvincularCliente: (id: string) =>
    chamar<Prospeccao>(`/api/comercial/${id}/cliente`, { method: 'DELETE' }),
  apagar: (id: string) => chamar<void>(`/api/comercial/${id}`, { method: 'DELETE' }),
};

// ── Marketing: o calendário editorial (PRD §50) ──────────────────────────────

/*
 * ⛔⛔ **A aba nasce isolada do banco de casos** (PRD §7.1). Este bloco só fala
 * com `/api/marketing`, e a regressão (`npm run testar-marketing`) confere isso
 * lendo este arquivo. ⭐ E a aba inteira é do master: o colaborador recebe 403
 * em toda rota, e a aba nem aparece no menu dele.
 */
export type RedeDoPost = 'instagram' | 'linkedin';
export type FormatoDoPost = 'carrossel' | 'imagem_unica' | 'texto';
export type SituacaoDoPost = 'ideia' | 'rascunho' | 'aprovado' | 'publicado';

export type PostDeMarketing = {
  id: string;
  /** O tema, como o post vai dizer. Não sobe para maiúscula: é frase. */
  titulo: string;
  /** Sempre na ordem Instagram, LinkedIn, e nunca vazio. */
  redes: RedeDoPost[];
  formato: FormatoDoPost;
  situacao: SituacaoDoPost;
  /** O plano. `AAAA-MM-DD`. */
  dataPrevista: string;
  /** O fato: quando foi ao ar. Só existe com a situação `publicado`. */
  publicadoEm: string | null;
  /** ⭐ Onde o post aparece na grade: a publicação, se houve; senão, o previsto. */
  diaNoCalendario: string;
  linkInstagram: string;
  linkLinkedin: string;
  observacao: string;
  criadoPor: { nome: string };
  publicadoPor: { nome: string } | null;
  criadoEm: string;
  atualizadoEm: string;
};

export type PainelDoMarketing = {
  /** "Hoje" do servidor, em São Paulo. */
  hoje: string;
  mes: string | null;
  posts: PostDeMarketing[];
  /** Quantos em cada situação, no recorte do mês e fora do filtro de situação. */
  placar: Record<SituacaoDoPost, number>;
  total: number;
};

/** O conteúdo do post: o que o formulário escreve. A situação anda por rota própria. */
export type DadosDoPost = {
  titulo: string;
  redes: RedeDoPost[];
  formato: FormatoDoPost;
  dataPrevista: string;
  observacao: string;
};

/** O que se diz ao marcar como publicado: o dia e, se houver, os links. */
export type PublicacaoDoPost = { publicadoEm: string; linkInstagram: string; linkLinkedin: string };

/* ⭐⭐ O CONTEÚDO DO POST — parte 2, 15/10/2026 (PRD §50.5). */

/**
 * O que um slide diz. `id` só existe depois de gravado, e não muda mais.
 * ⭐ `manifesto` (parte 4): o desenho do manifesto na imagem única, escolha da pessoa.
 */
export type TextoDoSlide = { id?: string; rotulo: string; titulo: string; apoio: string; textoAlternativo: string; manifesto?: boolean };

/** O rascunho da tela: o que se manda conferir e gravar. */
export type TextosDoPost = { slides: TextoDoSlide[]; legenda: string; hashtags: string; textoLinkedin: string };

export type AchadoNoPost = {
  /** `slides.0.titulo`, `legenda`, `hashtags`, `textoLinkedin`, `slides` ou `post`. */
  campo: string;
  /** "Slide 2 · título". */
  onde: string;
  regra: string;
  /** `grave` pede a pergunta antes de aprovar; `atencao` pede olhada. */
  gravidade: 'grave' | 'atencao';
  aviso: string;
  trecho: string;
};

export type LimiteDaRede = { maximo: number; nota: string };

/** O fecho institucional: fixo, igual em todo carrossel, e posto pela montagem. */
export type FechoDoPost = {
  frase: string;
  contatos: Array<{ tipo: 'site' | 'email' | 'whatsapp'; texto: string }>;
  textoAlternativo: string;
};

export type ConteudoDoPost = {
  /** "Hoje" do servidor, em São Paulo: a ficha precisa dele para a publicação. */
  hoje: string;
  postId: string;
  /** ⛔ A trava de duas janelas: volta na gravação, e a gravação com a velha é recusada. */
  versao: number;
  atualizadoEm: string | null;
  slides: Array<Required<TextoDoSlide> & { ordem: number }>;
  legenda: string;
  hashtags: string;
  textoLinkedin: string;
  /** Só no carrossel. */
  fecho: FechoDoPost | null;
  /** ⭐ Os números das redes vêm do servidor: a tela não guarda uma segunda cópia. */
  limites: Record<'legenda' | 'hashtags' | 'textoAlternativo' | 'linkedin' | 'carrossel', LimiteDaRede>;
  /** A conferência do que está GRAVADO. */
  achados: AchadoNoPost[];
};

/* ⭐⭐ OS FUNDOS POR IA — parte 3, 16/10/2026 (PRD §50.6). ⛔ O Hub monta o prompt e não fala com IA nenhuma. */

/** Uma imagem que já chegou: a medida e o tipo do ORIGINAL, e os avisos do corte para 1080 × 1350. */
export type FundoEnviado = {
  id: string;
  /** `PNG`, `JPG` ou `WEBP`, conferido pelo conteúdo. */
  tipo: string;
  largura: number;
  altura: number;
  tamanho: number;
  enviadoEm: string;
  enviadoPor: { nome: string };
  avisos: string[];
};

/** Uma vaga: o slide (ou o fecho), o prompt da imagem dele e o fundo, se já chegou. */
export type VagaDoFundo = {
  numero: number;
  /** O id do slide, ou `fecho`. */
  peca: string;
  nome: string;
  titulo: string;
  /** Só no tema dos níveis. A cor vai no prompt; a tela só diz o nome e o código. */
  nivel: { nome: string; hex: string } | null;
  entrada: number | null;
  saida: number | null;
  prompt: string;
  ajusteDaEmenda: string | null;
  fundo: FundoEnviado | null;
};

export type VizinhoDoMotivo = { titulo: string; motivo: string; nome: string };

export type PainelDosFundos = {
  postId: string;
  formato: FormatoDoPost;
  /** O motivo gravado. Nulo: a pessoa ainda não escolheu, e vale a sugestão. */
  motivo: string | null;
  sugestao: { motivo: string; anterior: VizinhoDoMotivo | null; seguinte: VizinhoDoMotivo | null } | null;
  catalogo: Array<{ chave: string; nome: string; descricao: string }>;
  prompts: {
    motivo: string;
    continuo: boolean;
    total: number;
    estiloBase: string | null;
    coresDosNiveis: boolean;
    ajustes: Array<{ quando: string; texto: string }>;
    tudo: string;
  } | null;
  imagens: VagaDoFundo[];
  /** O que ficou fora do plano, como o fecho de um post que deixou de ser carrossel. */
  guardadosFora: Array<{ peca: string; nome: string; fundo: FundoEnviado }>;
  envio: { teto: number; tipos: string[] };
};

/* ⭐⭐ A MONTAGEM DAS ARTES — parte 4, 16/10/2026 (PRD §50.7). */

/** O aviso da arte: o texto que não cabe, o caractere que a fonte não tem, a emenda que não fecha. */
export type AvisoDaArte = { campo: string; onde: string; gravidade: 'grave' | 'atencao'; aviso: string };

export type PainelDasArtes = {
  postId: string;
  formato: FormatoDoPost;
  redes: RedeDoPost[];
  /** O começo do nome dos arquivos: "Os níveis" vira `os-niveis`. */
  nome: string;
  /** As peças na ordem em que vão ao ar, e se o fundo de cada uma já chegou. */
  pecas: Array<{ numero: number; nome: string; vaga: string; temFundo: boolean }>;
  /** O nome das peças sem fundo: sem elas, não se monta. */
  faltam: string[];
  /** Os achados graves de agora (a conferência e a arte): montar com eles pergunta antes. */
  graves: AchadoNoPost[];
  /** Os avisos da arte que pedem olhada. */
  avisos: AchadoNoPost[];
  montagem: {
    id: string;
    montadaEm: string;
    montadaPor: { nome: string };
    pecas: number;
    nomes: string[];
    /** ⭐ É a do texto e dos fundos de agora. Falso: algo mudou depois da montagem. */
    atual: boolean;
    comAchadosGraves: boolean;
    avisos: AvisoDaArte[];
  } | null;
};

export const apiMarketing = {
  listar: (filtro: { mes?: string; situacao?: string } = {}) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(filtro)) if (v) q.set(k, v);
    const cauda = q.toString();
    return chamar<PainelDoMarketing>('/api/marketing' + (cauda ? `?${cauda}` : ''));
  },
  ver: (id: string) => chamar<PostDeMarketing>(`/api/marketing/${id}`),
  criar: (dados: DadosDoPost) =>
    chamar<PostDeMarketing>('/api/marketing', { method: 'POST', body: JSON.stringify(dados) }),
  editar: (id: string, dados: Partial<DadosDoPost>) =>
    chamar<PostDeMarketing>(`/api/marketing/${id}`, { method: 'PATCH', body: JSON.stringify(dados) }),
  /**
   * ⭐ **Mudar de situação.** Publicado exige a data em que foi ao ar; voltar de
   * publicado tira a data e os links junto (a tela pergunta antes).
   */
  mudarSituacao: (
    id: string,
    situacao: SituacaoDoPost,
    publicacao?: PublicacaoDoPost,
    /** ⭐ A resposta à pergunta de aprovar com achado grave: sem ela, o servidor responde 409. */
    aprovarComAchados?: boolean,
  ) =>
    chamar<PostDeMarketing>(`/api/marketing/${id}/situacao`, {
      method: 'PATCH',
      body: JSON.stringify({ situacao, ...(publicacao ?? {}), ...(aprovarComAchados ? { aprovarComAchados } : {}) }),
    }),
  apagar: (id: string) => chamar<void>(`/api/marketing/${id}`, { method: 'DELETE' }),
  /** ⭐ O conteúdo gravado, com o fecho, os limites e a conferência. */
  conteudo: (id: string) => chamar<ConteudoDoPost>(`/api/marketing/${id}/conteudo`),
  /**
   * ⭐ Gravar o conteúdo. A lista de slides é a lista inteira; o que não veio
   * sai. ⛔ Com a versão velha, 409: outra janela gravou antes.
   */
  gravarConteudo: (id: string, versao: number, textos: TextosDoPost) =>
    chamar<ConteudoDoPost>(`/api/marketing/${id}/conteudo`, { method: 'PATCH', body: JSON.stringify({ versao, ...textos }) }),
  /** Conferir o rascunho sem gravar. */
  conferir: (id: string, textos: TextosDoPost) =>
    chamar<{ achados: AchadoNoPost[] }>(`/api/marketing/${id}/conferencia`, { method: 'POST', body: JSON.stringify(textos) }),
  /** ⭐ Parte 3: o motivo, os prompts e as vagas com as imagens. */
  fundos: (id: string) => chamar<PainelDosFundos>(`/api/marketing/${id}/fundos`),
  escolherMotivo: (id: string, motivo: string) =>
    chamar<PainelDosFundos>(`/api/marketing/${id}/fundos/motivo`, { method: 'PUT', body: JSON.stringify({ motivo }) }),
  /**
   * ⭐ Enviar (ou trocar) o fundo de uma vaga.
   *
   * ⚠️ **`fetch` direto, e não `chamar`**: o `Content-Type` tem de vir do
   * `FormData`, com a fronteira. ⛔ **E o arquivo vai com um nome fixo**: o nome
   * que ele tem no computador nem sai do navegador.
   */
  enviarFundo: async (id: string, peca: string, arquivo: File) => {
    const corpo = new FormData();
    corpo.append('arquivo', arquivo, 'fundo');
    const r = await fetch(`/api/marketing/${id}/fundos/${peca}`, { method: 'POST', body: corpo, credentials: 'same-origin' });
    const dados = (await r.json().catch(() => null)) as (PainelDosFundos & { erro?: string }) | null;
    if (!r.ok) {
      throw new ErroDaApi(
        dados?.erro ?? (r.status === 413 ? 'A imagem passa do tamanho que o Hub aceita.' : `O servidor respondeu com um erro (${r.status}) ao receber a imagem.`),
        r.status,
      );
    }
    return dados as PainelDosFundos;
  },
  removerFundo: (id: string, peca: string) => chamar<PainelDosFundos>(`/api/marketing/${id}/fundos/${peca}`, { method: 'DELETE' }),
  /** O endereço da imagem em 1080 × 1350. Passa pela sessão; o `?v=` muda quando a imagem é trocada. */
  imagemDoFundo: (id: string, peca: string, fundoId: string) => `/api/marketing/${id}/fundos/${peca}/imagem?v=${fundoId}`,
  /** ⭐ Parte 4: as peças, o que falta, os avisos e a montagem guardada. */
  artes: (id: string) => chamar<PainelDasArtes>(`/api/marketing/${id}/artes`),
  /**
   * ⭐ Montar (ou remontar) as artes. ⛔ Com achado grave, o servidor responde
   * 409; o "sim" da pergunta volta com `montarComAchados`.
   */
  montarArtes: (id: string, montarComAchados?: boolean) =>
    chamar<PainelDasArtes>(`/api/marketing/${id}/artes`, { method: 'POST', body: JSON.stringify(montarComAchados ? { montarComAchados } : {}) }),
  /** O endereço de uma arte. O `?v=` muda a cada montagem, e o navegador não mostra a velha. */
  imagemDaArte: (id: string, numero: number, montagemId: string) => `/api/marketing/${id}/artes/${numero}/imagem?v=${montagemId}`,
  zipDasArtes: (id: string) => `/api/marketing/${id}/artes/zip`,
  pdfDasArtes: (id: string) => `/api/marketing/${id}/artes/pdf`,
};

// ── Usuários e convite ───────────────────────────────────────────────────────

/**
 * Em que pé está o acesso de cada pessoa.
 *
 * `ativo` cobre dois casos que a tela não precisa separar: quem aceitou o
 * convite e quem nunca precisou de um (o login original da casa).
 */
export type SituacaoAcesso = 'ativo' | 'convite_pendente' | 'convite_vencido' | 'desativado';

export type UsuarioDaCasa = {
  id: string;
  nome: string;
  email: string;
  papel: Papel;
  ativo: boolean;
  situacao: SituacaoAcesso;
  criadoEm: string;
};

/** ⭐ PRD §60: o "sim" do Richard para a Ester apagar ou gastar. ⭐ §63.7: e para a cópia de fora e o anexo num rascunho da caixa. */
export type AutorizacaoDaEster = {
  id: string;
  metodo: string;
  caminho: string;
  classe: 'apagar' | 'gastar' | 'copia_e_anexo';
  resumo: string;
  codigo: string;
  estado: 'pendente' | 'aprovada' | 'recusada' | 'usada';
  forma: 'botao' | 'telegram' | null;
  pedidaEm: string;
  expiraEm: string;
  vencida: boolean;
};

export const apiAutorizacoes = {
  pendentes: () =>
    chamar<{ autorizacoes: AutorizacaoDaEster[] }>('/api/autorizacoes?estado=pendente').then((r) => r.autorizacoes),
  aprovar: (id: string) => chamar<{ autorizacao: AutorizacaoDaEster }>(`/api/autorizacoes/${id}/aprovar`, { method: 'POST' }),
  recusar: (id: string) => chamar<{ autorizacao: AutorizacaoDaEster }>(`/api/autorizacoes/${id}/recusar`, { method: 'POST' }),
};

export const apiUsuarios = {
  listar: () => chamar<{ usuarios: UsuarioDaCasa[] }>('/api/usuarios'),

  criar: (dados: { nome: string; email: string; papel: Papel }) =>
    chamar<UsuarioDaCasa & { conviteEnviado: boolean; aviso: string | null }>('/api/usuarios', {
      method: 'POST',
      body: JSON.stringify(dados),
    }),

  reenviarConvite: (id: string) =>
    chamar<{ ok: boolean; conviteEnviado: boolean; aviso: string | null }>(
      `/api/usuarios/${id}/convite`,
      { method: 'POST' },
    ),

  definirAtivo: (id: string, ativo: boolean) =>
    chamar<{ ok: boolean; ativo: boolean }>(`/api/usuarios/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ ativo }),
    }),
};

// ── Busca global ─────────────────────────────────────────────────────────────

export type TipoAchado = 'cliente' | 'caso' | 'contrato' | 'compromisso' | 'anotacao';

export type Achado = {
  tipo: TipoAchado;
  id: string;
  titulo: string;
  detalhe: string | null;
  /** Para onde a tela navega ao clicar. Vem pronto do servidor. */
  caminho: string;
};

export type GrupoDeAchados = {
  tipo: TipoAchado;
  rotulo: string;
  /** Havia mais do que coube. O painel avisa em vez de mentir por omissão. */
  temMais: boolean;
  itens: Achado[];
};

export type Busca = {
  termo: string;
  /** Termo com menos de 2 letras: nem se procura. */
  curto: boolean;
  total?: number;
  grupos: GrupoDeAchados[];
};

export const apiBusca = {
  procurar: (q: string) => chamar<Busca>(`/api/busca?q=${encodeURIComponent(q)}`),
};

/** As duas que funcionam sem sessão — quem abre o convite ainda não tem senha. */
export const apiConvite = {
  conferir: (token: string) =>
    chamar<{ valido: boolean; nome: string | null; email: string | null }>(
      `/api/convite/conferir?token=${encodeURIComponent(token)}`,
    ),

  aceitar: (token: string, senha: string) =>
    chamar<{ ok: boolean }>('/api/convite/aceitar', {
      method: 'POST',
      body: JSON.stringify({ token, senha }),
    }),
};

// ── Relatórios — Incremento 7 ────────────────────────────────────────────────

export type SituacaoRelatorio = 'rascunho' | 'fechado' | 'entregue';

export type NaturezaDaSecao = 'fato' | 'achado' | 'recomendacao' | 'ressalva';

export type SecaoDoRelatorio = {
  /**
   * ⭐ **A seção já foi respondida?** — vem do servidor, e é a mesma regra que
   * trava a entrega (`lib/secao-respondida.ts`).
   *
   * ⚠️ **Nunca se recalcula na tela.** Duas aritméticas para a mesma pergunta
   * divergem no primeiro ajuste feito só numa delas — e aí a barra diria "tudo
   * escrito" sobre um relatório que a entrega recusa.
   */
  respondida: boolean;
  id: string;
  chave: string;
  titulo: string;
  /** `I · O caso`, `II · O que foi apurado`… Agrupa as seções na tela. */
  parte: string;
  /**
   * A chave da seção MÃE, quando esta é uma subseção — 24/08/2026.
   *
   * ⚠️ **Subseção é uma seção com pai**, e não uma estrutura à parte: a tela a
   * desenha recuada e com título menor, mas o campo de texto, a trava de
   * `natureza` e a gravação são exatamente os mesmos. Duas máquinas para a
   * mesma coisa divergem na primeira correção feita só numa delas.
   */
  paiChave: string | null;
  /**
   * ⚠️ **É trava, não rótulo.** Integração só escreve em `fato`; achado e
   * recomendação são o trabalho da Karen, e a entrega os exige escritos.
   */
  natureza: NaturezaDaSecao;
  /** O que esta seção precisa responder. */
  proposito: string;
  conteudo: string;
  ordem: number;
  /** Os campos livres acrescentados dentro da seção — 28/08/2026. */
  anotacoes: AnotacaoDaSecao[];
};

/**
 * Um trecho de texto com formatação.
 *
 * ⚠️ **Nomes de uma letra, e é medida de tamanho** — um N3 pode ter dezenas de
 * anotações. `n` negrito · `i` itálico · `s` sublinhado · `g` grifo.
 */
export type Trecho = {
  t: string;
  /** Negrito. */
  n?: true;
  /** Itálico. */
  i?: true;
  /** Sublinhado. */
  s?: true;
  /**
   * Grifo — marca-texto.
   *
   * ⚠️ **`true` é o formato ANTIGO e vale como amarelo.** Era booleano até
   * 30/08/2026; as quatro cores entraram em 31/08 e o que já estava gravado
   * continua sendo lido — migrar texto gravado é onde se perde o que alguém
   * escreveu.
   */
  g?: true | 'amarelo' | 'azul' | 'verde' | 'vermelho';
  /** Riscado. */
  r?: true;
  /** Subscrito (baixo). */
  b?: true;
  /** Sobrescrito (cima). */
  c?: true;
};

export type Alinhamento = 'esquerda' | 'centro' | 'direita' | 'justificado';

/**
 * Um bloco de conteúdo — 28/08/2026.
 *
 * > *"Gostaria que tivessem mais ferramentas, como por exemplo: colocar
 * > tópicos, tabelas, justificar parágrafos e etc."* — Richard
 *
 * ⚠️ **A lista é um bloco, e não parágrafos com marcador.** Guardar cada tópico
 * solto perderia onde a lista começa e termina — e no Word a numeração precisa
 * disso para reiniciar em 1 na lista seguinte.
 *
 * ⚠️ **Na tabela, a PRIMEIRA linha é o cabeçalho** — é ela que o Word repete
 * quando a tabela vira a página.
 */
export type Bloco =
  | { especie: 'paragrafo'; alinhamento?: Alinhamento; trechos: Trecho[] }
  | { especie: 'lista'; ordenada?: true; itens: Trecho[][] }
  | { especie: 'tabela'; linhas: Trecho[][][] }
  /**
   * Uma imagem. ⚠️ **O corpo guarda a REFERÊNCIA, nunca os bytes** — print em
   * base64 dentro do JSON encheria a linha do banco de megabytes, e o dump
   * cifrado que sobe para o Drive toda noite iria junto.
   */
  /**
   * ⚠️ `larguraEmPorcento` é a largura ESCOLHIDA por quem escreve, em
   * porcentagem da coluna útil — 07/09/2026. **Ausente não é 100:** ausente quer
   * dizer "do tamanho do arquivo, limitado à folha", que é como tudo o que já
   * está escrito se comporta.
   */
  | { especie: 'imagem'; imagemId: string; legenda?: string; larguraEmPorcento?: number };

/**
 * Um campo de escrita a mais dentro de uma seção.
 *
 * ⚠️ **O corpo NÃO é HTML.** É uma lista de blocos — parágrafo, lista ou
 * tabela —, e o formato é a própria lista de permissões: o que não vira bloco
 * ou trecho não é guardado. Ver `api/src/lib/texto-formatado.ts`.
 */
export type AnotacaoDaSecao = {
  id: string;
  ordem: number;
  titulo: string | null;
  corpo: Bloco[];
  /**
   * ⭐ De qual investigado é este campo — 31/08/2026.
   *
   * ⚠️ **Nulo é o normal:** campo da seção inteira. Preenchido quer dizer que
   * ele pertence àquela pessoa, e a tela o mostra sob o nome dela.
   */
  investigadoId?: string | null;
};

/** ⭐ 07/10/2026 — a escala do índice de probabilidade de recuperação do crédito. */
export type ProbabilidadeDeRecuperacao = 'provavel' | 'possivel' | 'remota';

export type Relatorio = {
  id: string;
  codigo: string;
  situacao: SituacaoRelatorio;
  sintese: string | null;
  entregueEm: string | null;
  criadoEm: string;
  /** ⭐ Quando a apuração começou. Nulo = a data em que o relatório nasceu (PRD §50). */
  apuracaoDe: string | null;
  /** ⭐ 07/10/2026 — o índice de probabilidade de recuperação do crédito. Nulo = não escolhido. */
  probabilidadeDeRecuperacao: ProbabilidadeDeRecuperacao | null;
  /** ⭐ O histórico: nasceu, saiu para o cliente, voltou para conserto. */
  eventos?: Array<{
    id: string;
    tipo: 'criado' | 'entregue' | 'reaberto';
    observacao: string;
    em: string;
    por: string;
  }>;
  caso: {
    id: string;
    codigo: string;
    titulo: string | null;
    nivel: NivelServico;
    nivelNome: string;
    /** ⭐ A pergunta que este nível responde, do portfólio. Vem do servidor. */
    nivelPergunta: string;
    /** O foco contratado daquele nível — o que o cliente comprou. */
    nivelFoco: string;
    investigados: number;
    prazoEm: string | null;
    /** ⭐ 29/09/2026: quanto do teto do Fundo de Custas o caso já usou (0 a 1+); nulo sem teto. Nunca o valor. */
    fundo?: { consumido: number | null };
  };
  cliente: { id: string; nome: string };
  /** Quantas seções já têm texto — é o avanço que a lista mostra. */
  escritas: number;
  total: number;
  /** Só vem quando se abre um relatório; a lista não as traz. */
  secoes?: SecaoDoRelatorio[];
  /**
   * O que cada levantamento NÃO alcança, por seção — o item 1.4 da estrutura.
   *
   * ⚠️ **A tela mostra o mesmo que o documento**, e no mesmo lugar: fechando o
   * capítulo, depois das anotações (`docs/PRD.md` §21).
   */
  ressalvas?: Record<string, Array<{ levantamento: string; texto: string }>>;
  /**
   * ⚠️ **Consultas cujo serviço o Hub não sabe dizer — SÓ para a tela.**
   * Consulta sem serviço é ressalva que não sai, ou seja, uma limitação que o
   * cliente não conhece. Nunca vai ao documento: é defeito de cadastro do Hub,
   * não limitação do trabalho.
   */
  consultasSemRessalva?: number;
};

/** Uma parte investigada, como sai no quadro do capítulo 1. */
export type ParteInvestigada = {
  nome: string;
  /** Já formatado. Vazio quando a ficha ainda não tem documento. */
  documento: string;
  /** A classe por extenso, com o grau acompanhando quando houver. */
  qualificacao: string;
  /** `true` = o cliente informou; `false` = a investigação trouxe. */
  doCliente: boolean;
};

/**
 * O que o Hub propõe para a abertura do relatório.
 *
 * ⚠️ **`objeto` e `escopo` podem vir nulos, e nulo não é vazio:** quer dizer
 * que não há de onde escrever — caso sem nenhum marco de dívida, ou sem
 * investigado cadastrado. A tela diz o que falta preencher, em vez de oferecer
 * um botão que produziria frase sem conteúdo.
 */
export type AberturaDoCaso = {
  partes: ParteInvestigada[];
  objeto: string | null;
  escopo: string | null;
};

/** Um fato do caso, na linha do tempo. */
export type MarcoNaLinha = {
  data: string;
  /**
   * ⚠️ **`constatacao` NÃO é fato:** a CNDT informa a situação na data da
   * certidão, não quando o débito surgiu. A tela precisa distinguir.
   */
  especie: 'divida' | 'aquisicao' | 'saida' | 'onus' | 'passivo' | 'ato' | 'constatacao' | 'processo';
  titulo: string;
  /** Nulo nos marcos da dívida, que são do caso e não de uma parte. */
  investigado: string | null;
  detalhe: string | null;
};

export type LinhaDoTempo = {
  marcos: MarcoNaLinha[];
  /** ⚠️ Quantos fatos o caso tem SEM data — e por isso não entram na linha. */
  semData: number;
  /** ⚠️ Quantos "nada consta" ficaram fora: ausência não tem data de fato. */
  ausencias: number;
  /** ⭐ 27/10/2026: imóveis que não são das partes (terceiro, grupo que não é parte, matrícula encerrada), fora da linha. */
  imoveisDeFora?: number;
  dividaEm: string | null;
  inadimplenciaEm: string | null;
  ajuizadoEm: string | null;
};

/**
 * ⭐ **O que falta apurar em cada tópico** — 24/09/2026, pedido da Karen.
 *
 * ⛔ O texto de resultado negativo só sai quando a consulta RESPONDEU. Quando
 * não sai, o tópico ficava mudo e ninguém sabia se era "não achamos" ou "não
 * procuramos". Isto é o que a tela usa para cobrar.
 */
export type ApuracaoDoTopico = {
  chave: string;
  autoriza: boolean;
  dependeDeConsulta: boolean;
  porPalavra: Record<string, number>;
  /** A frase da cobrança, com número. Nula quando não há o que cobrar. */
  porque: string | null;
};

export const apiRelatorios = {
  /**
   * ⭐ O estado da apuração por tópico, e a frase de escopo de cada um.
   *
   * ⚠️ **Só lê.** Não escreve em seção, não trava entrega, não consulta fonte.
   */
  apuracao: (id: string) =>
    chamar<{
      topicos: ApuracaoDoTopico[];
      escopos: Array<{ chave: string; frase: string | null }>;
    }>(`/api/relatorios/${id}/apuracao`),

  /** ⭐ A evolução do quadro das empresas que são parte — 15/09/2026. */
  evolucaoSocietaria: (id: string) =>
    chamar<{ empresas: EvolucaoDaEmpresa[] }>(`/api/relatorios/${id}/evolucao-societaria`),
  /** Cria o campo da evolução na qualificação da empresa. ⛔ Sempre um campo NOVO: o que existe não se toca. */
  levarEvolucao: (id: string, investigadoId: string) =>
    chamar<AnotacaoDaSecao>(`/api/relatorios/${id}/investigados/${investigadoId}/evolucao-societaria`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  listar: () => chamar<{ relatorios: Relatorio[] }>('/api/relatorios'),

  /** Abre o relatório do caso — ou devolve o que já existe. Um por caso. */
  abrir: (casoId: string) =>
    chamar<Relatorio>('/api/relatorios', {
      method: 'POST',
      body: JSON.stringify({ casoId }),
    }),

  ler: (id: string) => chamar<Relatorio>(`/api/relatorios/${id}`),

  /**
   * A síntese de abertura e o **início da apuração** (PRD §50).
   *
   * ⚠️ `apuracaoDe: null` volta ao padrão — a data em que o relatório nasceu.
   */
  editar: (id: string, dados: { sintese?: string | null; apuracaoDe?: string | null }) =>
    chamar<Relatorio>(`/api/relatorios/${id}`, { method: 'PATCH', body: JSON.stringify(dados) }),

  /** ⭐ 07/10/2026 — o índice de probabilidade de recuperação. Do master; nulo volta a "não escolhido". */
  definirRecuperacao: (id: string, nivel: ProbabilidadeDeRecuperacao | null) =>
    chamar<{ nivel: ProbabilidadeDeRecuperacao | null }>(`/api/relatorios/${id}/recuperacao`, { method: 'PUT', body: JSON.stringify({ nivel }) }),

  /* ── Os campos livres da seção · 28/08/2026 ────────────────────────────── */

  /**
   * Acrescenta um campo de escrita ao fim da seção.
   *
   * ⭐ `investigadoId` prende o campo a uma pessoa dentro da seção — 31/08/2026.
   * Omitido, o campo é da seção inteira, como sempre foi.
   */
  criarAnotacao: (id: string, secaoId: string, titulo?: string, investigadoId?: string) =>
    chamar<AnotacaoDaSecao>(`/api/relatorios/${id}/secoes/${secaoId}/anotacoes`, {
      method: 'POST',
      body: JSON.stringify({ titulo: titulo ?? null, investigadoId: investigadoId ?? null }),
    }),

  /**
   * Grava o texto formatado.
   *
   * ⚠️ **O servidor valida o corpo contra o formato de trechos**, e é lá que a
   * sanitização vale — a tela monta os trechos, mas quem garante é o servidor.
   * Trava só na tela é decoração.
   */
  escreverAnotacao: (
    id: string,
    anotacaoId: string,
    dados: { titulo: string | null; corpo: Bloco[] },
  ) =>
    chamar<
      AnotacaoDaSecao & {
        vazia: boolean;
        /** ⚠️ Avisa, não bloqueia: o texto grava sempre, e os achados vêm junto. */
        achados: Array<{ gravidade: 'grave' | 'atencao'; aviso: string; trecho: string }>;
      }
    >(`/api/relatorios/${id}/anotacoes/${anotacaoId}`, {
      method: 'PUT',
      body: JSON.stringify(dados),
    }),

  /**
   * Anexa imagem, ou converte um PDF em imagens.
   *
   * ⛔ **O PDF é CONVERTIDO, não anexado** — um `.docx` não embute página de
   * PDF. Cada página vira uma figura, e a resposta traz um bloco por página.
   *
   * ⚠️ **`fetch` direto, e não `chamar`:** o `chamar` põe
   * `Content-Type: application/json` em tudo que tem corpo, e envio de arquivo
   * precisa da fronteira que o `FormData` gera.
   */
  anexarNaAnotacao: async (id: string, anotacaoId: string, arquivo: File) => {
    const dados = new FormData();
    dados.append('arquivo', arquivo);
    const r = await fetch(`/api/relatorios/${id}/anotacoes/${anotacaoId}/imagens`, {
      method: 'POST',
      body: dados,
      credentials: 'same-origin',
    });
    const corpo = (await r.json().catch(() => ({}))) as {
      blocos?: Bloco[];
      paginas?: number;
      cortou?: boolean;
      teto?: number;
      erro?: string;
    };
    if (!r.ok) throw new ErroDaApi(corpo.erro ?? 'Não consegui anexar o arquivo.', r.status);
    return corpo as { blocos: Bloco[]; paginas?: number; cortou?: boolean; teto?: number };
  },

  /** O endereço da imagem, para a tela mostrar. Passa pela sessão. */
  imagem: (imagemId: string) => `/api/relatorios/imagens/${imagemId}`,

  /**
   * ⭐⭐ **As saídas do relatório — os endereços, 09/09/2026.**
   *
   * ⛔ **Elas existiam no servidor e nenhuma tela as alcançava desde
   * 20/08/2026**, quando o editor foi esvaziado seção por seção: *"saíram os
   * botões de saída"*. É a terceira vez que este projeto encontra a mesma
   * armadilha — construído, ligado, sem tela. Ver `ContratosSociais` (08/09) e
   * o organograma (25/08).
   *
   * ⚠️ **Word continua de pé e sem botão, e é decisão dele de 07/09:** *"o
   * relatório é PDF, a apresentação é PowerPoint"*. A rota não foi apagada —
   * saída que funciona não se remove sem ele mandar.
   */
  saidas: {
    pdf: (id: string) => `/api/relatorios/${id}/documento.pdf`,
    pptx: (id: string) => `/api/relatorios/${id}/apresentacao.pptx`,
    docx: (id: string) => `/api/relatorios/${id}/documento.docx`,
  },

  /** ⚠️ Só o master — apagar é destruir trabalho. */
  apagarAnotacao: (id: string, anotacaoId: string) =>
    chamar<void>(`/api/relatorios/${id}/anotacoes/${anotacaoId}`, { method: 'DELETE' }),

  /**
   * O que a máquina PROPÕE para o capítulo 1 — sem gravar nada.
   *
   * ⚠️ **Só leitura.** Quem grava é `escrever`, logo abaixo, pelo botão — a
   * mesma rota que a pessoa usa ao digitar. Máquina propõe, gente aceita.
   */
  abertura: (id: string) => chamar<AberturaDoCaso>(`/api/relatorios/${id}/abertura`),

  /**
   * ⭐ **Devolve os ACHADOS da conferência desde 11/09/2026.**
   *
   * ⛔ **O conferidor rodava só dentro da máquina de prompt**, sobre o texto que
   * voltava da IA. Texto digitado ou colado direto no campo não passava por
   * conferência nenhuma — e foi por aí que base legal e conclusão de fraude
   * entraram no relatório do primeiro caso real.
   *
   * ⚠️ **Avisa, não bloqueia:** o texto grava sempre, e os achados vêm junto.
   */
  escrever: (id: string, secaoId: string, conteudo: string) =>
    chamar<{
      id: string;
      conteudo: string;
      achados: Array<{ gravidade: 'grave' | 'atencao'; aviso: string; trecho: string }>;
    }>(`/api/relatorios/${id}/secoes/${secaoId}`, {
      method: 'PUT',
      body: JSON.stringify({ conteudo }),
    }),

  /**
   * ⛔⛔ **Para os botões que gravam texto pronto — 15/09/2026.** Com o tópico
   * já escrito, o texto vai para o FIM; nada do que está lá é apagado. Só o
   * editor da própria seção usa `escrever`.
   */
  acrescentar: (id: string, secaoId: string, conteudo: string) =>
    chamar<{
      id: string;
      conteudo: string;
      achados: Array<{ gravidade: 'grave' | 'atencao'; aviso: string; trecho: string }>;
    }>(`/api/relatorios/${id}/secoes/${secaoId}`, {
      method: 'PUT',
      body: JSON.stringify({ conteudo, acrescentar: true }),
    }),

  sintese: (id: string, sintese: string | null) =>
    chamar<Relatorio>(`/api/relatorios/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ sintese }),
    }),

  situacao: (id: string, situacao: SituacaoRelatorio, entregueEm?: string | null) =>
    chamar<Relatorio>(`/api/relatorios/${id}/situacao`, {
      method: 'POST',
      body: JSON.stringify({ situacao, entregueEm }),
    }),

  apagar: (id: string) => chamar<void>(`/api/relatorios/${id}`, { method: 'DELETE' }),

  /*
   * ⛔ **NÃO existe `sincronizar` no cliente, e a falta é deliberada —
   * 10/09/2026.**
   *
   * A sincronia da estrutura roda no servidor a cada abertura de relatório
   * (`GET /api/relatorios/:id`), e não passa por tela nenhuma: *"Não quero que
   * tenha botão... Sem botão."* — Richard. Estrutura é definição da casa, não
   * escolha de quem escreve.
   *
   * A rota `POST /api/relatorios/:id/sincronia` continua de pé como saída
   * manual — é o único caminho para `descartarEscritas`, que apaga texto. Se um
   * dia ela voltar ao cliente, é porque alguém decidiu construir a tela que
   * diz, com número, quanto se perde. **Método sem tela é o defeito que este
   * comentário existe para não repetir.**
   */
  /**
   * ⭐ **O PROMPT do Sumário Executivo — 08/09/2026.**
   *
   * > *"Gere o prompt completo e perfeito, onde a máquina pegue todas as
   * > informações que estiver no caso."* — Richard
   *
   * ⚠️ **O Hub não fala com IA nenhuma:** ele monta o texto e devolve. Quem o
   * leva ao chat é uma pessoa.
   */
  /**
   * ⭐⭐ **O prompt do CAPÍTULO 5** — hipóteses jurídicas ou recomendações.
   *
   * ⛔ **`hipoteses` e `recomendacoes` são entrega exclusiva do N3.** Em outro
   * nível vem o aviso no lugar do prompt: gerar seria oferecer trabalho que não
   * foi vendido.
   *
   * ⭐ **`consideracoes` vale nos três** — 10/09/2026. É a conclusão do que foi
   * apurado, e o prompt dela proíbe justamente o que as outras duas fazem:
   * nomear instituto jurídico e recomendar providência.
   */
  promptDaConclusao: (id: string, peca: 'hipoteses' | 'recomendacoes' | 'consideracoes') =>
    /* ⭐ `anexar` — 14/09/2026: o que anexar na conversa junto do prompt (a
       íntegra do processo do cliente, nas recomendações). */
    chamar<{ prompt: string; anexar?: string | null }>(`/api/relatorios/${id}/prompt-da-conclusao/${peca}`),

  /**
   * ⭐ **O prompt do CAPÍTULO 1 — 11/09/2026.**
   *
   * ⚠️ **Vale nos três níveis**, ao contrário do capítulo 5: todo relatório tem
   * uma abertura, e nenhuma das três seções é entrega exclusiva de um nível.
   */
  promptDaAbertura: (id: string, peca: 'objeto' | 'partes') =>
    chamar<{ prompt: string }>(`/api/relatorios/${id}/prompt-da-abertura/${peca}`),

  promptDoSumario: (id: string) =>
    chamar<{ prompt: string }>(`/api/relatorios/${id}/prompt-do-sumario`),

  /** ⭐ 08/10/2026 — o prompt do tópico 2.2: a justificativa do nível escolhido. */
  promptDoIndice: (id: string) =>
    chamar<{ prompt: string }>(`/api/relatorios/${id}/prompt-do-indice`),

  /** ⭐ 16/10/2026 — o prompt do texto da Linha do tempo: os mesmos fatos do desenho (PRD §52). */
  promptDaLinhaDoTempo: (id: string) =>
    chamar<{ prompt: string }>(`/api/relatorios/${id}/prompt-da-linha-do-tempo`),

  /** ⭐ 16/10/2026 (PRD §53): o e-mail de entrega, pronto para copiar. Só do master. */
  emailDeEntrega: (id: string, contatoId?: string | null) =>
    chamar<EmailDeEntrega>(
      `/api/relatorios/${id}/email-de-entrega${contatoId ? `?contato=${encodeURIComponent(contatoId)}` : ''}`,
    ),

  /**
   * ⭐ **Confere o texto que voltou da IA**, antes de gravar.
   *
   * ⚠️ **Avisa, não bloqueia** — quem decide o que vai ao documento é ele.
   */
  conferirSumario: (id: string, texto: string) =>
    chamar<{ achados: Array<{ gravidade: 'grave' | 'atencao'; aviso: string; trecho: string }> }>(
      `/api/relatorios/${id}/conferir-sumario`,
      { method: 'POST', body: JSON.stringify({ texto }) },
    ),

  /**
   * ⭐ **A LINHA DO TEMPO do caso — 08/09/2026.**
   *
   * ⚠️ **Gratuita:** tudo já está no caso, é releitura. Abrir a seção não custa.
   */
  linhaDoTempo: (id: string) =>
    chamar<LinhaDoTempo>(`/api/relatorios/${id}/linha-do-tempo`),

};

/**
 * O que muda quando o relatório adota a estrutura vigente.
 *
 * ⚠️ **A estrutura foi zerada em 20/08/2026** para ser redefinida seção por
 * seção. Enquanto isso, é este plano que deixa o relatório de teste acompanhar
 * o que vai sendo aprovado — sem perder o que já foi escrito.
 */
export type PlanoDeSincronia = {
  /** ⭐ `parte` é o capítulo onde o tópico vai aparecer — sem ele o aviso não se liga
   *  à barra de capítulos, que é onde a falta se nota. */
  acrescentar: Array<{ chave: string; titulo: string; parte: string }>;
  manter: Array<{ chave: string; titulo: string; temTexto: boolean }>;
  removerVazias: Array<{ chave: string; titulo: string }>;
  /** ⚠️ Só saem com `descartarEscritas`. A tela precisa dizer quanto se perde. */
  removerEscritas: Array<{ chave: string; titulo: string; caracteres: number }>;
};

export type ResultadoDaSincronia = {
  acrescentadas: number;
  atualizadas: number;
  removidas: number;
  preservadas: Array<{ titulo: string; caracteres: number }>;
};

// ── Investigados — a ficha base, 16/08/2026 ──────────────────────────────────

/**
 * As quatro classes — decisão do Richard, 18/08/2026 (`docs/PRD.md` §19.12).
 *
 * ⚠️ **Grupo familiar não é só "parente".** Abriga quem tem sangue e quem não
 * tem, desde que o vínculo com o devedor seja o que justifica investigar. E
 * **cônjuge e filhos entram sempre**, mesmo sem suspeita.
 */
export type QualificacaoInvestigado =
  | 'devedor'
  | 'avalista'
  /** A pessoa que é sócia e não assinou como garantidora — 07/09/2026. */
  | 'socio_nao_avalista'
  /** ⚠️ Só EMPRESAS desde 07/09/2026. O sócio saiu para `socio_nao_avalista`. */
  | 'grupo_economico'
  | 'grupo_familiar';

/**
 * Como cada classe se chama na tela e no documento.
 *
 * ⚠️ **Mora AQUI, e não na tela que a usa — 20/08/2026.** Estava dentro de
 * `Investigados.tsx`; quando a tabela da seção do relatório precisou dos mesmos
 * rótulos, copiar teria feito "Familiar" — trocado naquele mesmo dia — divergir
 * no primeiro ajuste seguinte. O rótulo é do dado, e o dado mora aqui.
 *
 * ⚠️ **'Familiar', e não 'Grupo familiar'.** O valor no banco continua
 * `grupo_familiar`: trocar o enum seria migração em quatro lugares — banco,
 * rota, tipo do frontend e tela — para mudar uma palavra que só o olho lê.
 */
export const QUALIFICACOES: Record<QualificacaoInvestigado, string> = {
  devedor: 'Devedor principal',
  avalista: 'Avalista',
  /*
   * ⭐ **Cinco papéis desde 07/09/2026** — o sócio saiu do grupo econômico.
   *
   * > *"Acrescentar campo para sua qualificação dentro da pesquisa: sócio não
   * > avalista, familiar, empresas do grupo."* — Richard
   *
   * ⚠️ **A distinção é jurídica, não cadastral.** Avalista assinou e responde
   * pela dívida; sócio não avalista responde por outro caminho — e chamar os
   * dois de "grupo econômico" no quadro que o cliente lê apagava a diferença
   * que decide a estratégia de execução.
   */
  socio_nao_avalista: 'Sócio não avalista',
  /* ⚠️ O valor no banco continua `grupo_economico`; mudou só o rótulo, como
     `grupo_familiar` em 20/08. */
  grupo_economico: 'Empresas do grupo',
  grupo_familiar: 'Familiar',
};

/** Os graus, quando a classe é grupo familiar. Para no 3º de propósito. */
export type GrauDeParentesco =
  | 'pai' | 'mae' | 'filho'
  | 'avo' | 'neto' | 'irmao'
  | 'bisavo' | 'bisneto' | 'tio' | 'sobrinho'
  | 'conjuge';
/**
 * ⚠️ **Estava TRÊS VERSÕES atrás do banco até 20/08/2026** — faltavam `imovel`,
 * `aeronave` e `embarcacao`, criados em 19/08. É a armadilha que o `RETOMAR.md`
 * já registra: **enum tem quatro lugares** — banco, rota, tipo do frontend e a
 * tela —, e o frontend é o que fica para trás, porque nada quebra quando ele
 * fica: o dado chega, não tem rótulo, e some da lista sem avisar.
 */
export type TipoDoDado =
  | 'telefone'
  | 'email'
  | 'endereco'
  | 'veiculo'
  | 'imovel'
  | 'aeronave'
  | 'embarcacao'
  /** ⚠️ Cadastrar um processo dispara a consulta ao DataJud, no servidor. */
  | 'processo'
  /** Profissão, cargo ou vínculo de emprego. Criado em 20/08/2026. */
  | 'profissao'
  /**
   * Os quatro campos de qualificação pedidos pelo Richard em 21/08/2026.
   *
   * ⚠️ **Dois deles a Assertiva JÁ devolvia**, escondidos dentro de `outro`:
   * situação cadastral e indício de óbito. Sem tipo próprio, o dado existe e
   * não se consegue contar nem agrupar — a mesma razão que criou `imovel` e
   * `profissao`.
   *
   * `estado_civil` e `regime_de_bens` a Assertiva **não** devolve: são de
   * digitar à mão, ou de ler no contrato social.
   */
  | 'estado_civil'
  | 'regime_de_bens'
  | 'situacao_cadastral'
  | 'obito'
  /**
   * Os dois de 21/08/2026, depois de o Richard ver treze participações
   * societárias sob o rótulo "OUTRO".
   *
   * ⚠️ **`historico_profissional` abriga os DOIS subtítulos da fonte** —
   * vínculo empregatício e registro profissional. É a Assertiva que os agrupa
   * sob *"POSSÍVEL HISTÓRICO PROFISSIONAL"*, e o "possível" é dela: a base
   * infere, não apura.
   */
  | 'participacao_societaria'
  | 'historico_profissional'
  /**
   * Os três de 21/08/2026 — o Richard tirou de "Outro" tudo que tinha nome.
   *
   * ⚠️ **`probabilidade_negociacao` guarda um PERCENTUAL de 0 a 100** no
   * `valor`, com a faixa ("Alta", "Média", "Baixa") na `nota`. Não é dinheiro,
   * não é patrimônio, e não entra em soma nenhuma.
   */
  | 'rede_social'
  | 'ppe'
  | 'probabilidade_negociacao'
  /**
   * ⭐ **Os quatro da PESSOA JURÍDICA — 09/09/2026, pedido dele.**
   *
   * > *"Na qualificação das pessoas jurídicas sugere-se seja informado o tipo
   * > de sociedade, a data de constituição, sua situação cadastral, atividade
   * > econômica principal e complementares e endereço."* — Richard
   *
   * ⚠️ **`situacao_cadastral` e `endereco` não ganharam par**: é o mesmo
   * conceito para o respectivo documento, e um par só para PJ obrigaria a tela
   * a saber de qual tipo de pessoa é cada rótulo.
   */
  | 'natureza_juridica'
  | 'constituicao'
  | 'atividade_principal'
  | 'atividade_secundaria'
  | 'outro';

/**
 * O rótulo de cada tipo, na tela e no documento.
 *
 * ⚠️ **Mora aqui, junto do tipo**, pelo mesmo motivo de `QUALIFICACOES`: a
 * seção do relatório e a ficha do caso mostram os mesmos dados, e duas listas
 * divergiriam no primeiro tipo novo.
 */
export const TIPOS_DE_DADO: Record<TipoDoDado, string> = {
  telefone: 'Telefone',
  email: 'E-mail',
  endereco: 'Endereço',
  profissao: 'Profissão ou emprego',
  imovel: 'Imóvel',
  veiculo: 'Veículo',
  aeronave: 'Aeronave',
  embarcacao: 'Embarcação',
  processo: 'Processo',
  estado_civil: 'Estado civil',
  regime_de_bens: 'Regime de bens',
  situacao_cadastral: 'Situação cadastral',
  obito: 'Óbito',
  participacao_societaria: 'Participações societárias',
  historico_profissional: 'Possível histórico profissional',
  ppe: 'Pessoa politicamente exposta',
  rede_social: 'Redes sociais',
  probabilidade_negociacao: 'Índice de probabilidade de negociação',
  natureza_juridica: 'Tipo de sociedade',
  constituicao: 'Constituída em',
  atividade_principal: 'Atividade econômica principal',
  atividade_secundaria: 'Atividades econômicas complementares',
  outro: 'Outro',
};

export type DadoDoInvestigado = {
  id: string;
  tipo: TipoDoDado;
  valor: string;
  nota: string | null;
  /**
   * De onde o dado veio. `null` = digitado por gente.
   *
   * ⚠️ **Aparece na tela de trabalho e em lugar nenhum do documento entregue.**
   * O sigilo do método é sobre o que a KAIRÓS imprime.
   */
  origem: string | null;
  coletadoEm: string | null;
};

export type Investigado = {
  id: string;
  casoId: string;
  tipo: TipoPessoa;
  nome: string;
  documento: string | null;
  documentoFormatado: string | null;
  qualificacao: QualificacaoInvestigado;
  /**
   * Como a pessoa entrou no caso — criada em 21/08/2026.
   *
   * ⚠️ **É o que faz a regra do Richard existir:** a seção ÍNDICE DE
   * ENDIVIDAMENTO investiga só os nomes que o cliente passou. E vale além
   * dela — é a distinção entre o que o cliente já sabia e o que a KAIRÓS
   * acrescentou.
   */
  origemNoCaso: 'cliente' | 'promovido';
  /** Quanto o levantamento de passivo vai custar. Vem do servidor. */
  custoDoPassivo?: number;
  /**
   * De onde a escolha dos órgãos sai — regra do Richard, 21/08/2026.
   *
   * ⚠️ Sem UF, as consultas regionais (TRT, PGE, tribunal estadual) **não
   * saem**: consultar o tribunal errado devolveria "nada consta" verdadeiro
   * sobre o tribunal e falso sobre a pessoa.
   */
  domicilio?: { municipio: string | null; uf: string | null };
  nascimentoEm: string | null;
  nomeMae: string | null;
  nomePai: string | null;
  observacao: string | null;
  /** O grau, quando a classe é grupo familiar. */
  grauParentesco: GrauDeParentesco | null;
  /** De quem. ⚠️ O grau nunca anda sozinho. */
  parenteDeId: string | null;
  /** ⚠️ Veio de derivação ou consulta e ninguém conferiu ainda. */
  parentescoAConfirmar: boolean;
  /** De onde o grau saiu. ⚠️ Uso interno — nunca sai no documento. */
  parentescoOrigem: string | null;
  dados: DadoDoInvestigado[];
};

export type DadosDoInvestigado = {
  tipo: TipoPessoa;
  nome: string;
  /**
   * ⭐ **De onde a pessoa veio — 08/09/2026.** `documental` é quem a KAIRÓS
   * achou lendo um documento, e nenhuma consulta traria: o menor de idade do
   * exemplo dele.
   *
   * ⚠️ **Só na criação, e o servidor recusa `promovido`** — essa origem é
   * escrita por ele quando uma conexão de consulta vira ficha. Deixar a tela
   * declará-la permitiria afirmar "veio de uma consulta" sobre algo que
   * ninguém consultou.
   */
  origemNoCaso?: 'cliente' | 'documental';
  documento?: string | null;
  qualificacao: QualificacaoInvestigado;
  nascimentoEm?: string | null;
  nomeMae?: string | null;
  nomePai?: string | null;
  observacao?: string | null;
  grauParentesco?: GrauDeParentesco | null;
  parenteDeId?: string | null;
  parentescoAConfirmar?: boolean;
};

/** Uma consulta paga já feita sobre este investigado. */
export type ConsultaExterna = {
  id: string;
  funcionalidade: string;
  protocolo: string | null;
  finalidade: string | null;
  reconsulta: boolean;
  /** ⚠️ Vem `null` para o colaborador — o servidor não manda, a tela não esconde. */
  custo: string | null;
  itens: number;
  fonte: string;
  /** ⚠️ Nulo = a consulta foi. Preenchido = não foi, e a tela precisa gritar. */
  falha: string | null;
  criadoEm: string;
};

/**
 * Uma identidade POSSÍVEL do próprio investigado, vinda de busca reversa.
 *
 * ⚠️ **Não confundir com conexão.** Adotar um candidato grava o documento DELE
 * na ficha do investigado. Adotar o candidato errado faz o Hub investigar um
 * estranho — por isso o `detalhe` traz nascimento, filiação e cidade.
 */
export type CandidatoDeIdentidade = {
  id: string;
  nome: string;
  documento: string | null;
  tipo: 'pf' | 'pj';
  detalhe: string | null;
  relacao: string;
};

/** Alguém LIGADO ao investigado — parente, sócio, empresa. */
export type ConexaoEncontrada = {
  id: string;
  nome: string;
  documento: string | null;
  tipo: 'pf' | 'pj';
  tipoRelacao: string;
  relacao: string;
  grauSugerido: GrauDeParentesco | null;
  detalhe: string | null;
  virouInvestigadoId: string | null;
  /**
   * ⭐ **Foi digitado por gente que leu um documento — 09/09/2026.**
   *
   * ⚠️ **Só estes têm botão de apagar.** O que veio de consulta é o registro do
   * que a fonte respondeu, e a KAIRÓS pagou para saber.
   */
  aMao: boolean;
};

/** O vínculo que se acrescenta à mão. */
export type VinculoAMao = {
  nome: string;
  tipo: 'pf' | 'pj';
  documento?: string | null;
  grau?: GrauDeParentesco | null;
  societario: boolean;
  detalhe?: string | null;
};

/** Um sinal de interposição. ⚠️ Sinal, não conclusão. */
export type SinalDeSuspeita = { chave: string; texto: string; peso: 'forte' | 'medio' };

export type SuspeitaDeUm = {
  conexaoId: string;
  nome: string;
  documento: string | null;
  relacao: string;
  sinais: SinalDeSuspeita[];
};

export const apiCasosSuspeitas = {
  /**
   * O que o Hub tem a apresentar sobre possível interposição.
   *
   * ⚠️ **Apresenta; não classifica.** Nenhum sinal chama alguém de laranja —
   * quem conjuga os elementos e conclui é a Karen.
   */
  ler: (casoId: string) =>
    chamar<{ suspeitas: SuspeitaDeUm[]; lacunas: string[] }>(
      `/api/casos/${casoId}/suspeitas`,
    ),
};

/**
 * Uma linha do quadro de ativos por qualificação.
 *
 * ⚠️ **`subtotal` não chega aqui:** o servidor já manda só o que sai impresso
 * (`linhasImpressasDoQuadro`), e é a MESMA lista que entra no Word. Filtrar de
 * novo na tela seria abrir a porta para as duas versões divergirem — o defeito
 * de 19/08/2026, que custou um PDF sem árvores e sem painel.
 */
export type LinhaDoQuadroDeAtivos = {
  /** `grupo` abre a classe; `parte` é uma pessoa; `total` fecha o quadro. */
  especie: 'grupo' | 'parte' | 'total';
  /** Nome da classe, ou nome do investigado com o grau: `FULANO · irmão de X`. */
  rotulo: string;
  /**
   * ⚠️ **É o valor do BEM do investigado, nunca dinheiro da KAIRÓS.** Nulo quer
   * dizer "não há valor documentado" — e não zero, que afirmaria que o
   * patrimônio vale nada.
   */
  valor: string | null;
  /**
   * Quantos lançamentos por categoria de ATIVOS LOCALIZADOS — 25/08/2026.
   *
   * ⚠️ **Substituiu `veiculos`, `imoveis` e `societarias`.** As três contavam
   * pela ficha do investigado, e o patrimônio passou a viver em
   * `AtivoDoInvestigado` — duas fontes fariam o quadro somar um patrimônio e a
   * seção somar outro. E a de participação societária já estava errada em
   * silêncio: apontava para o lugar onde a participação morava até 21/08.
   */
  contagem: Record<CategoriaDeAtivo, number>;
  /**
   * Quantos desta linha **não integram** o patrimônio atual.
   *
   * ⚠️ **Não é coluna: é o que a `ressalva` conta.** Sem ele, três imóveis todos
   * transmitidos apareceriam como "3 imóveis · —", e o travessão seria lido como
   * "não se apurou o valor" quando o certo é "estes bens saíram do patrimônio".
   */
  foraDaSoma: number;
};

/** Uma linha da tabela "Empresas do quadro" (o mesmo que o documento imprime). */
export type EmpresaDoMapa = {
  empresa: string;
  devedora: boolean;
  cnpj: string;
  administracao: string;
  desde: string | null;
  quota: string;
  situacao: string;
  etiquetas: string;
};

/**
 * O que o documento mostra e a tela precisa mostrar junto.
 *
 * ⚠️ **Existe por causa da trava do `docs/PRD.md` §21.** Até 20/08/2026 o quadro
 * e o painel só existiam dentro do Word: o Richard revisava na tela e dois
 * blocos que vão ao cliente não estavam lá.
 */
export const apiCasosAtivos = {
  /**
   * ⚠️ **As colunas e a ressalva vêm do SERVIDOR**, e não de listas escritas
   * aqui — 25/08/2026. A ordem das categorias, o título de cada coluna e a frase
   * que explica o que não soma já existem uma vez, do lado que o `.docx`
   * também lê. Escritas de novo na tela, seriam a terceira cópia a manter em
   * dia, e a que ninguém lembraria de olhar.
   */
  /** ⭐ 27/10/2026: as empresas do mapa em grade (caso com mais de 4); vazia no caso pequeno. */
  empresasDoMapa: (casoId: string) =>
    chamar<{ empresas: EmpresaDoMapa[] }>(`/api/casos/${casoId}/mapa/empresas`),

  quadro: (casoId: string) =>
    chamar<{
      linhas: LinhaDoQuadroDeAtivos[];
      ressalva: string | null;
      colunas: Array<{ chave: CategoriaDeAtivo; titulo: string }>;
    }>(`/api/casos/${casoId}/quadro-de-ativos`),

  /**
   * ⭐ O ANEXO de atos notariais — a relação inteira, como o documento a imprime.
   *
   * ⚠️ **A frase de abertura vem pronta do servidor**, da mesma função que o
   * gerador do `.docx` usa: é ela que diz o alcance da relação, e escrita em
   * dois lugares seria a que um dia diverge.
   */
  anexoNotarial: (casoId: string) =>
    chamar<{
      anexos: Array<{
        investigado: string;
        pesquisadaEm: string;
        escrituras: number;
        procuracoes: number;
        extraidos: number;
        abertura: string;
        linhas: Array<{
          ocorridoEm: string;
          cartorio: string;
          municipio: string;
          uf: string;
          especie: 'escritura' | 'procuracao';
          livro: string;
          folha: string;
          extraido: boolean;
        }>;
      }>;
    }>(`/api/casos/${casoId}/anexo-notarial`),
};

/**
 * Quem tem árvore genealógica no caso.
 *
 * ⚠️ **Vem só a lista; o desenho vem por `<img>`**, em
 * `/api/investigados/:id/arvore.svg`. SVG dentro de JSON obrigaria a tela a
 * injetá-lo como HTML — e `<img>` não executa o que vier dentro do arquivo.
 *
 * ⚠️ **Quem tem árvore é decidido no servidor**, e só lá: se a tela filtrasse
 * por conta própria, mostraria três árvores enquanto o documento imprime
 * quatro.
 */
/* ── Documentos do caso e a leitura do contrato social · 20/08/2026 ──────── */

export type EspecieDeDocumento = 'contrato_social' | 'junta_comercial' | 'outro';

export type DocumentoDoCaso = {
  id: string;
  nome: string;
  especie: EspecieDeDocumento;
  tamanho: number;
  /**
   * `false` quando nem o reconhecimento de imagem achou texto.
   *
   * ⚠️ **O comentário antigo dizia "quando o PDF é escaneado", e deixou de ser
   * verdade em 08/09/2026** — desde então o Hub lê escaneado por reconhecimento
   * óptico. Escaneado agora é `legivel: true` com `origemDoTexto: 'imagem'`.
   */
  legivel: boolean;
  falha: string | null;
  /**
   * ⭐ **Como o texto foi obtido — 09/09/2026.**
   *
   * `'texto'` = embutido, o que o sistema da junta gravou · `'imagem'` =
   * reconhecimento óptico · `null` = documento anterior a esta coluna, e nulo é
   * "não se sabe".
   *
   * ⚠️ **A tela DIZ quando é imagem.** No reconhecimento, CPF e CNPJ têm o
   * dígito verificador como rede; **nome, data e valor não têm** — e foi
   * exatamente daí que saiu um "50/06/2020" no caso real.
   */
  origemDoTexto: 'texto' | 'imagem' | 'texto-de-scanner' | null;
  /**
   * ⭐ **`'texto-de-scanner'` entrou em 10/09/2026**, e é o caso que faltava: o
   * PDF TEM texto, mas quem o escreveu foi o aparelho que digitalizou. É o que
   * produzia *"solidariamethOpelaﬁntegralizagão"* no relatório.
   */
  /** Está sendo relido agora. */
  relendo: boolean;
  /** O que dizer sobre a releitura: "lendo 3 de 13", ou o resultado da última. */
  releituraEstado: string | null;
  criadoEm: string;
  criadoPor: string;
};

export type SocioDoContrato = {
  nome: string;
  documento: string | null;
  quotas: number | null;
  valor: string | null;
  /** Se já está na ficha do caso, ou se apareceu só no contrato. */
  papel: 'investigado' | 'fora-da-ficha';
  /**
   * ⚠️ **Como o Hub reconheceu.** `nome` é mais fraco que `documento` — homônimo
   * é regra, não exceção, e a tela precisa deixar isso à vista.
   */
  reconhecidoPor: 'documento' | 'nome' | null;
};

export type PassoSocietario = {
  data: string | null;
  titulo: string | null;
  empresa: { nome: string | null; cnpj: string | null };
  /**
   * ⭐ **De QUAL empresa este ato é — 09/09/2026.** A linha do tempo misturava
   * empresas diferentes e afirmava saída de sócio que nunca aconteceu. Ver
   * `analise-societaria.ts`.
   */
  empresaChave: string;
  capital: { valor: string | null; quotas: number | null } | null;
  endereco: string | null;
  socios: SocioDoContrato[];
  movimentos: Array<{ de: string; para: string; quotas: number | null; trecho: string }>;
  mudanca: {
    entraram: SocioDoContrato[];
    sairam: SocioDoContrato[];
    mudaramQuotas: Array<{ socio: SocioDoContrato; de: number | null; para: number | null }>;
    enderecoAnterior: string | null;
  };
};

export type AnaliseSocietaria = {
  passos: PassoSocietario[];
  historicoDeEnderecos: Array<{ data: string | null; endereco: string; empresaChave: string }>;
  foraDaFicha: SocioDoContrato[];
  /** ⚠️ Sinal, não conclusão. E não vai ao relatório. */
  sinais: Array<{ chave: string; texto: string; peso: 'forte' | 'medio' }>;
  lacunas: string[];
  /** O que faltou para avaliar os SINAIS. Fica na tela, como eles — não vai ao documento. */
  lacunasDosSinais: string[];
  documentos: { lidos: number; comFalha: number };
  /** O que seria escrito na seção "Estrutura societária". */
  texto: string;
  gravado: boolean;
  /** ⛔ A seção já tinha texto, e o Hub não o substitui (15/09/2026). */
  jaTemTexto?: boolean;
  /** ⭐ As empresas que são parte: a evolução delas vai à qualificação de cada uma (15/09/2026). */
  naQualificacao?: Array<{ investigadoId: string; nome: string }>;
};

/** ⭐ A evolução do quadro de uma empresa que é parte, para o cartão dela — 15/09/2026. */
export type EvolucaoDaEmpresa = {
  investigadoId: string;
  atos: number;
  /** O texto como vai ao campo, para conferir antes de levar. */
  texto: string;
};

/** O título do campo que o botão cria — e por onde a tela o reconhece. */
export const TITULO_DA_EVOLUCAO = 'Evolução do quadro societário';

export const apiDocumentos = {
  listar: (casoId: string) =>
    chamar<{ documentos: DocumentoDoCaso[] }>(`/api/casos/${casoId}/documentos`),

  /**
   * ⚠️ **Não passa por `chamar`, e é a única do arquivo que não passa.**
   * `chamar` põe `Content-Type: application/json` em tudo que tem corpo; num
   * envio de arquivo o cabeçalho tem de vir do próprio `FormData`, com a
   * fronteira que o navegador gera. Escrevê-lo à mão quebra o envio.
   */
  subir: async (casoId: string, arquivo: File, especie: EspecieDeDocumento) => {
    const corpo = new FormData();
    corpo.append('especie', especie);
    corpo.append('arquivo', arquivo);

    const resposta = await fetch(`/api/casos/${casoId}/documentos`, {
      method: 'POST',
      credentials: 'same-origin',
      body: corpo,
    });
    const texto = await resposta.text();
    const dados: unknown = texto ? JSON.parse(texto) : null;
    if (!resposta.ok) {
      const mensagem =
        dados && typeof dados === 'object' && 'erro' in dados
          ? String((dados as { erro: unknown }).erro)
          : 'Não consegui enviar o arquivo.';
      throw new ErroDaApi(mensagem, resposta.status);
    }
    return dados as DocumentoDoCaso;
  },

  apagar: (id: string) => chamar<void>(`/api/documentos/${id}`, { method: 'DELETE' }),

  /**
   * A leitura dos contratos do caso.
   *
   * ⚠️ **`gravar: false` não escreve em lugar nenhum.** A análise aparece na
   * tela e só vai para a seção quando alguém mandar — máquina que reescreve o
   * que a pessoa redigiu apaga trabalho sem avisar.
   */
  /**
   * ⭐⭐ **O prompt que resume um contrato em prosa — 09/09/2026.**
   *
   * ⚠️ **Só para prosa.** A ficha cadastral da junta tem leitor determinístico
   * (`leitura-da-ficha-da-junta.ts`) e não passa por aqui: formulário se lê com
   * régua, e a régua não erra dígito nem leva o documento para fora.
   */
  /**
   * ⭐ **Relê as páginas de um PDF escaneado, aqui dentro — 10/09/2026.**
   *
   * ⚠️ **Responde na hora e trabalha depois** — ~2 s por página. A tela
   * acompanha pelo progresso da listagem.
   */
  reler: (documentoId: string) =>
    chamar<{ ok: boolean }>(`/api/documentos/${documentoId}/reler`, { method: 'POST' }),

  promptDeResumo: (documentoId: string) =>
    chamar<{ prompt: string; cortou: boolean; entrou: number }>(
      `/api/documentos/${documentoId}/prompt-de-resumo`,
    ),

  /** ⭐ Confere a resposta contra o PRÓPRIO documento — a conferência mais forte do Hub. */
  conferirResumo: (documentoId: string, texto: string) =>
    chamar<{ achados: Array<{ gravidade: 'grave' | 'atencao'; aviso: string; trecho: string }> }>(
      `/api/documentos/${documentoId}/conferir-resumo`,
      { method: 'POST', body: JSON.stringify({ texto }) },
    ),

  analisar: (casoId: string, gravar: boolean) =>
    chamar<AnaliseSocietaria>(`/api/casos/${casoId}/documentos/analisar`, {
      method: 'POST',
      body: JSON.stringify({ gravar }),
    }),
};

export const apiCasosArvores = {
  listar: (casoId: string) =>
    chamar<{ arvores: Array<{ investigadoId: string; nome: string }> }>(
      `/api/casos/${casoId}/arvores`,
    ),
};

/**
 * As quatro categorias de passivo, na ordem em que saem na tela.
 *
 * ⚠️ **A ordem é argumento, como a das classes de investigado:** primeiro o que
 * já virou execução e disputa o patrimônio agora — protesto e dívida ativa —,
 * depois o que ainda corre. Alfabética poria "ação judicial" antes de tudo.
 */
export type CategoriaDePassivo = 'protesto' | 'divida_ativa' | 'acao_judicial' | 'trabalhista';

export const CATEGORIAS_DE_PASSIVO: Record<CategoriaDePassivo, string> = {
  protesto: 'Protestos',
  divida_ativa: 'Dívida ativa',
  acao_judicial: 'Ações judiciais',
  trabalhista: 'Débitos trabalhistas',
};

/**
 * O que cada categoria abriga — sai como ajuda, ao lado do título do grupo.
 *
 * ⚠️ **A do trabalhista existe para evitar um erro caro.** A CNDT acusa débito
 * em **execução definitiva** no BNDT; reclamatória em curso não aparece. Sem
 * esse aviso, "nada consta" é lido como "não há passivo trabalhista".
 */
export const AJUDA_DA_CATEGORIA: Record<CategoriaDePassivo, string> = {
  protesto: 'títulos protestados em cartório',
  divida_ativa: 'inscrições federais, estaduais e municipais',
  acao_judicial: 'processos em que a parte figura',
  trabalhista: 'o que a CNDT acusa — execução definitiva, não reclamatória em curso',
};

export type PassivoDoInvestigado = {
  id: string;
  categoria: CategoriaDePassivo;
  descricao: string;
  /** ⚠️ Nulo NÃO é zero: quer dizer "não há valor documentado". */
  valor: number | null;
  /** A data do FATO — do protesto, da inscrição, da distribuição. */
  ocorridoEm: string | null;
  referencia: string | null;
  situacao: string | null;
  /** `null` = lançado à mão. ⚠️ Nunca sai impressa no documento. */
  origem: string | null;
  coletadoEm: string | null;
};

/**
 * As classificações do ativo — espelho do enum `ClassificacaoDoAtivo` do Prisma.
 *
 * ⛔⛔ **ESTA LISTA É ESCRITA À MÃO, E JÁ DIVERGIU — 10/09/2026.**
 *
 * Nasceu com as nove da especificação de 24/08. Quando
 * `veiculo_situacao_nao_apurada` entrou no banco, **ninguém a acrescentou
 * aqui** — e como o `ROTULO` é `Record<ClassificacaoDoAtivo, string>`, o
 * compilador acreditou que aquele valor não existia e parou de cobrar o rótulo.
 * Resultado no caso real: os dois veículos daquela classificação apareciam com
 * o **selo em branco** na tela, e o selo é justamente o que diz que o bem não
 * entra no patrimônio.
 *
 * ⚠️ **O `Record` exaustivo só protege depois desta linha.** Classificação nova
 * no `schema.prisma` **precisa** de linha nova aqui — é o único ponto do
 * caminho em que o compilador não consegue avisar sozinho.
 */
/**
 * ⭐ **De onde veio a avaliação — 10/09/2026.** Espelho do enum
 * `ModalidadeDaAvaliacao` do Prisma. ⚠️ Escrita à mão como a lista abaixo, e
 * com o mesmo aviso: valor novo no `schema.prisma` precisa de linha nova aqui.
 */
export type ModalidadeDaAvaliacao =
  | 'judicial'
  | 'laudo'
  | 'declarada'
  | 'tabela_de_referencia'
  | 'comparativo_de_mercado'
  | 'preco_por_metro'
  | 'valor_venal'
  | 'valor_da_terra_nua'
  | 'avaliacao_mercadologica'
  | 'a_apurar'
  /** ⭐ 18/09/2026: quantidade × cotação, só na ação — quem escreve é o SERVIDOR. */
  | 'cotacao_em_bolsa';

/**
 * ⭐ **O lançamento PROPOSTO por uma matrícula lida no chat — 10/09/2026.**
 * ⚠️ Nada aqui foi gravado: são valores de partida para o formulário, e
 * `ressalvas` é tudo que o Hub não entendeu ou recusou.
 */
/**
 * ⭐⭐ **Uma avaliação do bem — várias por bem, decisão dele, 15/09/2026.** A que
 * `entraNaConta` é espelhada pelo servidor em `avaliacao` e
 * `modalidadeDaAvaliacao`, que é o que as somas leem.
 */
export type AvaliacaoDoAtivo = {
  /** Ausente na linha nova, ainda não gravada. */
  id?: string;
  /** ⚠️ Nula só na avaliação trazida de antes, que não dizia de onde veio. */
  modalidade: ModalidadeDaAvaliacao | null;
  valor: number;
  referencia: string | null;
  avaliadaEm: string | null;
  entraNaConta: boolean;
};

export type MatriculaProposta = {
  classificacao: ClassificacaoDoAtivo | null;
  descricao: string | null;
  identificacao: string | null;
  tipoDoImovel: string | null;
  cidade: string | null;
  uf: string | null;
  areaPrivativa: number | null;
  areaDoTerreno: number | null;
  areaConstruida: number | null;
  modalidadeDeAquisicao: string | null;
  /** ⭐ 16/09/2026 — o último ato do imóvel, como a leitura o achou. */
  numeroDaMatricula?: string | null;
  cartorio?: string | null;
  qualidadeNoAto?: string | null;
  outraParte?: string | null;
  ocorridoEm: string | null;
  certidaoEm: string | null;
  valorDeclarado: number | null;
  percentual: number | null;
  avaliacao: number | null;
  modalidadeDaAvaliacao: ModalidadeDaAvaliacao | null;
  transmitente: string | null;
  adquirente: string | null;
  vinculoEntrePartes: string | null;
  proprietarioAtual: string | null;
  matriculaSucessora: string | null;
  motivoEncerramento: string | null;
  elementoDeConexao: string | null;
  onus: Array<{
    natureza: string;
    credor: string | null;
    valor: number | null;
    constituidoEm: string | null;
    situacao: string | null;
    /** ⭐ R4: de onde o gravame saiu (só na leitura do PDF). */
    origem?: OrigemDoCampo;
  }>;
  ressalvas: string[];
  /** ⭐ As avaliações que a matrícula traz — 15/09/2026. */
  avaliacoes?: AvaliacaoDoAtivo[];
  /** ⭐ R4: de onde veio cada campo preenchido (só na leitura do PDF; não é gravado). */
  origens?: Partial<Record<CampoLido, OrigemDoCampo>>;
  /** ⭐ R4: o parágrafo do imóvel como o papel o escreve, para consulta ao lado do endereço. */
  descricaoDoPapel?: string | null;
  /** ⭐ R4: o ato de onde saíram data, título, qualidade e valor ("R.8"). */
  atoDoUltimo?: string | null;
  /** ⭐ R4: as outras partes do último ato, com o papel no ato. */
  partes?: Array<{ nome: string; documento: string | null; papel: string | null }>;
  /** ⭐ Ajustes da R4: o tabelionato da escritura do último ato, com o livro e as folhas. */
  tabelionato?: string | null;
  /** ⭐ Ajustes da R4: a situação patrimonial do último ato, quando o tipo do ato e o papel a dizem. */
  efeitoPatrimonial?: EfeitoPatrimonialDoAto | null;
};

/**
 * ⭐⭐ **De onde veio cada campo lido — R4 da reforma das leituras, 27/10/2026 (PRD §67.5).** A página, o ato e o
 * trecho do papel; e o motivo de conferir, quando o Hub tem dúvida. Vive só na proposta, no navegador.
 */
export type OrigemDoCampo = {
  pagina: number | null;
  ato: string | null;
  trecho: string;
  conferir: string | null;
  /** ⭐ §77: quem leu este valor (os dois concordaram, ou só um leu). Só na tela, nunca gravado. */
  quem?: 'os-dois' | 'gpt' | 'hub';
  /** ⭐ §77: quando o Hub e o GPT divergiram, a outra leitura, para trocar com um clique. */
  outra?: { valor: string | number; de: 'gpt' | 'hub'; naTela: string };
};

export type CampoLido =
  | 'classificacao'
  | 'descricao'
  | 'tipoDoImovel'
  | 'cidade'
  | 'uf'
  | 'numeroDaMatricula'
  | 'cartorio'
  | 'areaPrivativa'
  | 'areaConstruida'
  | 'areaDoTerreno'
  | 'ocorridoEm'
  | 'modalidadeDeAquisicao'
  | 'qualidadeNoAto'
  | 'valorDeclarado'
  | 'avaliacao'
  | 'vinculoEntrePartes'
  | 'partes'
  | 'proprietarioAtual'
  | 'tabelionato'
  | 'efeitoPatrimonial'
  | 'certidaoEm'
  | 'matriculaSucessora'
  | 'motivoEncerramento';

/**
 * ⭐ **O papel do investigado no ato notarial — 10/09/2026.** Espelho do enum
 * do Prisma. ⚠️ Escrito à mão, com o mesmo aviso da lista de classificações.
 */
export type PapelNoAtoNotarial =
  | 'comprador'
  | 'vendedor'
  | 'doador'
  | 'donatario'
  | 'cedente'
  | 'cessionario'
  | 'permutante'
  | 'herdeiro'
  | 'meeiro'
  | 'devedor'
  | 'credor'
  | 'garantidor'
  | 'outorgante'
  | 'procurador'
  | 'beneficiario';

/** ⭐ O papel de uma parte no ato do imóvel — 16/10/2026; a lista dele desde 27/10/2026 (PRD §65). Espelho do `schema.prisma`. */
export type PapelDaParteNoAto =
  | 'vendedor'
  | 'comprador'
  | 'doador'
  | 'donatario'
  | 'cedente'
  | 'cessionario'
  | 'credor_fiduciario'
  | 'devedor_fiduciante'
  | 'credor_hipotecario'
  | 'arrematante'
  | 'outro'
  | 'promitente_vendedor'
  | 'promitente_comprador'
  | 'alienante'
  | 'adquirente'
  | 'permutante'
  | 'interveniente_anuente'
  | 'interveniente_quitante'
  | 'garantidor'
  | 'avalista'
  | 'fiador'
  | 'devedor_hipotecario'
  | 'garantidor_hipotecario'
  | 'fiduciante'
  | 'fiduciario'
  | 'terceiro_garantidor'
  | 'credor_pignoraticio'
  | 'proprietario'
  | 'coproprietario'
  | 'nu_proprietario'
  | 'usufrutuario'
  | 'instituidor_de_usufruto'
  | 'titular_de_direito_real_de_habitacao'
  | 'concedente_de_direito_de_superficie'
  | 'superficiario'
  | 'titular_de_servidao'
  | 'falecido'
  | 'herdeiro'
  | 'legatario'
  | 'inventariante'
  | 'meeiro'
  | 'espolio'
  | 'renunciante_de_heranca'
  | 'cedente_de_direitos_hereditarios'
  | 'cessionario_de_direitos_hereditarios'
  | 'outorgante'
  | 'outorgado'
  | 'procurador'
  | 'substabelecente'
  | 'substabelecido'
  | 'representante_legal'
  | 'representado'
  | 'administrador_judicial'
  | 'conjuge'
  | 'companheiro'
  | 'anuente_conjugal'
  | 'socio'
  | 'socio_retirante'
  | 'acionista'
  | 'administrador'
  | 'cedente_de_quotas'
  | 'cessionario_de_quotas'
  | 'incorporadora'
  | 'incorporada'
  | 'empresa_cindida'
  | 'sociedade_sucessora'
  | 'exequente'
  | 'executado'
  | 'penhorante'
  | 'depositario_judicial'
  | 'adjudicante'
  | 'requerente'
  | 'requerido'
  | 'locador'
  | 'locatario'
  | 'arrendador'
  | 'arrendatario'
  | 'comodante'
  | 'comodatario'
  | 'distratante'
  | 'beneficiario';

/** ⭐ Uma parte do último ato do imóvel. ⛔ Não é investigado. */
export type ParteDoAto = { id?: string; nome: string; documento: string | null; papel: PapelDaParteNoAto | null };
/** ⭐ Um coproprietário do mesmo imóvel. A fração não muda a do investigado. */
export type CoproprietarioDoAtivo = { id?: string; nome: string; documento: string | null; fracao: number | null };

/** ⭐ O que o ato fez com o patrimônio; a lista dele desde 27/10/2026 (PRD §65). Espelho do `schema.prisma`. */
export type EfeitoPatrimonialDoAto =
  | 'aquisicao'
  | 'transmissao'
  | 'partilha_sucessao'
  | 'constituicao_de_onus'
  | 'cessao_de_direitos'
  | 'operacao_financeira'
  | 'sem_efeito_identificado'
  | 'compra_ou_incorporacao'
  | 'venda_doacao_ou_transferencia'
  | 'heranca'
  | 'inventario'
  | 'divorcio_ou_partilha'
  | 'hipoteca_alienacao_fiduciaria_ou_penhor'
  | 'liberacao_de_garantia_ou_restricao'
  | 'transferencia_de_direitos'
  | 'emprestimo_ou_confissao_de_divida'
  | 'aporte_em_sociedade'
  | 'restituicao_aos_socios'
  | 'distribuicao_aos_socios'
  | 'incorporacao_societaria'
  | 'fusao'
  | 'cisao_ou_transformacao'
  | 'quotas_ou_acoes'
  | 'penhora'
  | 'arresto_sequestro_ou_indisponibilidade'
  | 'cancelamento_de_penhora'
  | 'troca_de_bens'
  | 'usufruto'
  | 'superficie_servidao_ou_outros'
  | 'cancelamento_de_direito'
  | 'retificacao'
  | 'encerramento_de_matricula'
  | 'sem_repercussao'
  | 'informacoes_insuficientes';

export type ClassificacaoDoAtivo =
  | 'imovel_livre'
  /* ⭐ 16/10/2026 (PRD §51): "Livre, empresa do grupo". Uma opção na tela; o
     servidor escolhe entre os dois valores pela titular. */
  | 'imovel_livre_empresa_do_grupo'
  | 'imovel_onerado'
  | 'imovel_transmitido'
  | 'imovel_matricula_encerrada'
  | 'imovel_de_terceiro'
  | 'imovel_de_empresa_do_grupo'
  | 'veiculo_atual'
  | 'veiculo_historico'
  | 'veiculo_baixado'
  | 'veiculo_situacao_nao_apurada'
  | 'escritura'
  | 'procuracao'
  | 'processo_relevante'
  /* ⭐ OUTROS ATIVOS — 18/09/2026 (PRD §45). Espelho do `schema.prisma`. */
  | 'acao_em_nome'
  | 'acao_onerada'
  | 'acao_com_constricao'
  | 'acao_deixou_de_constar'
  | 'marca_em_vigor'
  | 'marca_pedido'
  | 'marca_com_onus'
  | 'marca_transferida'
  | 'marca_extinta'
  | 'patente_em_vigor'
  | 'patente_pedido'
  | 'patente_com_onus'
  | 'patente_transferida'
  | 'patente_extinta'
  | 'mineral_sem_onus_averbado'
  | 'mineral_onerado'
  | 'mineral_cedido'
  | 'mineral_extinto'
  /* ⭐ 28/09/2026 — participação em sociedade constituída no exterior. */
  | 'exterior_vigente'
  | 'exterior_transferida'
  | 'exterior_encerrada'
  /* ⭐ 07/10/2026 — direito creditório (pedido da Karen). */
  | 'credito_livre'
  | 'credito_onerado'
  | 'credito_cedido'
  | 'credito_extinto';

/** ⭐ Qual "outro ativo" é — 18/09/2026. Vem do servidor (`OUTRO_ATIVO`). */
export type EspecieDeOutroAtivo = 'acao' | 'marca' | 'patente' | 'direito_minerario' | 'direito_creditorio' | 'participacao_exterior';

/**
 * A espécie do bem móvel — decisão do Richard, 25/08/2026.
 *
 * ⚠️ **Eixo diferente da classificação.** A classificação diz a SITUAÇÃO do bem
 * diante do investigado; a espécie diz **que bem é**.
 */
export type EspecieDeVeiculo = 'automovel' | 'aeronave' | 'embarcacao';

/**
 * O índice de COBERTURA — quanto do crédito do cliente os ativos alcançam.
 *
 * ⭐ **Substitui o "índice de probabilidade de negociação"**, que a Assertiva
 * confirmou em 25/08/2026 não entregar por API. ⚠️ **A diferença não é de
 * fornecedor: é de pergunta.** O do bureau responde *"esta pessoa negocia
 * dívida?"* — pergunta de cobrança. Este responde *"o que se localizou alcança o
 * crédito do cliente?"*, que é o trabalho contratado. E é **auditável**.
 */
export type CoberturaDoCredito = {
  credito: number | null;
  livre: number;
  onerado: number;
  /** ⚠️ Não se subtrai do ativo: preferência entre credores é matéria jurídica. */
  passivo: number;
  coberturaLivre: number | null;
  coberturaTotal: number | null;
  /**
   * ⭐⭐ **As ações cotadas em bolsa — linha PRÓPRIA, decisão dele, 18/09/2026.**
   * ⛔ Não entra no livre nem no "com o onerado": o ônus da ação não é público.
   */
  acoes: number;
  coberturaAcoes: number | null;
  /** Quantas ações o caso tem — a linha só aparece quando há alguma. */
  acoesQuantas: number;
  /** ⭐ Marca, patente, mineração: são dele e ficam fora da conta por REGRA. */
  foraPorRegraQuantos: number;
  /**
   * ⛔ **O que TEM valor e NÃO soma** — 08/09/2026. Imóvel transmitido,
   * matrícula encerrada, veículo baixado, escritura, procuração e crédito em
   * processo. ⚠️ **Não é lacuna, é regra:** cada um deles tem avaliação e não
   * integra o patrimônio atual, por decisão dele.
   */
  foraDaSoma: number;
  /** Quantos lançamentos formam esse valor. */
  foraQuantos: number;
  ativosSemValor: number;
  passivosSemValor: number;
  /** O que impede o índice de existir, quando impede. */
  lacuna: string | null;
};

export type CategoriaDeAtivo =
  | 'imoveis'
  | 'veiculos'
  /* ⭐ 18/09/2026: ações, marcas, patentes e direitos minerários. */
  | 'outros'
  | 'atos'
  | 'processos';

export type OnusDoAtivo = {
  id: string;
  natureza: string;
  credor: string | null;
  /** ⭐ 27/10/2026 (PRD §65): o processo do gravame, escrito à mão. */
  processo: string | null;
  /** ⚠️ Nulo é "não quantificado na matrícula", nunca zero. */
  valor: number | null;
  constituidoEm: string | null;
  situacao: string | null;
};

export type AtivoDoInvestigado = {
  id: string;
  /** ⭐ As várias avaliações do bem — 15/09/2026. */
  avaliacoes: AvaliacaoDoAtivo[];
  classificacao: ClassificacaoDoAtivo;
  /** ⚠️ Nula fora de "veículos" — e nula não é "automóvel". */
  especie: EspecieDeVeiculo | null;
  /** ⚠️ **Vem do servidor, derivada da classificação** — a tela não deduz. */
  categoria: CategoriaDeAtivo;
  /**
   * ⚠️ **Se este lançamento entra no patrimônio atual.** Vem calculado do
   * servidor: transmitido, matrícula encerrada, veículo histórico, veículo
   * baixado, escritura e procuração **não entram**. Recalcular aqui criaria uma
   * segunda aritmética sobre o mesmo dado.
   */
  entraNaConsolidacao: boolean;
  /** ⭐ 18/09/2026. Qual outro ativo é — nulo fora de "Outros ativos". */
  outro: EspecieDeOutroAtivo | null;
  /**
   * ⭐ **É dele e fica fora da conta por REGRA** — marca, patente, mineração.
   * ⚠️ Não é "saiu do patrimônio", e a tela não pode dizer que é.
   */
  foraDaContaPorRegra: boolean;
  /* ⭐ A posição em ações — 18/09/2026. Nulas fora da ação. */
  quantidade: number | null;
  cotacao: number | null;
  cotacaoEm: string | null;
  percentualDoCapital: number | null;
  descricao: string;
  identificacao: string | null;
  /** ⚠️ Nulo NÃO é zero: "não há avaliação apurada". */
  avaliacao: number | null;
  /** ⚠️ Nulo NÃO é 100%: "participação não apurada". */
  percentual: number | null;
  /** ⚠️ **Calculado no servidor**: avaliação × percentual. Nunca guardado. */
  valorAtribuivel: number | null;
  /** ⚠️ Apresentado SEPARADO da avaliação, sempre — regra do Richard. */
  valorDeclarado: number | null;
  ocorridoEm: string | null;
  transmitente: string | null;
  adquirente: string | null;
  vinculoEntrePartes: string | null;
  proprietarioAtual: string | null;
  /**
   * ⭐ **O fato objetivo que justifica olhar um bem de terceiro — 10/09/2026.**
   * Obrigatório em `imovel_de_terceiro`, e o servidor recusa sem ele.
   */
  elementoDeConexao: string | null;
  tipoDoImovel: string | null;
  cidade: string | null;
  uf: string | null;
  /** Em m². ⚠️ Nula não é zero: nula quer dizer que a matrícula não informou. */
  areaPrivativa: number | null;
  areaDoTerreno: number | null;
  areaConstruida: number | null;
  modalidadeDeAquisicao: string | null;
  /**
   * ⭐ **O último ato do imóvel — 16/09/2026.** `modalidadeDeAquisicao` é a
   * escritura/título; estes completam a linha da tabela do relatório.
   */
  numeroDaMatricula: string | null;
  cartorio: string | null;
  qualidadeNoAto: string | null;
  /** ⚠️ No imóvel, desde 16/10/2026, é o espelho dos nomes de `partes`, escrito pelo servidor. */
  outraParte: string | null;
  /** ⭐ 16/10/2026: as partes do último ato, na ordem escrita, e os coproprietários. */
  partes: ParteDoAto[];
  coproprietarios: CoproprietarioDoAtivo[];
  /** ⭐ 16/10/2026: o CNPJ da empresa titular, na "Livre, empresa do grupo". */
  documentoDoTitular: string | null;
  /** ⚠️ As frações e a participação passam de 100%: aviso do servidor, nunca recusa. */
  avisoDasFracoes: string | null;
  /** ⚠️ A empresa titular passou a ser parte do caso depois do lançamento. Só na lista. */
  titularEhParteDoCaso?: boolean;
  /** "Valor global não individualizado": o valor, se houver, é da operação inteira. */
  valorGlobal: boolean;
  modalidadeDaAvaliacao: ModalidadeDaAvaliacao | null;
  /** ⭐ De quando é o retrato registral. ⚠️ Nula é lacuna, não "está em dia". */
  certidaoEm: string | null;
  tabelionato: string | null;
  papelNoAto: PapelNoAtoNotarial | null;
  efeitoPatrimonial: EfeitoPatrimonialDoAto | null;
  poderes: string | null;
  prazoDeValidade: string | null;
  /** ⚠️ Três estados: sim, não, e nulo — "o documento não diz". */
  podeSubstabelecer: boolean | null;
  matriculaSucessora: string | null;
  motivoEncerramento: string | null;
  situacao: string | null;
  /** ⭐ A nossa análise do processo — 14/09/2026. Só em `processo_relevante`. */
  analise: string | null;
  /** O texto que a máquina formulou. ⚠️ Ver o aceite, abaixo. */
  interpretacao: string | null;
  forcaDoIndicio: string | null;
  interpretadoEm: string | null;
  /**
   * ⭐ **Sem aceite, a interpretação da máquina NÃO vai ao documento
   * entregue.** É a garantia que a decisão do Richard exige de si mesma:
   * *"eu revejo antes de entregar"*. Revisão prometida não é revisão garantida.
   */
  aceita: boolean;
  aceitaEm: string | null;
  /** `null` = lançado à mão. ⚠️ Nunca sai impressa no documento. */
  origem: string | null;
  coletadoEm: string | null;
  onus: OnusDoAtivo[];
};

/** ⭐ Os gravames e as faltas de valor de UM tópico — 18/09/2026. */
export type GravamesDoTopico = {
  exposicao: number;
  exposicaoQuantos: number;
  exposicaoSemValor: number;
  gravamesSemSituacao: number;
  semValor: number;
};

export type ConsolidacaoDeAtivos = {
  livre: number;
  onerado: number;
  /** ⭐ 18/09/2026: as ações cotadas, em linha própria. */
  acoes: number;
  /** ⭐ Marca, patente, mineração: dele, e fora da conta por regra. */
  foraPorRegraQuantos: number;
  /** O que foi localizado e **não** integra o patrimônio atual. */
  fora: number;
  /** Quantos lançamentos ficaram sem valor apurado. ⚠️ A tela diz este número. */
  semValor: number;
  /**
   * ⭐⭐ **A EXPOSIÇÃO: a soma dos gravames ATIVOS — 10/09/2026.**
   *
   * ⛔ **NUNCA se subtrai do patrimônio.** É o asterisco que ele pôs na tabela,
   * virado regra: o valor de um gravame é o do ato registrado, **não o saldo
   * devedor atualizado**, e a preferência entre credores é matéria jurídica,
   * não aritmética.
   */
  exposicao: number;
  /** Quantos gravames ativos formam a exposição. */
  exposicaoQuantos: number;
  /** ⚠️ Gravames ativos **sem valor no papel** — pesam e não somam. */
  exposicaoSemValor: number;
  /** ⚠️ Gravames cuja situação não foi apurada: nem somam, nem se ignoram. */
  gravamesSemSituacao: number;
  contagem: Record<CategoriaDeAtivo, number>;
};

export type NovoAtivo = {
  classificacao: ClassificacaoDoAtivo;
  especie?: EspecieDeVeiculo | null;
  descricao: string;
  identificacao?: string | null;
  avaliacao?: number | null;
  percentual?: number | null;
  valorDeclarado?: number | null;
  ocorridoEm?: string | null;
  transmitente?: string | null;
  adquirente?: string | null;
  vinculoEntrePartes?: string | null;
  proprietarioAtual?: string | null;
  elementoDeConexao?: string | null;
  tipoDoImovel?: string | null;
  cidade?: string | null;
  uf?: string | null;
  areaPrivativa?: number | null;
  areaDoTerreno?: number | null;
  areaConstruida?: number | null;
  modalidadeDeAquisicao?: string | null;
  numeroDaMatricula?: string | null;
  cartorio?: string | null;
  qualidadeNoAto?: string | null;
  outraParte?: string | null;
  /** ⭐ 16/10/2026: chegando, a lista substitui a que havia; ausente, nada se toca. */
  partes?: ParteDoAto[];
  coproprietarios?: CoproprietarioDoAtivo[];
  documentoDoTitular?: string | null;
  valorGlobal?: boolean;
  modalidadeDaAvaliacao?: ModalidadeDaAvaliacao | null;
  certidaoEm?: string | null;
  tabelionato?: string | null;
  papelNoAto?: PapelNoAtoNotarial | null;
  efeitoPatrimonial?: EfeitoPatrimonialDoAto | null;
  poderes?: string | null;
  prazoDeValidade?: string | null;
  /** ⚠️ Três estados: sim, não, e nulo — "o documento não diz". */
  podeSubstabelecer?: boolean | null;
  matriculaSucessora?: string | null;
  motivoEncerramento?: string | null;
  situacao?: string | null;
  /* ⭐ A posição em ações — 18/09/2026. O valor quem calcula é o servidor. */
  quantidade?: number | null;
  cotacao?: number | null;
  cotacaoEm?: string | null;
  percentualDoCapital?: number | null;
  /** ⭐ As várias avaliações — 15/09/2026. Ausente, o servidor não mexe na lista. */
  avaliacoes?: AvaliacaoDoAtivo[];
};

/** O quanto o Hub tem certeza de que o processo é do investigado, e não de um xará. */
export type GrauDeCerteza = 'confirmado' | 'provavel' | 'homonimo';

/**
 * Onde o investigado está no processo.
 *
 * ⛔ **`nao_declarado` existe porque a tela mentia.** A fonte manda três letras
 * e a tela perguntava só *"é autor?"*, escrevendo "polo passivo" para todo o
 * resto — inclusive para terceiro interessado. Letra desconhecida não vira réu.
 */
export type PosicaoNoProcesso = 'autor' | 'reu' | 'terceiro' | 'nao_declarado';

/** Por que o processo interessa ao caso. Vazio = vai para o segundo bloco. */
export type MotivoDeInteresse =
  | 'ele_cobra'
  | 'desconsideracao'
  | 'sucessao'
  | 'insolvencia'
  | 'execucao_contra_ele'
  | 'cumprimento_contra_ele';

/** ⭐ O que a varredura do Diário Oficial da União achou — 19/09/2026. */
export type AchadoNoDou = {
  alvoId: string;
  /** `confirmado` quando o documento da ficha bate com o impresso no ato. */
  grau: 'confirmado' | 'provavel' | 'homonimo';
  porQue: string;
  trechos: string[];
  materia: {
    id: string;
    secao: string;
    publicadoEm: string;
    edicao: string | null;
    pagina: string | null;
    tipo: string | null;
    orgao: string | null;
    identifica: string | null;
    ementa: string | null;
    /** O endereço da página no próprio DOU — o registro, nunca a ferramenta. */
    paginaNoDou: string | null;
  };
};

export type BuscaNoDou = {
  atualizadaEm: string;
  meses: Array<{ ano: number; mes: number; secoes: string[]; materias: number; em: string }>;
  achados: AchadoNoDou[];
  falha?: string | null;
};

export type AndamentoDoDou = { investigadoId: string; total: number; feitos: number; comecouEm: string };

/** ⭐ O que a busca nos diários dos estados achou — 19/09/2026. */
export type AchadoEstadual = {
  uf: string;
  nomeDoDiario: string;
  data: string;
  pagina: number;
  grau: 'confirmado' | 'provavel' | 'homonimo';
  porQue: string;
  trechos: string[];
  enderecoNoDiario: string | null;
};

export type BuscaNosEstados = {
  atualizadaEm: string;
  ufs: string[];
  de: string | null;
  ate: string;
  achados: AchadoEstadual[];
  totais: Array<{ uf: string; total: number }>;
  falhas: Array<{ uf: string; porQue: string }>;
};

export type ProcessoAchado = {
  numero: string;
  numeroComMascara: string | null;
  tribunal: string;
  classe: string | null;
  orgao: string | null;
  /** ⚠️ Traduzido da letra que a fonte manda; o Hub não deduz de outro dado. */
  posicao: PosicaoNoProcesso;
  /** ⚠️ Vazio quer dizer "não interessa" — e ele NUNCA some da lista. */
  interesse: MotivoDeInteresse[];
  publicacoes: number;
  primeiraEm: string | null;
  ultimaEm: string | null;
  grau: GrauDeCerteza;
  porQue: string;
  /** ⭐ A confirmação no tribunal já feita e guardada — 14/09/2026. */
  confirmacao?: (ConfirmacaoDeProcesso & { em: string }) | null;
  /** Confirmação feita antes de 14/09/2026, cuja resposta não se guardava. */
  confirmadoAntesEm?: string | null;
  /** ⭐ Onde está no caso — 19/09/2026: a busca passou a lançar os confirmados e os prováveis. */
  noCaso?: 'processos' | 'passivo' | null;
  /** ⭐ A pessoa apagou do caso: a busca seguinte não o traz de volta. */
  descartado?: boolean;
};

export type BuscaNoDjen = {
  /** ⭐ Quando a busca guardada foi feita — 14/09/2026. */
  em?: string;
  totalDePublicacoes: number;
  /** ⚠️ `true` = há mais publicações do que o Hub trouxe. A tela DIZ isso. */
  truncado: boolean;
  processos: ProcessoAchado[];
  resumo: Record<GrauDeCerteza, number>;
  /** Quantos caem no primeiro bloco. */
  interessam: number;
  /** ⚠️ A soma disto passa de `interessam`: um processo pode ter dois motivos. */
  porMotivo: Record<MotivoDeInteresse, number>;
};

/**
 * Registra que a central notarial foi pesquisada à mão para este investigado.
 *
 * ⚠️ **Não lança nada** — marca que se PROCUROU. É o que faz a ressalva de
 * escrituras e procurações sair no documento, e é a diferença entre "nada
 * consta" e "não se olhou".
 */
export type AtoNotarial = {
  /** `Deed` escritura · `PowerOfAttorney` procuração. */
  tipo: 'Deed' | 'PowerOfAttorney';
  livro: string | null;
  folha: string | null;
  data: string | null;
  tabelionato: { nome: string | null; cns: number | null; uf: string | null; cidade: string | null };
};

/**
 * Um ato da relação da central, depois de guardado.
 *
 * ⚠️ **Isto NÃO é um ativo.** A central diz que o ato existe; não diz o que ele
 * contém nem se continua valendo. O ato só vira patrimônio depois que a cópia é
 * extraída e alguém a lê.
 */
export type AtoGuardado = {
  id: string;
  ocorridoEm: string;
  cartorio: string;
  cns: string;
  municipio: string;
  uf: string;
  especie: 'escritura' | 'procuracao';
  livro: string;
  folha: string;
  /** Marcado como "quero a cópia deste". */
  pedida: boolean;
  pedidaEm: string | null;
  /** A cópia chegou às mãos — só então o ato vai ao corpo do relatório. */
  chegou: boolean;
  chegadaEm: string | null;
  /** ⚠️ Nulo é "ainda não se sabe quanto custou", nunca zero. */
  custo: number | null;
  /** O resumo do ato, **aceito pelo master**. Nulo enquanto ninguém aceitou. */
  resumo: string | null;
};

/**
 * O que a leitura do ato **propõe** — e nada aqui está gravado.
 *
 * ⛔ **`valores` é uma LISTA de propósito.** Um ato traz o preço, a base do
 * imposto, os emolumentos e às vezes o valor de um ato antigo citado dentro.
 * A leitura tentou eleger um e errou: escolheu R$ 15,6 milhões num ato cujo
 * preço era R$ 42,1 milhões. **Quem escolhe é quem lê.**
 */
export type LeituraDoAto = {
  ok: boolean;
  /** `texto` quando o cartório gerou o PDF; `imagem` quando é foto do livro. */
  origem: 'texto' | 'imagem';
  objeto: string | null;
  documentos: string[];
  valores: Array<{ valor: number; trecho: string }>;
  matriculas: string[];
  poderes: string[];
  resumo: string;
  falha: string | null;
  ressalvas: string[];
};

export type PesquisaGuardada = {
  id: string;
  /** A data que a central carimba — é até ela que a relação alcança. */
  pesquisadaEm: string;
  quantidade: number;
  lidaEm: string;
  atos: AtoGuardado[];
  resumo: {
    escrituras: number;
    procuracoes: number;
    pedidas: number;
    chegadas: number;
    custoConhecido: number;
    chegadasSemCusto: number;
    /** Cópias marcadas e ainda não recebidas — o gasto que está por vir. */
    aPedir: number;
    /** ⚠️ Estimativa de tabela, com a faixa que o ISS do município abre. */
    estimativa: { estimado: number; minimo: number; maximo: number };
    primeiroAto: string | null;
    ultimoAto: string | null;
    cartorios: number;
  };
};

export type BuscaDeEscrituras = {
  atos: AtoNotarial[];
  custo: number;
  desativada: boolean;
  /** Quando a busca foi feita. */
  em?: string;
};

export const apiCentralNotarial = {
  registrar: (investigadoId: string) =>
    chamar<{ registrado: boolean }>(`/api/investigados/${investigadoId}/central-notarial`, {
      method: 'POST',
    }),

  /**
   * Sobe o PDF da pesquisa na central e o Hub lê a relação inteira.
   *
   * ⚠️ **Não passa por `chamar`**: o `Content-Type` tem de vir do `FormData`.
   *
   * ⭐ **Esta GRAVA**, ao contrário do leitor de matrícula — a relação não pode
   * se perder: ela vai aos anexos do documento entregue.
   */
  lerPesquisa: async (investigadoId: string, arquivo: File) => {
    const corpo = new FormData();
    corpo.append('arquivo', arquivo);
    const r = await fetch(`/api/investigados/${investigadoId}/ativos/busca-cep`, {
      method: 'POST',
      body: corpo,
      credentials: 'same-origin',
    });
    const dados = (await r.json()) as {
      pesquisa?: PesquisaGuardada;
      ressalvas?: string[];
      marcacoesHerdadas?: number;
      erro?: string;
    };
    if (!r.ok) throw new Error(dados.erro ?? 'Não consegui ler a pesquisa.');
    return dados;
  },

  /** As pesquisas guardadas, da mais recente para a mais antiga. */
  pesquisas: (investigadoId: string) =>
    chamar<{ pesquisas: PesquisaGuardada[] }>(
      `/api/investigados/${investigadoId}/pesquisas-na-central`,
    ),

  /**
   * Marca — ou desmarca — "quero a cópia destes atos".
   *
   * ⭐ **Em lote:** uma relação real tem quase duzentas linhas, e uma chamada
   * por clique faria a tela tremer.
   */
  pedirCopias: (investigadoId: string, ids: string[], pedir: boolean) =>
    chamar<{ pesquisa: PesquisaGuardada | null }>(
      `/api/investigados/${investigadoId}/atos-notariais/pedido`,
      { method: 'POST', body: JSON.stringify({ ids, pedir }) },
    ),

  /**
   * ⭐ A cópia chegou: anexa a certidão do cartório.
   *
   * Numa chamada só o Hub **guarda** o arquivo, **lê a capa** (livro, páginas,
   * selo e o custo) e **lança o gasto no Fundo de Custas** do caso.
   *
   * ⛔ **Recusa se a certidão não for do ato marcado** — o livro e a faixa de
   * páginas da capa têm de bater com o livro e a folha do ato.
   */
  anexarCopia: async (investigadoId: string, atoId: string, arquivo: File) => {
    const corpo = new FormData();
    corpo.append('arquivo', arquivo);
    const r = await fetch(`/api/investigados/${investigadoId}/atos-notariais/${atoId}/copia`, {
      method: 'POST',
      body: corpo,
      credentials: 'same-origin',
    });
    const dados = (await r.json()) as {
      leitura?: {
        livro: string | null;
        paginaInicial: string | null;
        paginaFinal: string | null;
        selo: string | null;
        custo: number | null;
        ressalvas: string[];
      };
      leituraDoAto?: LeituraDoAto | null;
      pesquisa?: PesquisaGuardada | null;
      lancadoNoFundo?: boolean;
      avisosDoFundo?: string[];
      /** O ato veio como imagem: o resumo vai depender de leitura de imagem. */
      atoEhImagem?: boolean;
      erro?: string;
    };
    if (!r.ok) throw new Error(dados.erro ?? 'Não consegui anexar a cópia.');
    return dados;
  },

  /** Relê o ato de uma cópia já anexada — não grava nada. */
  lerAto: (investigadoId: string, atoId: string) =>
    chamar<{ leituraDoAto: LeituraDoAto }>(
      `/api/investigados/${investigadoId}/atos-notariais/${atoId}/ler`,
      { method: 'POST' },
    ),

  /** Grava o resumo do ato — **é o aceite**. Texto vazio desfaz. */
  salvarResumo: (investigadoId: string, atoId: string, resumo: string) =>
    chamar<{ pesquisa: PesquisaGuardada | null }>(
      `/api/investigados/${investigadoId}/atos-notariais/${atoId}/resumo`,
      { method: 'PATCH', body: JSON.stringify({ resumo }) },
    ),

  /** Apaga uma pesquisa guardada. ⛔ Recusa se houver cópia pedida. */
  apagarPesquisa: (investigadoId: string, pesquisaId: string) =>
    chamar<{ apagada: boolean }>(
      `/api/investigados/${investigadoId}/pesquisas-na-central/${pesquisaId}`,
      { method: 'DELETE' },
    ),

  /** Se a busca paga pode ser feita — e, se não, por quê. */
  disponibilidade: (investigadoId: string) =>
    chamar<{
      disponivel: boolean;
      motivo: string | null;
      preco: number;
      /** ⭐ A última busca deste investigado, guardada — 14/09/2026. */
      guardada: BuscaDeEscrituras | null;
    }>(
      `/api/investigados/${investigadoId}/escrituras`,
    ),

  /**
   * A busca paga na central. ⚠️ **R$ 19,00 por documento.**
   *
   * ⛔ `desativada: false` na resposta é ALARME: a consulta foi criada e não
   * foi desativada — ela cobra de novo no próximo ciclo.
   */
  buscar: (investigadoId: string) =>
    chamar<{ busca: BuscaDeEscrituras }>(
      `/api/investigados/${investigadoId}/escrituras`,
      { method: 'POST' },
    ),
};

export type ImovelDaUniao = {
  /** RIP — Registro Imobiliário Patrimonial, o número da União. */
  rip: string | null;
  endereco: string | null;
  /** Terreno de marinha, acrescido, interior. */
  natureza: string | null;
  /** Aforamento, ocupação, cessão — como o particular o detém. */
  conceituacao: string | null;
  areaTotal: number | null;
  areaDaUniao: number | null;
  fracao: number | null;
  responsavel: string | null;
};


/**
 * Um documento que a varredura de imóveis do caso vai olhar.
 *
 * ⚠️ **`empresa` carrega a decisão do Richard de 08/09/2026:** o imóvel achado
 * no CNPJ de uma empresa aparece **na lista da pessoa, com etiqueta** dizendo
 * que o bem está no nome da empresa. Sem esta linha, o achado chegaria à tela
 * como um imóvel qualquer e quem lê não saberia que a titularidade é de outra
 * pessoa jurídica — que é justamente o que decide a estratégia de execução.
 */
export type AlvoDeImovel = {
  documento: string;
  /** ⚠️ Formatado no servidor — a máscara tem uma implementação só. */
  documentoFormatado: string;
  tipo: 'pf' | 'pj';
  investigadoId: string;
  investigado: string;
  /** A razão social, quando o alvo é empresa. `null` quando é a própria pessoa. */
  empresa: string | null;
  /** Outros investigados que também são sócios da mesma empresa. */
  tambemDe: string[];
  /**
   * ⭐ A última resposta guardada deste documento — 14/09/2026. `null` = nunca
   * consultado; `imoveis: null` = respondeu antes de 14/09, quando a lista não
   * se guardava.
   */
  ultima: {
    em: string;
    imoveis: ImovelDaUniao[] | null;
    itens: number;
    falha: string | null;
  } | null;
};

/** O que a varredura vai olhar, e a que custo — **sem gastar nada**. */
export type PlanoDeImoveis = {
  alvos: AlvoDeImovel[];
  dosInvestigados: number;
  dasEmpresas: number;
  /**
   * ⚠️ **Só os investigados, e o casamento é exato.** O histórico guarda a quem
   * a consulta pertence, não o documento consultado — então consulta feita no
   * CNPJ de uma empresa fica registrada no sócio, indistinguível da do CPF dele.
   */
  jaConsultados: { investigado: string; em: string }[];
  /** Quantas consultas de empresa já houve neste caso. Zero = nenhuma ainda. */
  consultasDeEmpresaAntes: number;
  /** Quem ficou de fora por não ter documento — a lacuna que trava a automação. */
  semDocumento: string[];
};

/** O resultado de um documento. ⚠️ Falha nunca vira "nada encontrado". */
export type AchadoDeImovel = {
  alvo: AlvoDeImovel;
  imoveis: ImovelDaUniao[];
  custo: string;
  cobrado: boolean;
  /** A fonte respondeu e nada havia. É informação, e é paga. */
  nadaConsta: boolean;
  falha: string | null;
};

export type VarreduraDeImoveis = {
  achados: AchadoDeImovel[];
  custoTotal: string;
  consultados: number;
  comImovel: number;
};

export type AeronaveDoRab = {
  marcas: string;
  modelo: string | null;
  fabricante: string | null;
  anoFabricacao: string | null;
  numeroSerie: string | null;
  /** ⭐ O gravame, que a fonte já entrega — 99,7% das linhas o trazem. */
  gravame: string | null;
  matriculadaEm: string | null;
  canceladaEm: string | null;
  motivoDoCancelamento: string | null;
  proprietario: string | null;
  documentoPublicado: string | null;
  /** ⚠️ `cnpj` é exato; por CPF o casamento exige o NOME junto. */
  casadoPor: 'cnpj' | 'cpf_mascarado_com_nome';
};

export type BuscaNoRab = {
  encontradas: number;
  lancadas: number;
  aeronaves: AeronaveDoRab[];
  /** ⚠️ As duas datas: quando a ANAC atualizou e quando o Hub importou. */
  fonteDe: string | null;
  importadoEm: string | null;
  falha: string | null;
};

export type ConfirmacaoDeProcesso = {
  numero: string;
  /**
   * ⚠️ **`inconclusivo` NÃO é desmentido.** Quer dizer que a fonte não devolveu
   * documento de parte nenhuma — o processo continua como estava.
   */
  resultado: 'confirmado' | 'de_outra_pessoa' | 'inconclusivo';
  porQue: string;
  partes: Array<{ nome: string; documento: string | null }>;
  /** O que a fonte declarou ter cobrado. Nunca uma constante da tela. */
  custo: string;
  cobrado: boolean;
};

export type AtoDaMatricula = {
  marca: string;
  especie: 'registro' | 'averbacao';
  data: string | null;
  natureza: string | null;
  valor: number | null;
  trecho: string;
  /** ⭐ O que o investigado fez no ato — 15/09/2026. Nulo: ele não aparece nele. */
  papel: string | null;
  /** O preço declarado no ato. ⚠️ Não é avaliação. */
  valorDeclarado: number | null;
  /** ⭐ O que o ato faz — `informa` não mexe no patrimônio (15/09/2026). */
  efeito: 'onus' | 'transmissao' | 'encerra' | 'cancela' | 'informa' | null;
};

export type LeituraDeMatricula = {
  ok: boolean;
  matricula: string | null;
  cartorio: string | null;
  /** Os CPF/CNPJ que aparecem no papel. ⚠️ Serve para achar parente não mapeado. */
  documentos: string[];
  /** ⚠️ **Sugestão, não decisão.** Nula quando o leitor não se convenceu. */
  classificacaoSugerida: ClassificacaoDoAtivo | null;
  porQue: string | null;
  atos: AtoDaMatricula[];
  onus: Array<{
    natureza: string;
    credor: string | null;
    valor: number | null;
    constituidoEm: string | null;
    /** ⚠️ Gravame cancelado não é gravame. */
    cancelado: boolean;
    marca: string;
  }>;
  percentual: number | null;
  /** ⭐ O que a matrícula diz do imóvel — 15/09/2026. */
  imovel: {
    descricao: string | null;
    endereco: string | null;
    tipo: string | null;
    cidade: string | null;
    uf: string | null;
    areaPrivativa: number | null;
    areaConstruida: number | null;
    areaDoTerreno: number | null;
    areaTotal: number | null;
    areaComum: number | null;
    hectares: number | null;
    fracaoIdeal: number | null;
  };
  /** ⭐ As avaliações que o papel traz — venal, terra nua, declarada, judicial. */
  avaliacoes: Array<{ modalidade: ModalidadeDaAvaliacao; valor: number; referencia: string }>;
  /** ⭐ O lançamento proposto, para levar ao formulário — 15/09/2026. */
  proposta: MatriculaProposta | null;
  /**
   * ⭐ **Como o dono foi reconhecido no papel — 09/09/2026.**
   *
   * `documento` = o CPF/CNPJ dele está na matrícula, e é a única forma que
   * afirma identidade · `nome` = só o nome bate, e nome não tem dígito
   * verificador · `nenhum` = nem um nem outro.
   *
   * ⛔ **Antes disto, `nenhum` recusava a leitura inteira** — e uma matrícula
   * legítima achada num processo era rejeitada. Agora ela é lida e MARCADA.
   */
  reconhecidoPor: 'documento' | 'nome' | 'nenhum';
  falha: string | null;
  /** O que o leitor não reconheceu — declarado, nunca engolido. */
  ressalvas: string[];
  /**
   * ⭐⭐ PRD §71 (M5): quem leu. `codex`: o Codex leu e o Hub conferiu (o fiscal); `hub`: só o Hub (a reserva, ou o
   * caso marcado "não vai a IA de fora"). Ausente: a leitura de sempre, sem a leitura pelo Codex ligada.
   */
  pelo?: 'codex' | 'hub';
  /** Por que leu só o Hub (a cota, o login caído, o caso que não vai a IA de fora). */
  soNoHub?: string | null;
  /** O modelo e o nível que leram (vai só ao rastro da ficha). */
  modelo?: string | null;
  /** O Codex disse que o documento parece dar ordens a quem lê. */
  canario?: boolean | null;
};

/** ⭐ PRD §71: um ato como o Codex o leu, conferido contra o mapa do Hub. */
export type AtoDoCodex = {
  marca: string;
  pagina: number | null;
  data: string | null;
  tipo: string | null;
  papel: string | null;
  valor: string | null;
  mapa: 'no_mapa' | 'fora_do_mapa' | 'nao_existe' | null;
};

/** ⭐ PRD §71: a leitura pelo Codex, como a tela a recebe. */
export type LeituraPeloCodex = {
  id: string;
  situacao: 'na_fila' | 'lendo' | 'pronto' | 'falhou' | 'cancelado';
  motivo: 'login' | 'cota' | 'tempo' | 'parado' | 'outro' | null;
  modelo: string;
  nivel: string;
  entrada: 'texto' | 'imagem';
  paginas: number;
  proposta: MatriculaProposta | null;
  avisos: string[];
  atos: AtoDoCodex[];
  canario: boolean | null;
};

/** ⭐ PRD §71: a cota do GPT para a tela (só números; "não informada" é dito). */
export type CotaDoCodex = {
  ligada: boolean;
  cota: {
    informada: boolean;
    janela: number | null;
    semana: number | null;
    vistoEm: string | null;
    voltaEm: string | null;
    paginasHoje: number;
    paginasNaSemana: number;
    tetos: { janela: number; semana: number; paginasPorDia: number; paginasPorSemana: number };
    disjuntorAte: string | null;
    podeLer: boolean;
    porQue: string | null;
  };
  caso: { id: string; soNoHub: boolean } | null;
};

export type NovoOnus = {
  natureza: string;
  credor?: string | null;
  processo?: string | null;
  valor?: number | null;
  constituidoEm?: string | null;
  situacao?: string | null;
};

export type NovoPassivo = {
  categoria: CategoriaDePassivo;
  descricao: string;
  valor?: number | null;
  ocorridoEm?: string | null;
  referencia?: string | null;
  situacao?: string | null;
};

/**
 * ⭐⭐ A trilha da apuração — decisão 13, 13/09/2026. Uma palavra por
 * levantamento, deduzida do que está gravado; `null` = em branco.
 */
export type PalavraDaTrilha =
  | 'feito'
  | 'nada_consta'
  | 'falta'
  | 'nao_respondeu'
  | 'aguardando'
  | 'a_confirmar'
  | 'fora_do_nivel'
  | 'sem_caminho';

export type LinhaDaTrilha = {
  chave: string;
  nome: string;
  palavra: PalavraDaTrilha | null;
  detalhe: string | null;
  topico: string | null;
};

/** ⭐ Sócio com domicílio no exterior, no quadro societário — decisão 16. */
export type SociosNoExterior = {
  referencia: string | null;
  empresas: Array<{ cnpj: string; razaoSocial: string; socios: Array<{ nome: string; pais: string }> }>;
};

/** ⭐ O investigado como representante de sócio no exterior — decisão 16, a partir do Forensic. */
export type RepresentacoesNoExterior = {
  referencia: string | null;
  partes: Array<{ investigadoId: string; frases: string[] }>;
};

export const apiSociosNoExterior = {
  representacoes: (casoId: string) =>
    chamar<{ representacoes: RepresentacoesNoExterior | null }>(`/api/casos/${casoId}/representacoes-no-exterior`),
  /** `socios: null` = a base não respondeu; o aviso de base já está no tópico. */
  doCaso: (casoId: string) =>
    chamar<{ socios: SociosNoExterior | null }>(`/api/casos/${casoId}/socios-no-exterior`),
};

/** ⭐ Sinais de blindagem, camuflagem ou fraude — 15/09/2026. Só aponta; não conclui. */
export type SinalDeBlindagem = {
  /** O padrão do catálogo — dá o nome, o "por que importa" e o que pode afastar. */
  padrao: string;
  grupo: 'patrimonio' | 'empresas' | 'pessoas';
  sobre: string;
  parte: string | null;
  fatos: string[];
  peso: 'forte' | 'medio';
};

export type SinaisDoCaso = {
  referencia: { rotulo: string; data: string } | null;
  contexto: string[];
  lacunas: string[];
  sinais: SinalDeBlindagem[];
};

export const apiSinaisDeBlindagem = {
  doCaso: (casoId: string) =>
    chamar<{
      sinais: SinaisDoCaso | null;
      catalogo: Record<string, { nome: string; porQue: string; afasta: string }>;
    }>(`/api/casos/${casoId}/sinais-de-blindagem`),
};

export const apiTrilha = {
  doCaso: (casoId: string) =>
    chamar<{ partes: Array<{ investigadoId: string; linhas: LinhaDaTrilha[] }> }>(
      `/api/casos/${casoId}/trilha`,
    ),
};

/** ⭐ O e-mail de entrega do relatório — 16/10/2026 (PRD §53). */
export type EmailDeEntrega = {
  clienteId: string;
  contatos: Array<{ id: string; nome: string; email: string; principal: boolean }>;
  contatoId: string | null;
  para: string;
  assunto: string;
  texto: string;
  html: string;
  paragrafos: Array<Array<{ t: string; n?: boolean }>>;
};

/** ⭐ As quatro pesquisas feitas fora do Hub que se registram — decisão 14, 13/09/2026. */
export type PesquisaRegistravel =
  | 'imoveis_por_titular'
  | 'indisponibilidade_de_bens'
  | 'junta_comercial'
  | 'marcas'
  /* ⭐ 16/10/2026 (PRD §52.9): embarcações, no tópico Veículos. */
  | 'embarcacoes';

export type PesquisaRegistrada = {
  id: string;
  investigadoId: string;
  servico: PesquisaRegistravel;
  achou: boolean;
  em: string;
};

export const apiPesquisasRegistradas = {
  doCaso: (casoId: string) =>
    chamar<{ registradas: PesquisaRegistrada[] }>(`/api/casos/${casoId}/pesquisas-registradas`),
  registrar: (investigadoId: string, servico: PesquisaRegistravel, achou: boolean) =>
    chamar<{ registrada: PesquisaRegistrada }>(`/api/investigados/${investigadoId}/pesquisas-registradas`, {
      method: 'POST',
      body: JSON.stringify({ servico, achou }),
    }),
  apagar: (investigadoId: string, registroId: string) =>
    chamar<void>(`/api/investigados/${investigadoId}/pesquisas-registradas/${registroId}`, {
      method: 'DELETE',
    }),
};

/**
 * ⭐ **Participações no exterior — pista a conferir, 28/09/2026.** A base do
 * ICIJ, lida no servidor (grátis) ou pela Infosimples (paga, do master). Nada
 * disto vai ao documento. Ver `api/src/integracoes/icij.ts`.
 */
export type VinculoNoExterior = {
  nomeNaBase: string;
  paisesDaPessoa: string | null;
  papel: string;
  desde: string | null;
  ate: string | null;
  sociedade: string;
  jurisdicao: string | null;
  constituidaEm: string | null;
  encerradaEm: string | null;
  situacao: string | null;
  conjunto: string | null;
  no: string;
};

export type AchadoNoSiteDoIcij = {
  descricao: string;
  pais: string | null;
  conjunto: string | null;
  no: string | null;
  vinculos: VinculoNoExterior[];
};

type UltimaBusca<T> = { em: string; itens: number; falha: string | null; lista: T[] };

export type ExteriorDoInvestigado = {
  base: UltimaBusca<VinculoNoExterior> | null;
  infosimples: UltimaBusca<AchadoNoSiteDoIcij> | null;
  /** De quando é a base local. `null` = não importada neste servidor. */
  baseDe: string | null;
  /** O preço de referência da busca paga. `null` para quem não vê valor. */
  preco: number | null;
};

export const apiInvestigados = {
  /**
   * ⭐ **Quanto custa aprofundar um veículo, e o que falta para poder.**
   *
   * ⛔ Existe para o botão dizer o preço ANTES do clique: pedir que se
   * autorize gasto cujo tamanho não se enxerga é pedir decisão às cegas.
   */
  custoDoAprofundamento: () =>
    chamar<{
      base: string | null;
      gravame: string;
      precificador: string;
      porVeiculo: string | null;
      impedimento: string | null;
    }>('/api/veiculos/custo-do-aprofundamento'),

  listar: (casoId: string) =>
    chamar<{ investigados: Investigado[] }>(`/api/casos/${casoId}/investigados`),

  criar: (casoId: string, dados: DadosDoInvestigado) =>
    chamar<Investigado>(`/api/casos/${casoId}/investigados`, {
      method: 'POST',
      body: JSON.stringify(dados),
    }),

  editar: (id: string, dados: Partial<DadosDoInvestigado>) =>
    chamar<Investigado>(`/api/investigados/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(dados),
    }),

  apagar: (id: string) => chamar<void>(`/api/investigados/${id}`, { method: 'DELETE' }),

  acrescentarDado: (
    id: string,
    dado: { tipo: TipoDoDado; valor: string; nota?: string | null },
  ) =>
    chamar<Investigado>(`/api/investigados/${id}/dados`, {
      method: 'POST',
      body: JSON.stringify(dado),
    }),

  apagarDado: (id: string, dadoId: string) =>
    chamar<void>(`/api/investigados/${id}/dados/${dadoId}`, { method: 'DELETE' }),

  /** O passivo: protestos, dívida ativa, ações judiciais e débito trabalhista. */
  passivos: (id: string) =>
    chamar<{ passivos: PassivoDoInvestigado[] }>(`/api/investigados/${id}/passivos`),

  acrescentarPassivo: (id: string, p: NovoPassivo) =>
    chamar<{ passivo: PassivoDoInvestigado }>(`/api/investigados/${id}/passivos`, {
      method: 'POST',
      body: JSON.stringify(p),
    }),

  apagarPassivo: (id: string, passivoId: string) =>
    chamar<void>(`/api/investigados/${id}/passivos/${passivoId}`, { method: 'DELETE' }),

  /* ── Os ativos localizados · 24/08/2026 ─────────────────────────────── */

  ativos: (id: string) =>
    chamar<{
      ativos: AtivoDoInvestigado[];
      consolidacao: ConsolidacaoDeAtivos;
      /** ⭐ 18/09/2026: os gravames de CADA tópico — a legenda de um não conta os do outro. */
      porCategoria?: Record<CategoriaDeAtivo, GravamesDoTopico>;
    }>(`/api/investigados/${id}/ativos`),

  acrescentarAtivo: (id: string, a: NovoAtivo) =>
    chamar<{ ativo: AtivoDoInvestigado }>(`/api/investigados/${id}/ativos`, {
      method: 'POST',
      body: JSON.stringify(a),
    }),

  /** ⚠️ Só master — corrigir avaliação ou percentual é correção de cadastro. */
  corrigirAtivo: (id: string, ativoId: string, a: Partial<NovoAtivo>) =>
    chamar<{ ativo: AtivoDoInvestigado }>(`/api/investigados/${id}/ativos/${ativoId}`, {
      method: 'PATCH',
      body: JSON.stringify(a),
    }),

  /**
   * ⭐⭐ **CORRIGIR um lançamento — 10/09/2026.**
   *
   * ⛔ **Não existia caminho na tela, e a rota estava lá desde sempre.** Quem
   * errasse um dígito ao transcrever uma matrícula ruim tinha de **apagar e
   * redigitar tudo — e apagar leva os ônus junto**, porque eles pendem do bem.
   *
   * ⚠️ **Manda só o que MUDOU** (`partial`), e o servidor tem a mesma regra do
   * card "Sobre a dívida": campo ausente não é campo apagado.
   */
  editarAtivo: (id: string, ativoId: string, a: Partial<NovoAtivo>) =>
    chamar<AtivoDoInvestigado>(`/api/investigados/${id}/ativos/${ativoId}`, {
      method: 'PATCH',
      body: JSON.stringify(a),
    }),

  apagarAtivo: (id: string, ativoId: string) =>
    chamar<void>(`/api/investigados/${id}/ativos/${ativoId}`, { method: 'DELETE' }),

  /** ⭐ 07/10/2026 — o processo irrelevante vai para Restrições e passivo, com tudo o que tinha. */
  marcarIrrelevante: (id: string, ativoId: string) =>
    chamar<{ passivoId: string }>(`/api/investigados/${id}/ativos/${ativoId}/irrelevante`, { method: 'POST' }),

  /**
   * ⛔ **Existe desde sempre, e NENHUMA TELA a chamava — 10/09/2026.**
   *
   * As três colunas da tabela que o Richard mandou — ônus, credor e valor —
   * eram impreenchíveis, e a matrícula que ele tinha na mão traz **seis**
   * gravames. É a quarta vez que este projeto encontra a mesma armadilha:
   * construído, ligado, sem tela.
   *
   * ⚠️ **Valor nulo é "não quantificado na matrícula", nunca zero.**
   */
  /**
   * ⭐ A nossa análise do processo relevante — 14/09/2026. Sai no relatório,
   * abaixo do processo; o servidor confere ao gravar e devolve os achados.
   */
  salvarAnaliseDoProcesso: (id: string, ativoId: string, texto: string) =>
    chamar<{
      ativo: AtivoDoInvestigado;
      achados: Array<{ gravidade: 'grave' | 'atencao'; aviso: string; trecho: string }>;
    }>(`/api/investigados/${id}/ativos/${ativoId}/analise`, {
      method: 'PUT',
      body: JSON.stringify({ texto }),
    }),

  acrescentarOnus: (id: string, ativoId: string, o: NovoOnus) =>
    chamar<{ ativo: AtivoDoInvestigado }>(`/api/investigados/${id}/ativos/${ativoId}/onus`, {
      method: 'POST',
      body: JSON.stringify(o),
    }),

  /** ⭐ 27/10/2026 (PRD §65.7): corrigir um gravame já lançado (só os campos que vierem). Do master. */
  corrigirOnus: (id: string, ativoId: string, onusId: string, o: Partial<NovoOnus>) =>
    chamar<{ ativo: AtivoDoInvestigado }>(`/api/investigados/${id}/ativos/${ativoId}/onus/${onusId}`, {
      method: 'PATCH',
      body: JSON.stringify(o),
    }),

  /**
   * Sobe a matrícula em PDF e devolve o que o Hub leu.
   *
   * ⚠️ **Não passa por `chamar`**, pela mesma razão do envio de documento e do
   * PDF do CENPROT: o `Content-Type` tem de vir do `FormData`, com a fronteira
   * que o navegador gera. Escrevê-lo à mão quebra o envio.
   *
   * ⚠️ **A rota NÃO grava nada** — devolve a leitura para o master conferir e
   * lançar. Gravar direto faria a máquina classificar patrimônio sem ninguém
   * olhar.
   */
  /**
   * ⭐ §70 (a leitura em lote): o arquivo vai com um NOME NEUTRO — o nome original costuma trazer o nome da pessoa e
   * fica só na tela. Sem vaga no servidor (2 leituras ao mesmo tempo), vem `LeituraOcupada` com o tempo de esperar, e
   * a fila tenta de novo sozinha. `sinal` deixa a fila desistir (o servidor para a leitura junto).
   */
  lerMatricula: async (id: string, arquivo: File, sinal?: AbortSignal) => {
    const corpo = new FormData();
    corpo.append('arquivo', arquivo, 'leitura.pdf');
    const r = await fetch(`/api/investigados/${id}/ativos/matricula`, {
      method: 'POST',
      body: corpo,
      credentials: 'same-origin',
      signal: sinal,
    });
    const dados = (await r.json().catch(() => ({}))) as { leitura?: LeituraDeMatricula; erro?: string; esperar?: number };
    if (r.status === 429) throw new LeituraOcupada(dados.erro ?? 'O Hub está ocupado.', dados.esperar ?? 15);
    if (!r.ok) throw new Error(dados.erro ?? 'Não consegui ler a matrícula.');
    return dados.leitura!;
  },

  /**
   * ⭐⭐ PRD §71 (M5): "Realizar leitura" — o Hub lê (o fiscal) e manda ao Codex no mesmo pedido. Volta a leitura do Hub
   * (a reserva) e o número do pedido ao Codex, ou o porquê de ter lido só no Hub. Nome neutro, como sempre.
   */
  lerMatriculaPeloCodex: async (id: string, arquivo: File, sinal?: AbortSignal) => {
    const corpo = new FormData();
    corpo.append('arquivo', arquivo, 'leitura.pdf');
    const r = await fetch(`/api/investigados/${id}/ativos/matricula/codex`, { method: 'POST', body: corpo, credentials: 'same-origin', signal: sinal });
    const dados = (await r.json().catch(() => ({}))) as {
      leitura?: LeituraDeMatricula;
      codex?: { id: string; modelo: string; nivel: string; paginas: number } | null;
      semCodex?: string | null;
      erro?: string;
      esperar?: number;
    };
    if (r.status === 429) throw new LeituraOcupada(dados.erro ?? 'O Hub está ocupado.', dados.esperar ?? 15);
    if (!r.ok) throw new Error(dados.erro ?? 'Não consegui ler a matrícula.');
    return { leitura: dados.leitura!, codex: dados.codex ?? null, semCodex: dados.semCodex ?? null };
  },

  /** ⭐ §71: como está a leitura pelo Codex (a fila pergunta até ficar pronta ou falhar). */
  leituraPeloCodex: async (id: string, pedidoId: string, sinal?: AbortSignal) => {
    const r = await fetch(`/api/investigados/${id}/ativos/matricula/codex/${pedidoId}`, { credentials: 'same-origin', signal: sinal });
    const dados = (await r.json().catch(() => ({}))) as { codex?: LeituraPeloCodex; erro?: string };
    if (!r.ok) throw new Error(dados.erro ?? 'Esta leitura não está mais aqui: envie o PDF de novo.');
    return dados.codex!;
  },

  /** ⭐ §71: "Cancelar" — a leitura pelo Codex sai da memória do servidor. */
  cancelarLeituraPeloCodex: async (id: string, pedidoId: string) => {
    await fetch(`/api/investigados/${id}/ativos/matricula/codex/${pedidoId}`, { method: 'DELETE', credentials: 'same-origin' }).catch(() => undefined);
  },
  /** ⭐ PRD §75: quantos campos da proposta do Codex foram mudados à mão no lançamento (só o número; falha em silêncio). */
  correcoesDaLeitura: (id: string, pedidoId: string, campos: number) => {
    void fetch(`/api/investigados/${id}/ativos/matricula/codex/${pedidoId}/correcoes`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ campos }),
    }).catch(() => undefined);
  },

  /** ⭐ §71: a cota do GPT e se o caso desta parte é "só no Hub". */
  cotaDoCodex: (investigadoId: string) => chamar<CotaDoCodex>(`/api/leitura-pelo-codex/cota?investigado=${investigadoId}`),

  /** ⭐ §71: "este caso não vai a IA de fora" (só master). */
  leituraSoNoHub: (casoId: string, soNoHub: boolean) =>
    chamar<{ ok: true; soNoHub: boolean }>(`/api/casos/${casoId}/leitura-so-no-hub`, { method: 'PUT', body: JSON.stringify({ soNoHub }) }),

  /**
   * Busca processos no diário do CNJ, pelo NOME.
   *
   * ⚠️ **É de graça** — API pública, sem chave. E **não grava nada**: devolve os
   * processos com o grau de certeza, e quem lança é gente.
   */
  /**
   * Aeronaves no RAB da ANAC — base local, **gratuita**.
   *
   * ⭐ **Grava sozinha**, ao contrário da de veículos: o RAB é registro e
   * declara a situação da matrícula.
   */
  aeronaves: (id: string) =>
    chamar<{ busca: BuscaNoRab }>(`/api/investigados/${id}/ativos/aeronaves`, {
      method: 'POST',
    }),

  /** ⭐ 28/09/2026 — a última busca de cada porta, no exterior. */
  exterior: (id: string) => chamar<ExteriorDoInvestigado>(`/api/investigados/${id}/exterior`),
  /** Na base do ICIJ que mora no servidor — **gratuita**, e nenhum nome sai. */
  exteriorNaBase: (id: string) =>
    chamar<{ encontrados: number }>(`/api/investigados/${id}/exterior/base`, { method: 'POST' }),
  /** Pela Infosimples — **paga**, só do master, e leva o nome a ela. */
  exteriorPelaInfosimples: (id: string) =>
    chamar<{ encontrados: number }>(`/api/investigados/${id}/exterior/infosimples`, { method: 'POST' }),

  /** ⚠️ É do CASO, não do investigado: o crédito é um só. E é de graça. */
  cobertura: (casoId: string) =>
    chamar<{ cobertura: CoberturaDoCredito }>(`/api/casos/${casoId}/cobertura`),

  processosNoDjen: (id: string) =>
    chamar<{
      busca: BuscaNoDjen;
      /** ⭐ 19/09/2026: o que a busca lançou no caso, e onde. */
      lancados: { emProcessos: number; emPassivo: number; jaNoCaso: number; descartados: number; homonimos: number } | null;
    }>(`/api/investigados/${id}/processos/djen`, { method: 'POST' }),

  /** ⭐ A última busca no diário, guardada, com as confirmações — 14/09/2026. */
  ultimaBuscaNoDjen: (id: string) =>
    chamar<{ busca: BuscaNoDjen | null }>(`/api/investigados/${id}/processos/djen`),

  /**
   * ⭐⭐ **O Diário Oficial da União — 19/09/2026 (PRD §48).**
   *
   * ⚠️ **A busca NÃO espera a resposta**: varrer meses leva minutos, e rota que demora
   * cai. O POST começa e devolve o andamento; a tela pergunta de novo até acabar.
   */
  douDaParte: (id: string) =>
    chamar<{ busca: BuscaNoDou | null; andamento: AndamentoDoDou | null }>(`/api/investigados/${id}/dou`),

  /** ⭐ Os diários dos estados que o Hub lê — 19/09/2026 (PRD §48.2). */
  diariosEstaduais: () =>
    chamar<{
      diarios: Array<{ uf: string; nome: string; desde: string; ligado: boolean; porQueNao: string | null }>;
    }>('/api/diarios-estaduais'),

  estadosDaParte: (id: string) =>
    chamar<{ busca: BuscaNosEstados | null }>(`/api/investigados/${id}/diarios-estaduais`),

  /** ⚠️ Aqui a busca é do estado e responde em segundos — por isso a rota espera. */
  buscarNosEstados: (id: string, ufs: string[], anos: number | null) =>
    chamar<{ busca: BuscaNosEstados }>(`/api/investigados/${id}/diarios-estaduais`, {
      method: 'POST',
      body: JSON.stringify({ ufs, anos }),
    }),

  buscarNoDou: (id: string, meses: number) =>
    chamar<{ busca: BuscaNoDou | null; andamento: AndamentoDoDou | null }>(`/api/investigados/${id}/dou`, {
      method: 'POST',
      body: JSON.stringify({ meses }),
    }),

  /**
   * O destravar: confirma no tribunal se o processo é do investigado.
   *
   * ⚠️ **Custa R$ 0,20 e é só do master** — ao contrário da busca no diário, que
   * é gratuita. Um processo por chamada.
   */
  confirmarProcesso: (id: string, processo: string) =>
    chamar<{ confirmacao: ConfirmacaoDeProcesso & { em: string } }>(
      `/api/investigados/${id}/processos/confirmar`,
      { method: 'POST', body: JSON.stringify({ processo }) },
    ),

  /**
   * O PLANO da busca de imóveis do investigado — **não gasta nada**.
   *
   * ⚠️ **Existe para o botão dizer o custo ANTES do clique.** Um clique dispara
   * uma consulta por documento — a pessoa **e as empresas dela** —, e mostrar o
   * número depois seria mostrar a fatura.
   */
  planoDeImoveis: (id: string) =>
    chamar<{ plano: PlanoDeImoveis; precoDeTabela: number | null; precoAproximado?: boolean }>(
      `/api/investigados/${id}/ativos/imoveis/plano`,
    ),

  /**
   * A busca de imóveis do INVESTIGADO — ele e os CNPJs das empresas dele.
   *
   * ⚠️ **Consulta paga, uma por documento.** Só master.
   * ⭐ **Substituiu `imoveisDaUniao` em 08/09/2026**, na padronização dos botões:
   * a rota antiga consultava um documento só e convivia com um botão de
   * varredura que fazia quase o mesmo.
   */
  buscarImoveis: (id: string, modo: 'o-que-falta' | 'tudo' = 'o-que-falta') =>
    chamar<{ busca: VarreduraDeImoveis }>(`/api/investigados/${id}/ativos/imoveis`, {
      method: 'POST',
      body: JSON.stringify({ modo }),
    }),

  /**
   * ⭐⭐ **ACEITAR a interpretação da máquina sobre um bem — 10/09/2026.**
   *
   * ⛔ **A rota existia desde 24/08 e NENHUMA TELA a chamava** — nem esta nem a
   * de desfazer. O efeito era pior que uma funcionalidade faltando: a tela
   * dizia *"ainda não revista, não entra no documento entregue"* e **não
   * oferecia como revê-la**. A interpretação nunca atravessava, e o Hub
   * afirmava que a culpa era da revisão que não houve.
   *
   * É a quinta vez desta armadilha: contrato social, organograma, botões de
   * saída, gravames — e agora o aceite.
   *
   * ⚠️ **Só master**, e não é hierarquia: quem aceita a conclusão responde por
   * ela num documento assinado.
   */
  /**
   * ⭐⭐ **De que mês é a base da Receita carregada nesta máquina — 10/09/2026.**
   *
   * ⛔ **A rota existia com o comentário *"a tela avisa quando ela está velha"*
   * — e NENHUMA tela avisava.** O organograma, o quadro societário e a busca de
   * empresas do sócio saem todos dela. Base de três meses atrás devolve sócio
   * que já saiu e não devolve o que entrou — e o Hub afirmava o quadro **sem
   * dizer de quando ele é**.
   *
   * ⚠️ **Isto NÃO é revelar método.** É o mesmo caso da situação cadastral: o
   * registro oficial que detém o fato pode aparecer; a ferramenta, não. E aqui
   * a tela é interna — o cliente não a vê.
   */
  baseDaReceita: () => chamar<{ mes: string | null }>('/api/receita/base'),

  aceitarAtivo: (id: string, ativoId: string) =>
    chamar<AtivoDoInvestigado>(`/api/investigados/${id}/ativos/${ativoId}/aceitar`, {
      method: 'POST',
    }),

  /**
   * ⭐ **Desfazer o aceite.** Sem isto, aceitar era irreversível — e revisão que
   * não se desfaz é revisão que se faz com medo.
   */
  desfazerAceite: (id: string, ativoId: string) =>
    chamar<AtivoDoInvestigado>(`/api/investigados/${id}/ativos/${ativoId}/aceitar`, {
      method: 'DELETE',
    }),

  apagarOnus: (id: string, ativoId: string, onusId: string) =>
    chamar<void>(`/api/investigados/${id}/ativos/${ativoId}/onus/${onusId}`, {
      method: 'DELETE',
    }),

  /**
   * Sobe a pesquisa de protestos do CENPROT em PDF.
   *
   * ⚠️ **Não passa por `chamar`**, pela mesma razão do envio de documento: o
   * `Content-Type` tem de vir do `FormData`, com a fronteira que o navegador
   * gera. Escrevê-lo à mão quebra o envio.
   */
  subirProtestoCenprot: async (id: string, arquivo: File) => {
    const corpo = new FormData();
    corpo.append('arquivo', arquivo);
    const r = await fetch(`/api/investigados/${id}/passivos/cenprot`, {
      method: 'POST',
      credentials: 'same-origin',
      body: corpo,
    });
    const texto = await r.text();
    const dados: unknown = texto ? JSON.parse(texto) : null;
    if (!r.ok) {
      const m = (dados as { erro?: string } | null)?.erro;
      throw new Error(m ?? 'Não consegui ler a pesquisa.');
    }
    return dados as {
      gravados: number;
      atualizados: number;
      /** ⛔ Já lançados, com dado diferente no PDF — mantidos como estavam (15/09/2026). */
      mantidos?: number;
      consultadoEm: string | null;
      ressalvas: string[];
    };
  },

  /** Levanta o passivo pela Infosimples. ⚠️ Consulta paga, só master. */
  levantarPassivo: (id: string) =>
    chamar<{
      gravados: number;
      /** ⛔ Já lançados, com dado diferente na consulta nova — mantidos como estavam (15/09/2026). */
      mantidos?: number;
      /** Processos em que a parte está no polo ativo: foram a Processos relevantes. */
      noPoloAtivo: number;
      /** Processos em outra posição, de papel não reconhecido, ou recursos. */
      outraPosicao: number;
      falhas: Array<{ rotulo: string; motivo: string }>;
      custo: number;
    }>(`/api/investigados/${id}/passivos/levantar`, { method: 'POST' }),

  /** O que as consultas já trouxeram: histórico, candidatos e conexões. */
  consultas: (id: string) =>
    chamar<{
      consultas: ConsultaExterna[];
      candidatos: CandidatoDeIdentidade[];
      conexoes: ConexaoEncontrada[];
    }>(`/api/investigados/${id}/consultas`),

  /** Dispara a busca à mão. A automática só roda quando o documento muda. */
  buscar: (id: string) =>
    chamar<Investigado>(`/api/investigados/${id}/buscar`, { method: 'POST' }),

  /**
   * ⚠️ **A consulta mais cara do plano — R$ 15,41.** Só do master, e só por
   * clique: nunca entra no caminho automático.
   */
  /**
   * ⭐⭐ **Aprofundar os veículos — 23/09/2026.**
   *
   * Uma chamada, três respostas por veículo: **restrições** (bloqueio RENAJUD,
   * judicial, tributária), **gravame** e **avaliação pelo chassi**. É o que
   * separa "veículo localizado" de "veículo penhorável".
   *
   * ⛔ Do master, por clique, e nunca automática.
   */
  aprofundarVeiculos: (id: string) =>
    chamar<{
      olhados: number;
      aprofundados: number;
      comGravame: number;
      comRestricao: number;
      comValor: number;
      semChassi: number;
      custo: string;
      avisos: string[];
    }>(`/api/investigados/${id}/aprofundar-veiculos`, { method: 'POST' }),

  veiculos: (id: string) =>
    chamar<{
      investigado: Investigado;
      encontrados: number;
      gravados: number;
      /* ⚠️ Quantos foram para ATIVOS LOCALIZADOS — a mesma consulta alimenta a
         ficha e o relatório, e a tela precisa poder dizer os dois. */
      ativos: number;
    }>(`/api/investigados/${id}/veiculos`, { method: 'POST' }),

  /**
   * Promove um vínculo a investigado do caso.
   *
   * ⚠️ **Só por clique.** O Hub apresenta suspeita; quem decide que alguém é
   * interposta pessoa é a Karen.
   */
  promover: (id: string, conexaoId: string, qualificacao?: QualificacaoInvestigado) =>
    chamar<{ investigado: Investigado; naFicha: number; aviso: string | null }>(
      `/api/investigados/${id}/promover/${conexaoId}`,
      { method: 'POST', body: JSON.stringify({ qualificacao }) },
    ),

  /**
   * ⭐ **Acrescenta um vínculo apurado em documento — 09/09/2026.**
   *
   * ⚠️ **Não consome cota do nível.** A pessoa entra no MAPA, não no caso —
   * quem a põe no caso é o "Trazer para o caso" da linha, como sempre.
   */
  acrescentarConexao: (id: string, dados: VinculoAMao) =>
    chamar<{ conexao: ConexaoEncontrada }>(`/api/investigados/${id}/conexoes`, {
      method: 'POST',
      body: JSON.stringify(dados),
    }),

  /** ⛔ Só apaga o que foi digitado à mão, e só antes de virar investigado. */
  apagarConexao: (id: string, conexaoId: string) =>
    chamar<void>(`/api/investigados/${id}/conexoes/${conexaoId}`, { method: 'DELETE' }),

  /** ⚠️ Grava o documento do candidato na ficha e roda a cadeia inteira. */
  adotar: (id: string, candidatoId: string) =>
    chamar<Investigado>(`/api/investigados/${id}/adotar/${candidatoId}`, { method: 'POST' }),

  /**
   * O quadro societário, da base da Receita carregada neste servidor.
   *
   * ⚠️ **É botão, e não automático como o processo.** A consulta escreve na
   * seção do relatório, e texto que aparece sozinho num documento assinado
   * precisa ter sido pedido por alguém.
   */
  societario: (id: string) =>
    chamar<{
      investigado: Investigado;
      encontradas: number;
      referencia: string | null;
      podeSerHomonimo: boolean;
      escreveuNoRelatorio: boolean;
    }>(`/api/investigados/${id}/societario`, { method: 'POST' }),
};

// ═══════════════════════════════════════════════════════════════════════════
// ⭐ O LINK DE CADASTRO — 16/09/2026 (PRD §43.11). Tudo só do master.
// ═══════════════════════════════════════════════════════════════════════════

export type ContatoDoCadastro = { nome: string; cargo: string; email: string; telefone: string; principal: boolean };

/** O que o cliente escreveu — e o que o master confere antes de gravar. */
export type DadosDoCadastro = {
  tipo: 'pj' | 'pf';
  documento: string;
  nome: string;
  nomeFantasia: string;
  site: string;
  email: string;
  telefone: string;
  cep: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  uf: string;
  representanteNome: string;
  representanteCpf: string;
  representanteCargo: string;
  contatos: ContatoDoCadastro[];
  origem: string;
  observacao: string;
  /** Os campos que o CNPJ ou o CEP preencheram e o cliente não mexeu. */
  preenchidos: string[];
};

export type CadastroAConferir = {
  id: string;
  apelido: string;
  recebidoEm: string;
  tipo: 'pj' | 'pf';
  nome: string;
  documento: string;
  cidadeUf: string;
  clienteExistente: { id: string; nome: string; diferencas: number } | null;
};

export type LinkAguardando = {
  id: string;
  apelido: string;
  criadoEm: string;
  expiraEm: string | null;
  situacao: 'aberto' | 'vencido' | 'cancelado' | 'usado';
};

export type Diferenca = { campo: string; rotulo: string };

export type EnvioDeCadastro = {
  envio: { id: string; apelido: string; recebidoEm: string; situacao: string; dados: DadosDoCadastro };
  clienteExistente: {
    id: string;
    nome: string;
    completa: Diferenca[];
    difere: Array<Diferenca & { naCarteira: string; noEnvio: string }>;
  } | null;
  receita: { conferido: boolean; situacao: string | null; difere: Array<Diferenca & { noEnvio: string; naReceita: string }> };
};

// ═══ O PEDIDO DE PESQUISA — 17/09/2026 (PRD §43.13) ═══════════════════════

export type NivelDoPedido = 'n1_insight' | 'n2_forensic' | 'n3_kairos';

/** A linha do pedido nas listas. ⛔ Na aba Clientes, `primeiro` vem nulo: é tela de reunião. */
export type PedidoNaLista = {
  id: string;
  recebidoEm: string;
  cliente: { id: string; nome: string };
  niveis: NivelDoPedido[];
  pesquisas: number;
  investigados: number;
  primeiro: string | null;
  quem: string | null;
  /** ⭐ Quantos documentos o cliente anexou (18/09/2026). */
  documentos: number;
  valido: boolean;
};

export type InvestigadoDoPedido = {
  tipo: 'pf' | 'pj';
  nome: string;
  documento: string;
  papel: QualificacaoInvestigado;
  sabe: string;
};

export type DividaDoPedido = {
  valor: string;
  contratoEm: string;
  inadimplenciaEm: string;
  temProcesso: boolean;
  processo: string;
  ajuizadoEm: string;
};

export type PedidoDePesquisa = {
  pedido: {
    id: string;
    recebidoEm: string;
    situacao: 'a_conferir' | 'proposta_montada' | 'descartado';
    cliente: { id: string; nome: string };
    contratoId: string | null;
    dados: {
      pesquisas: Array<{ nivel: NivelDoPedido; investigados: InvestigadoDoPedido[]; divida: DividaDoPedido }>;
      mesmaDivida: boolean;
      quem: { nome: string; email: string; telefone: string };
      observacao: string;
    };
  };
  /** Uma pesquisa por linha, com os investigados já escritos para a proposta. */
  proposta: Array<{ nivel: NivelDoPedido; entendimento: string }>;
  avisos: string[];
  /** Por CNPJ: o que o cadastro da Receita diz. `null` = não respondeu (não é "não existe"). */
  receita: Record<string, { razaoSocial: string; situacao: string | null } | null>;
  /** ⭐ Os documentos que o cliente anexou (18/09/2026, PRD §43.15). */
  documentos: DocumentoDoPedido[];
};

/**
 * ⭐ Um documento anexado ao pedido — 18/09/2026 (PRD §43.15).
 *
 * ⛔ `nome` e `descricao` são palavra do CLIENTE: a tela mostra a descrição entre
 * aspas e nunca a sobe para maiúscula. O que se abre é a CÓPIA convertida (as
 * páginas); o original só com confirmação.
 */
export type DocumentoDoPedido = {
  id: string;
  nome: string;
  descricao: string | null;
  tipo: 'pdf' | 'jpg' | 'png' | null;
  tamanho: number;
  estado: 'em_conferencia' | 'liberado' | 'recusado';
  /** Por que recusou — um código; o texto mora em `DocumentosDoPedido.tsx`. */
  motivo: string | null;
  /** Hoje só `acao_ao_abrir`: PDF aceito com ressalva. */
  nota: string | null;
  paginas: number | null;
  totalDePaginas: number | null;
  conferidoEm: string | null;
  criadoEm: string;
};

export type ContaDeDocumentos = { total: number; liberados: number; emConferencia: number; recusados: number };

export type OpcoesParaTrazer = {
  casoId: string;
  nivel: NivelServico;
  sugestao: { pedidoId: string; pesquisa: number } | null;
  pedidos: Array<{
    id: string;
    recebidoEm: string;
    doContratoDoCaso: boolean;
    quem: string;
    pesquisas: Array<{
      indice: number;
      nivel: NivelDoPedido;
      investigados: Array<{ indice: number; tipo: 'pf' | 'pj'; nome: string; documento: string | null; papel: QualificacaoInvestigado; sabe: string | null; jaNoCaso: boolean }>;
      marcosQueFaltam: Array<{ campo: string; rotulo: string; valor: string }>;
      marcosQueOCasoJaTem: Array<{ rotulo: string; valor: string }>;
      notas: string | null;
      notasJaNoCaso: boolean;
      /** Cabe no fim da observação do caso (4.000 caracteres)? Se não, não se oferece. */
      notasCabem: boolean;
    }>;
  }>;
};

export const apiFormulario = {
  cadastros: () =>
    chamar<{ ligado: boolean; aConferir: CadastroAConferir[]; aguardando: LinkAguardando[]; pedidos?: PedidoNaLista[] }>('/api/formulario/cadastros'),
  pedidosDoCliente: (clienteId: string) =>
    chamar<{ ligado: boolean; link: { id: string; criadoEm: string; recebidos: number } | null; aConferir: PedidoNaLista[]; montados: number }>(
      `/api/formulario/clientes/${clienteId}/pedidos`,
    ),
  gerarLinkDePedido: (clienteId: string) =>
    chamar<{ id: string; apelido: string; endereco: string; expiraEm: null }>(`/api/formulario/clientes/${clienteId}/link-de-pedido`, { method: 'POST', body: '{}' }),
  pedido: (id: string) => chamar<PedidoDePesquisa>(`/api/formulario/pedidos/${id}`),
  /** ⭐ "Recebido do cliente": os documentos dos pedidos que viraram o contrato do caso. */
  documentosDoCaso: (casoId: string) =>
    chamar<{ pedidos: Array<{ id: string; recebidoEm: string; documentos: DocumentoDoPedido[] }> }>(`/api/formulario/casos/${casoId}/documentos`),
  documentosPorCaso: (clienteId: string) =>
    chamar<{ porCaso: Record<string, ContaDeDocumentos> }>(`/api/formulario/clientes/${clienteId}/documentos-por-caso`),
  faltasPorCaso: (clienteId: string) =>
    chamar<{ porCaso: Record<string, FaltaDoPedido> }>(`/api/formulario/clientes/${clienteId}/faltas-por-caso`),
  paraTrazer: (casoId: string) => chamar<OpcoesParaTrazer>(`/api/formulario/casos/${casoId}/pedidos`),
  trazer: (casoId: string, corpo: { pedidoId: string; pesquisa: number; investigados: number[]; divida: boolean; notas: boolean }) =>
    chamar<{ investigados: number; marcos: string[]; notas: boolean }>(`/api/formulario/casos/${casoId}/trazer`, { method: 'POST', body: JSON.stringify(corpo) }),
  criarLink: (apelido: string) =>
    chamar<{ id: string; apelido: string; endereco: string; expiraEm: string | null }>('/api/formulario/links', {
      method: 'POST',
      body: JSON.stringify({ apelido }),
    }),
  endereco: (id: string) => chamar<{ endereco: string; expiraEm: string | null }>(`/api/formulario/links/${id}/endereco`),
  cancelar: (id: string) => chamar<{ ok: true }>(`/api/formulario/links/${id}/cancelar`, { method: 'POST', body: '{}' }),
  envio: (id: string) => chamar<EnvioDeCadastro>(`/api/formulario/envios/${id}`),
  cadastrar: (id: string, dados: DadosDoCadastro) =>
    chamar<{ clienteId: string }>(`/api/formulario/envios/${id}/cadastrar`, { method: 'POST', body: JSON.stringify({ dados }) }),
  completar: (id: string) =>
    chamar<{ clienteId: string; completados: number; contatosNovos: number; diferentes: unknown[] }>(`/api/formulario/envios/${id}/completar`, {
      method: 'POST',
      body: '{}',
    }),
  descartar: (id: string) => chamar<{ ok: true }>(`/api/formulario/envios/${id}/descartar`, { method: 'POST', body: '{}' }),
};

/* ⭐⭐ O BOTÃO "GERAR TEXTO" — PRD §58 (19/10/2026). O Hub guarda o pedido; o redator do
   servidor o leva ao GPT; o texto volta como RASCUNHO conferido. Tudo do master. */

/** Os tópicos que têm o botão: os mesmos que têm o "Gerar o prompt" (lista fechada no servidor). */
export type PecaDaRedacao =
  | 'objeto'
  | 'partes'
  | 'sumario'
  | 'indice'
  | 'linha-do-tempo'
  | 'consideracoes'
  | 'hipoteses'
  | 'recomendacoes'
  /** §58.12: o resumo de um documento do caso (o "resumir com IA"). */
  | 'resumo-de-documento';

export type PedidoDeRedacao = {
  id: string;
  peca: PecaDaRedacao;
  situacao: 'na_fila' | 'escrevendo' | 'pronto' | 'falhou';
  motivo: 'login' | 'cota' | 'tempo' | 'parado' | 'outro' | null;
  modelo: string;
  pedidoEm: string;
  levadoEm: string | null;
  devolvidoEm: string | null;
  naFrente: number;
  rascunho: string | null;
  achados: Array<{ gravidade: 'grave' | 'atencao'; aviso: string; trecho: string }> | null;
  /** §58.12: o documento resumido, e quanto dele coube no pedido (nulo = inteiro). */
  documentoId: string | null;
  documentoEntrou: number | null;
};

export type PedidosDeRedacao = {
  ligado: boolean;
  limiteDoDia: number;
  usadosHoje: number;
  pedidos: PedidoDeRedacao[];
};

export const apiRedacao = {
  listar: (relatorioId: string) => chamar<PedidosDeRedacao>(`/api/relatorios/${relatorioId}/redacao`),
  pedir: (relatorioId: string, peca: PecaDaRedacao, documentoId?: string) =>
    chamar<PedidoDeRedacao>(`/api/relatorios/${relatorioId}/redacao`, {
      method: 'POST',
      body: JSON.stringify(documentoId ? { peca, documentoId } : { peca }),
    }),
  /** Cria um campo de escrita NOVO no fim do tópico com o rascunho, e o devolve. */
  usar: (relatorioId: string, pedidoId: string) =>
    chamar<AnotacaoDaSecao>(`/api/relatorios/${relatorioId}/redacao/${pedidoId}/usar`, { method: 'POST', body: '{}' }),
  /** Descarta o rascunho, ou fecha a mensagem de falha. */
  descartar: (relatorioId: string, pedidoId: string) =>
    chamar<{ ok: true }>(`/api/relatorios/${relatorioId}/redacao/${pedidoId}/descartar`, { method: 'POST', body: '{}' }),
};


